import { DocButton, StorageIndicator, deleteEntityDocuments } from './DocumentPanel'
import { useAuth } from '../context/AuthContext'
import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import * as XLSX from 'xlsx'
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import CashOutForecast from './CashOutForecast'
import FinancialFees from './FinancialFees'
import CashFlowExpectation from './CashFlowExpectation'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const YEARS = Array.from({length: 10}, (_, i) => 2020 + i)
const PAYMENT_METHODS = ['bank_transfer', 'cheque', 'cash']
const PAYMENT_TYPES = ['subcontractor_supplier', 'others', 'payroll']
const VAT_OPTIONS = [0, 5, 15]

export default function Finance({ projectId: initProjectId }) {
  const { user } = useAuth()  // existing app-wide auth pattern (same as App.jsx's `const { can, role } = useAuth()`) — used only to populate entered_by on new inserts below; no other Cash In logic reads this.
  // `useAuth()`'s user.id is the Supabase Auth (auth.users) id. The `entered_by` FK on the
  // new tables (same pattern as cash_in_entries.entered_by) points to the app's own `users`
  // table, which has a DIFFERENT id for the same person (confirmed by inspecting both:
  // auth.users.id and users.id differ for the same email). Resolve the correct users.id
  // once by email and cache it — never write auth.users.id directly into entered_by.
  const [cin2EnteredByUserId, setCin2EnteredByUserId] = useState(null)
  useEffect(() => {
    if (!user?.email) return
    supabase.from('users').select('id').eq('email', user.email).maybeSingle()
      .then(({ data }) => setCin2EnteredByUserId(data?.id || null))
  }, [user?.email])
  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState(null)
  useEffect(() => { if (initProjectId) setProjectId(initProjectId) }, [initProjectId])
  const [projectName, setProjectName] = useState('')
  const [activeTab, setActiveTab] = useState('cashinout')

  // Finance Notes
  const [financeNotes, setFinanceNotes]       = useState({})
  const [editingNote, setEditingNote]         = useState(null)
  const [noteText, setNoteText]               = useState('')
  const [showNoteModal, setShowNoteModal]     = useState(false)
  const [activeNoteKey, setActiveNoteKey]     = useState(null)
  const [activeNoteLabel, setActiveNoteLabel] = useState('')

  // Cash Out
  const [cashOutEntries, setCashOutEntries] = useState([])
  const [cashOutSummary, setCashOutSummary] = useState([])
  const [showOutModal, setShowOutModal] = useState(false)
  const [editOut, setEditOut] = useState(null)

  // Cash In
  const [cashInEntries, setCashInEntries] = useState([])
  const [showInModal, setShowInModal] = useState(false)
  const [editIn, setEditIn] = useState(null)

  // ── Cash In — New (parallel tab, isolated state — does not touch the old Cash In workflow) ──
  const [cin2Entries, setCin2Entries] = useState([])
  const [cin2Allocations, setCin2Allocations] = useState([])
  const [cin2Planning, setCin2Planning] = useState([])       // from cash_in_planning (Scheduling: client_invoice + certified_amount)
  const [cin2Settings, setCin2Settings] = useState(null)      // from cashflow_settings — LEGACY, untouched, kept only for CashFlowExpectation/legacy compatibility. NOT used by the new Cash In rule engine.
  const [cin2Rules, setCin2Rules] = useState(null)             // from project_cashin_rules — the sole source for the new Advance/Retention rule engine
  const [cin2ECScheduleRules, setCin2ECScheduleRules] = useState([]) // from expected_collection_schedule_rules — split-method offset/% pairs
  const [cin2Project, setCin2Project] = useState(null)          // from projects.contract_value — used only for retention_cap_type='max_pct_contract'
  const [cin2ShowRulesModal, setCin2ShowRulesModal] = useState(false)
  const [cin2ShowResetConfirm, setCin2ShowResetConfirm] = useState(false)
  const [cin2ResetConfirmText, setCin2ResetConfirmText] = useState('')
  const [cin2Resetting, setCin2Resetting] = useState(false)
  const [cin2RulesForm, setCin2RulesForm] = useState({
    has_advance: false, advance_recovery_method: 'percentage_certified', advance_recovery_value: '',
    advance_recovery_start_type: 'first_certificate', advance_recovery_start_value: '',
    advance_recovery_stop_type: 'until_fully_recovered', advance_recovery_stop_value: '',
    has_retention: false, retention_method: 'percentage', retention_value: '',
    retention_start_type: 'first_certificate', retention_start_value: '',
    retention_cap_type: 'no_cap', retention_cap_value: '',
    expected_collection_method: 'same_month', expected_collection_lag_months: '',
  })
  const [cin2ECSplitRows, setCin2ECSplitRows] = useState([{ offset_months: 0, percentage: 100 }]) // local editable split table
  const [cin2Month, setCin2Month] = useState(new Date().toISOString().slice(0,7)) // 'YYYY-MM'
  const [cin2ActiveView, setCin2ActiveView] = useState('erp') // 'erp' | 'schedule' — same data (monthRows), two presentations only

  // ── Cash In — Expected Other Income / Other Deductions (independent streams — never
  // enter the Expected Collection formula, never pass through calcExpectedCollectionSchedule) ──
  const [cin2OtherMovements, setCin2OtherMovements] = useState([])
  const [cin2CategoryMaster, setCin2CategoryMaster] = useState([])
  const [cin2ShowAddMovementModal, setCin2ShowAddMovementModal] = useState(null)  // {type:'income'|'deduction', month} | null
  const [cin2ShowManageCategories, setCin2ShowManageCategories] = useState(false)

  // ── Cash Out — New (ERP View) — cout2* namespace, isolated from Cash In (cin2*) ──
  // SSOT: cf_payment_entries ONLY. purchase_orders / subcontracts are enrichment-only
  // (never a source of totals/counts/amounts — see cout2FetchAll).
  const [cout2Entries, setCout2Entries]           = useState([])   // enriched cf_payment_entries rows
  const [cout2Loading, setCout2Loading]           = useState(false)
  const [cout2OpenMonth, setCout2OpenMonth]       = useState(null) // expanded month (ERP monthly table)
  const [cout2OpenType, setCout2OpenType]         = useState(null) // expanded {month, payment_type} drill-down key
  const [cout2Filters, setCout2Filters]           = useState({
    month: '', year: '', paymentType: 'all', payee: '', poNumber: '', costCode: '', poStatus: 'all', search: '',
  })
  const [cin2OpenMonth, setCin2OpenMonth] = useState(null)    // month accordion currently expanded, or null
  const [cin2ShowCollectionModal, setCin2ShowCollectionModal] = useState(false)
  const [cin2CollectionForm, setCin2CollectionForm] = useState({ date: '', collected_amount: '', notes: '' })
  const [cin2ShowAdvanceModal, setCin2ShowAdvanceModal] = useState(false)
  const [cin2AdvanceForm, setCin2AdvanceForm] = useState({ date: '', collected_amount: '', notes: '' })
  const [cin2ShowAllocateModal, setCin2ShowAllocateModal] = useState(false)
  const [cin2AllocateTarget, setCin2AllocateTarget] = useState(null)
  const [cin2AllocateForm, setCin2AllocateForm] = useState({ certification_id: '', allocated_amount: '' })
  const [cin2ReverseReasonFor, setCin2ReverseReasonFor] = useState(null)
  const [cin2ReverseReason, setCin2ReverseReason] = useState('')
  // Certification details modal: records the ACTUAL Retention Held / Advance Recovered
  // for a given month's Finance-owned certification details (Retention/Advance Recovery/
  // Other Deduction — cash_in_entries, transaction_type='cert_finance_detail').
  // This row NEVER carries Certified Amount or Client Invoice — those remain exclusively
  // in Scheduling/cash_in_planning. This row only holds the Finance-side adjustment values
  // for the month's certification, keyed by month, not a copy of the certification itself.
  // Client Invoice and Certified Amount are NEVER entered here — Scheduling owns them.
  const [cin2ShowCertDetailModal, setCin2ShowCertDetailModal] = useState(false)
  const [cin2CertDetailMonth, setCin2CertDetailMonth] = useState(null)
  const [cin2CertDetailForm, setCin2CertDetailForm] = useState({ retention_held_amount: '', advance_recovered_amount: '', other_deduction_amount: '', other_deduction_description: '' })
  const [cin2ShowUpload, setCin2ShowUpload] = useState(false)
  const [cin2UploadRows, setCin2UploadRows] = useState([])
  const [cin2UploadError, setCin2UploadError] = useState('')
  const [cin2Uploading, setCin2Uploading] = useState(false)

  // References
  const [availablePOs, setAvailablePOs] = useState([])
  const [availableContracts, setAvailableContracts] = useState([])
  const [costCodes, setCostCodes] = useState([])

  // Upload
  const [showUpload, setShowUpload]     = useState(false)
  const [uploadRows, setUploadRows]     = useState([])
  const [uploadError, setUploadError]   = useState('')
  const [uploadTarget, setUploadTarget] = useState('out')
  const [uploadMode, setUploadMode]     = useState('replace')
  const [uploading, setUploading]       = useState(false)
  const [curveFilters, setCurveFilters]   = useState({ cashIn: true, cashOut: true, cumIn: true, cumOut: true, balance: true })
  const [curveMonthFrom, setCurveMonthFrom] = useState('')
  const [curveMonthTo, setCurveMonthTo]     = useState('')
  const fileRef  = useRef()
  const chartRef = useRef()

  // Stats
  const [stats, setStats] = useState({ totalOut: 0, totalIn: 0, net: 0 })

  // PO Lines tab state
  const [poLines, setPoLines]               = useState([])
  const [poLinesLoading, setPoLinesLoading] = useState(false)
  const [poLineSearch, setPoLineSearch]     = useState('')
  const [poLineEditId, setPoLineEditId]     = useState(null)
  const [poLineEditVal, setPoLineEditVal]   = useState('')

  // Cash Flow Packages tab state
  const [cfPackages, setCfPackages]         = useState([])  // dictionary from Excel
  const [allSubCodes, setAllSubCodes]       = useState([])  // all sub codes from project
  const [cfEntries, setCfEntries]           = useState([])  // payment entries
  const [customPaymentTypes, setCustomPaymentTypes] = useState([]) // [{value, label}] user-managed payment types
  const [showPaymentTypesMgr, setShowPaymentTypesMgr] = useState(false)
  const [newPaymentTypeLabel, setNewPaymentTypeLabel] = useState('')
  const [customCategories, setCustomCategories] = useState([]) // [{value, label}] user-managed categories
  const [showCategoriesMgr, setShowCategoriesMgr] = useState(false)
  const [newCategoryLabel, setNewCategoryLabel] = useState('')
  const [cfUploadPreview, setCfUploadPreview] = useState(null)
  const [cfUploading, setCfUploading]       = useState(false)
  const [cfDragOver, setCfDragOver]         = useState(false)
  const [cfShowModal, setCfShowModal]       = useState(false)
  const [cfShowDict, setCfShowDict]         = useState(false)
  const [dictTab, setDictTab]               = useState('view')
  const [manualPkg, setManualPkg]           = useState({ cf_package: '', sub_code: '', sub_code_description: '', budget_type: 'direct' })
  const [editDictRow, setEditDictRow]       = useState(null)  // item being edited in dictionary
  const [editDictForm, setEditDictForm]     = useState({ cf_package: '', sub_code: '', sub_code_description: '', budget_type: 'direct' })
  const [cfEditEntry, setCfEditEntry]       = useState(null)
  const [cfPicker, setCfPicker]             = useState(null)
  const [cfSelected, setCfSelected]         = useState(new Set())
  const [cfCollapsedPkgs, setCfCollapsedPkgs] = useState(new Set())
  const [cfBulkPicker, setCfBulkPicker]     = useState(null) // 'category' | 'payment_type'
  const [cfBulkDateEdit, setCfBulkDateEdit] = useState(null) // { date, is_opening } while editing
  const [cfLineEdit, setCfLineEdit]         = useState(null) // entry id being edited inline for date/opening
  // Bulk payment upload (No PO / Petty Cash / PO) — 3 separate templates
  const [bpMode, setBpMode]                 = useState(null) // 'nopo' | 'petty' | 'po'
  const [bpRows, setBpRows]                 = useState([])   // parsed + validated rows
  const [bpUnmatched, setBpUnmatched]       = useState([])   // rows needing manual code selection
  const [bpUploading, setBpUploading]       = useState(false)
  const [bpError, setBpError]               = useState('')
  const [bpConfirmData, setBpConfirmData]   = useState(null) // { dupes, warnings } pending confirmation before final import
  const [cfEditForm, setCfEditForm]         = useState({ date: '', amount_inc_vat: '', notes: '' })
  const [cfEditIpcOptions, setCfEditIpcOptions] = useState([])

  // CF Packages filters
  const [cfFilterPkg, setCfFilterPkg]       = useState('all')
  const [cfFilterSubCode, setCfFilterSubCode] = useState('')
  const [cfFilterType, setCfFilterType]     = useState('all')          // budget_type: direct / indirect
  const [cfFilterCategory, setCfFilterCategory] = useState('all')      // invoice / adv_payment
  const [cfFilterPaymentType, setCfFilterPaymentType] = useState('all')// subcontractor_supplier / payroll / others
  const [cfFilterSupplier, setCfFilterSupplier] = useState('')
  const [cfFilterDateFrom, setCfFilterDateFrom] = useState('')
  const [cfFilterDateTo, setCfFilterDateTo] = useState('')
  const [cfPoLines, setCfPoLines]           = useState([])
  const [cfIpcOptions, setCfIpcOptions]     = useState([]) // IPCs for the selected po_id (material + equipment)
  const [cfPoSearch, setCfPoSearch]         = useState('')
  const [cfLineAmounts, setCfLineAmounts]     = useState({}) // line_id -> amount
  const [cfScAmounts, setCfScAmounts]         = useState({}) // sub_code -> amount (used when paying at sub_code level, not per-line)
  const [cfExpandedSc, setCfExpandedSc]       = useState({}) // sub_code -> bool
  const [poLinesMap, setPoLinesMap]           = useState({}) // po_id -> lines[]

  // Filtered + grouped cf_payment_entries by package — computed once into state when data/filters change
  // (same pattern as PurchaseOrders.jsx: group once after fetch/filter change, not inline during render)
  const [cfGroupedEntries, setCfGroupedEntries] = useState({})
  useEffect(() => {
    const filtered = cfEntries.filter(e => {
      if (cfFilterPkg !== 'all' && (e.cf_package || 'Unclassified') !== cfFilterPkg) return false
      if (cfFilterSubCode && !(e.sub_code || '').toLowerCase().includes(cfFilterSubCode.toLowerCase())) return false
      if (cfFilterType !== 'all' && (e.budget_type || 'direct') !== cfFilterType) return false
      if (cfFilterCategory !== 'all' && (e.category || 'invoice') !== cfFilterCategory) return false
      if (cfFilterPaymentType !== 'all' && (e.payment_type || 'subcontractor_supplier') !== cfFilterPaymentType) return false
      if (cfFilterSupplier && !((e.payee || e.purchase_orders?.vendor || '').toLowerCase().includes(cfFilterSupplier.toLowerCase()))) return false
      if (cfFilterDateFrom && e.date < cfFilterDateFrom) return false
      if (cfFilterDateTo && e.date > cfFilterDateTo) return false
      return true
    })
    const grouped = {}
    filtered.forEach(e => {
      const pkg = e.cf_package || 'Unclassified'
      if (!grouped[pkg]) grouped[pkg] = []
      grouped[pkg].push(e)
    })
    if (cfFilterPkg === 'all') {
      cfPackages.forEach(p => {
        if (!grouped[p.cf_package]) grouped[p.cf_package] = []
      })
    }
    setCfGroupedEntries(grouped)
  }, [cfEntries, cfPackages, cfFilterPkg, cfFilterSubCode, cfFilterType, cfFilterCategory, cfFilterPaymentType, cfFilterSupplier, cfFilterDateFrom, cfFilterDateTo])
  const [cfForm, setCfForm]                 = useState({
    date: '', po_id: '', ipc_id: '', payee: '', sub_code: '', cf_package: '',
    sub_code_desc: '', amount_inc_vat: '', notes: '', is_opening: true, no_po: false, petty_cash: false,
    category: 'invoice', payment_type: 'subcontractor_supplier'
  })
  const [pcVouchers, setPcVouchers]           = useState([])   // unique voucher list
  const [pcVoucherLines, setPcVoucherLines]   = useState([])   // lines of selected voucher
  const [pcLoading, setPcLoading]             = useState(false)
  const [pcSearch, setPcSearch]               = useState('')
  const [selectedPcVoucher, setSelectedPcVoucher] = useState(null)  // selected voucher_no
  const [pcLineAmounts, setPcLineAmounts]     = useState({})

  // Filters
  const [filterMonth, setFilterMonth] = useState('all')
  const [filterYear, setFilterYear] = useState('all')
  const [filterType, setFilterType] = useState('all')

  const [outForm, setOutForm] = useState({
    date: '', payment_type: 'subcontractor_supplier', po_id: '', contract_id: '',
    payroll_period: '', petty_ref: '', payee: '', cost_code_id: '',
    amount_exc_vat: '', vat_pct: '0', vat_amount: '0', amount_inc_vat: '',
    payment_method: 'bank_transfer', notes: '', status: 'draft'
  })

  const [inForm, setInForm] = useState({
    date: '', invoice_number: '', amount: '',
    payment_method: 'bank_transfer', notes: '', is_opening: false
  })

  useEffect(() => { fetchProjects() }, [])
  useEffect(() => { if (projectId) fetchAll() }, [projectId])

  async function fetchProjects() {
    const { data } = await supabase.from('projects').select('id, name, organizations(name)').order('name')
    setProjects(data || [])
  }

  async function fetchAll() {
    const [, inData] = await Promise.all([
      fetchCashOut(),
      supabase.from('cash_in_entries').select('*').eq('project_id', projectId).order('period_month',{ascending:false}),
      fetchPOs(),
      fetchContracts(),
      fetchCostCodes(),
      fetchCFPackages(),
      fetchPaymentTypes(),
      fetchCategories(),
      fetchCFEntries(),
    ])
    const inEntries  = inData?.data  || []
    setCashInEntries(inEntries)
    // cashOutSummary is now derived automatically from cfEntries via useEffect — no manual fetch needed
    fetchFinanceNotes()
    const totalIn  = inEntries.reduce((s,e) => s+(parseFloat(e.collected_amount)||0), 0)
    setStats(p => ({ ...p, totalIn }))
  }

  async function fetchCFPackages() {
    const { data } = await supabase
      .from('cf_package_dictionary')
      .select('*')
      .eq('project_id', projectId)
      .order('cf_package')
    setCfPackages(data || [])

    // Fetch Sub Codes (level=2) from the real cost_codes hierarchy, with Cost Code + Resource Code ancestry resolved
    const { data: costCodes } = await supabase
      .from('cost_codes')
      .select('id, code, description, level, parent_id')
      .eq('project_id', projectId)
      .eq('is_active', true)
      .order('code')
    const all = costCodes || []
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    const combined = all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let cc = null, rc = null
      if (parent && parseInt(parent.level) === 4) {
        rc = parent
        cc = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        cc = parent
      }
      return {
        code: sub.code, description: sub.description,
        costCode: cc?.code || null, costCodeDescription: cc?.description || null,
        resourceCode: rc?.code || null, resourceCodeDescription: rc?.description || null,
      }
    }).sort((a, b) => a.code.localeCompare(b.code))
    setAllSubCodes(combined)
  }

  async function fetchPaymentTypes() {
    const { data } = await supabase
      .from('payment_types')
      .select('*')
      .eq('project_id', projectId)
      .order('label')
    setCustomPaymentTypes(data || [])
  }

  async function addPaymentType() {
    const label = newPaymentTypeLabel.trim()
    if (!label) return
    const value = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    if (!value) return
    const exists = [...PAYMENT_TYPES, ...customPaymentTypes.map(t => t.value)].includes(value)
    if (exists) { setNewPaymentTypeLabel(''); return }
    await supabase.from('payment_types').insert({ project_id: projectId, value, label })
    setNewPaymentTypeLabel('')
    fetchPaymentTypes()
  }

  async function deletePaymentType(id) {
    if (!window.confirm('Delete this payment type? Existing entries using it will keep their value but it will no longer appear as an option.')) return
    await supabase.from('payment_types').delete().eq('id', id)
    fetchPaymentTypes()
  }

  // Combined list: built-in defaults + user-added custom types, deduped by value
  function getAllPaymentTypes() {
    const builtIn = [
      { value: 'subcontractor_supplier', label: 'Subcontractor / Supplier' },
      { value: 'payroll', label: 'Payroll' },
      { value: 'others', label: 'Others' },
    ]
    const customOnly = customPaymentTypes.filter(t => !builtIn.some(b => b.value === t.value))
    return [...builtIn, ...customOnly]
  }
  function paymentTypeLabel(value) {
    const found = getAllPaymentTypes().find(t => t.value === value)
    return found?.label || value || 'Others'
  }

  async function fetchCategories() {
    const { data } = await supabase
      .from('payment_categories')
      .select('*')
      .eq('project_id', projectId)
      .order('label')
    setCustomCategories(data || [])
  }

  async function addCategory() {
    const label = newCategoryLabel.trim()
    if (!label) return
    const value = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    if (!value) return
    const exists = ['invoice', 'advanced_payment', ...customCategories.map(t => t.value)].includes(value)
    if (exists) { setNewCategoryLabel(''); return }
    await supabase.from('payment_categories').insert({ project_id: projectId, value, label })
    setNewCategoryLabel('')
    fetchCategories()
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category? Existing entries using it will keep their value but it will no longer appear as an option.')) return
    await supabase.from('payment_categories').delete().eq('id', id)
    fetchCategories()
  }

  // Combined list: built-in defaults + user-added custom categories, deduped by value
  function getAllCategories() {
    const builtIn = [
      { value: 'invoice', label: 'Invoice' },
      { value: 'advanced_payment', label: 'Adv. Payment' },
    ]
    const customOnly = customCategories.filter(t => !builtIn.some(b => b.value === t.value))
    return [...builtIn, ...customOnly]
  }
  function categoryLabel(value) {
    const found = getAllCategories().find(t => t.value === value)
    return found?.label || value || 'Invoice'
  }

  // Fetch all rows for a query, bypassing Supabase's default 1000-row cap.
  async function fetchAllRows(queryFn) {
    const PAGE = 1000
    let from = 0
    let all = []
    while (true) {
      const { data } = await queryFn(from, from + PAGE - 1)
      if (!data?.length) break
      all = all.concat(data)
      if (data.length < PAGE) break
      from += PAGE
    }
    return all
  }

  async function fetchCFEntries() {
    const data = await fetchAllRows((from, to) =>
      supabase
        .from('cf_payment_entries')
        .select('*, purchase_orders(po_number, vendor)')
        .eq('project_id', projectId)
        .order('date', { ascending: false })
        .order('id', { ascending: true })
        .range(from, to)
    )
    const ipcIds = [...new Set((data||[]).map(e=>e.ipc_id).filter(Boolean))]
    let ipcMap = {}
    if (ipcIds.length) {
      const { data: ipcRows } = await supabase.from('ipc_certificates').select('id, ipc_number').in('id', ipcIds)
      ;(ipcRows||[]).forEach(r => { ipcMap[r.id] = r.ipc_number })
    }
    const enriched = (data||[]).map(e => ({ ...e, _ipc_number: e.ipc_id ? ipcMap[e.ipc_id] : null }))
    setCfEntries(enriched)
    calcStats(cashOutEntries, cashInEntries, enriched)
  }

  // ── Cash Out — New: fetch cf_payment_entries (SSOT), enrich with PO/Subcontract data. ──
  // Enrichment NEVER alters count/amount — verified below via a direct pre/post check
  // (Section 9 of the spec). purchase_orders is Many-to-One via po_id (PK), and
  // subcontracts.po_id was confirmed unique per PO — both joins are structurally safe
  // (cannot fan out rows), but we still verify empirically on every fetch as a guardrail.
  async function cout2FetchAll() {
    if (!projectId) return
    setCout2Loading(true)
    try {
      const raw = await fetchAllRows((from, to) =>
        supabase
          .from('cf_payment_entries')
          .select('*, purchase_orders(po_number, vat_pct, status)')
          .eq('project_id', projectId)
          .order('date', { ascending: false })
          .order('id', { ascending: true })
          .range(from, to)
      )

      // Enrichment step 2: Contract Number via subcontracts.po_id (separate lookup —
      // same pattern as the existing _ipc_number enrichment above — avoids any risk of
      // the PostgREST nested-select producing more than one row per payment entry).
      const poIds = [...new Set((raw||[]).map(e => e.po_id).filter(Boolean))]
      let contractByPoId = {}
      if (poIds.length) {
        const { data: scRows } = await supabase
          .from('subcontracts')
          .select('po_id, contract_no')
          .in('po_id', poIds)
        ;(scRows||[]).forEach(r => { if (r.po_id) contractByPoId[r.po_id] = r.contract_no })
      }

      const enriched = (raw||[]).map(e => ({
        ...e,
        _po_number: e.purchase_orders?.po_number || null,
        _po_vat_pct: e.purchase_orders?.vat_pct ?? null,
        _po_status: e.purchase_orders?.status || null,
        _contract_no: e.po_id ? (contractByPoId[e.po_id] || null) : null,
      }))

      // Data-integrity guardrail (spec Section 9): enrichment must never change the
      // transaction count or the total. If it does, do not silently proceed — surface
      // it loudly so the discrepancy is caught before anyone trusts the numbers.
      const countBefore = raw.length
      const totalBefore = raw.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
      const countAfter = enriched.length
      const totalAfter = enriched.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
      if (countAfter !== countBefore || Math.abs(totalAfter - totalBefore) > 0.01) {
        console.error('[Cash Out — New] ENRICHMENT INTEGRITY FAILURE', { countBefore, countAfter, totalBefore, totalAfter })
      }

      setCout2Entries(enriched)
    } finally {
      setCout2Loading(false)
    }
  }

  // Pre-indexed paid-amount lookups — computed once whenever cfEntries changes (i.e. once per fetch, not per render)
  const [cfPaidIndex, setCfPaidIndex] = useState({ byPoLine: {}, byPoTotal: {}, byVoucherLine: {} })
  const [cfKpiTotals, setCfKpiTotals] = useState({ totalAll: 0, totalDirect: 0, totalIndirect: 0 })
  useEffect(() => {
    const byPoLine = {}, byPoTotal = {}, byVoucherLine = {}
    let totalAll = 0, totalDirect = 0, totalIndirect = 0
    for (const e of cfEntries) {
      const amt = parseFloat(e.amount_inc_vat) || 0
      if (e.po_id) {
        const lineKey = `${e.po_id}::${e.sub_code || ''}`
        byPoLine[lineKey] = (byPoLine[lineKey] || 0) + amt
        byPoTotal[e.po_id] = (byPoTotal[e.po_id] || 0) + amt
      }
      if (e.payee) {
        const vKey = `${e.payee}::${e.sub_code || ''}`
        byVoucherLine[vKey] = (byVoucherLine[vKey] || 0) + amt
      }
      totalAll += amt
      if (e.budget_type === 'indirect') totalIndirect += amt
      else totalDirect += amt
    }
    setCfPaidIndex({ byPoLine, byPoTotal, byVoucherLine })
    setCfKpiTotals({ totalAll, totalDirect, totalIndirect })
  }, [cfEntries])

  // ── Ceiling helpers: how much already paid vs. contract/voucher value ──
  // excludeId is only needed when editing an existing entry (rare path) — falls back to exact scan in that case
  function getPaidForPOLine(poId, subCode, excludeId = null) {
    if (excludeId) {
      return cfEntries
        .filter(e => e.po_id === poId && (e.sub_code || null) === (subCode || null) && e.id !== excludeId)
        .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
    }
    return cfPaidIndex.byPoLine[`${poId}::${subCode || ''}`] || 0
  }
  function getPaidForPOTotal(poId, excludeId = null) {
    if (excludeId) {
      return cfEntries
        .filter(e => e.po_id === poId && e.id !== excludeId)
        .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
    }
    return cfPaidIndex.byPoTotal[poId] || 0
  }
  function getPaidForPCVoucher(voucherNo, subCode, excludeId = null) {
    const payee = `Petty Cash — ${voucherNo}`
    if (excludeId) {
      return cfEntries
        .filter(e => e.payee === payee && (e.sub_code || null) === (subCode || null) && e.id !== excludeId)
        .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
    }
    return cfPaidIndex.byVoucherLine[`${payee}::${subCode || ''}`] || 0
  }
  // Returns { over: bool, overBy: number, ceiling: number, paidBefore: number } or null if no ceiling known
  function checkCeiling(paidBefore, newAmount, ceiling) {
    if (ceiling === null || ceiling === undefined || isNaN(ceiling)) return null
    const total = paidBefore + (parseFloat(newAmount) || 0)
    const overBy = total - ceiling
    return { over: overBy > 0.01, overBy, ceiling, paidBefore, total }
  }

  async function handleCFPoSelect(poId) {
    setCfForm(p => ({ ...p, po_id: poId, sub_code: '', cf_package: '', sub_code_desc: '', ipc_id: '' }))
    setCfLineAmounts({}); setCfScAmounts({})
    setCfPoLines([])
    setCfIpcOptions([])
    if (!poId) return
    const { data, error } = await supabase
      .from('po_line_items')
      .select('po_id, id, sub_code, description, amount_inc_vat')
      .eq('po_id', poId)
    if (error) { console.error('PO lines fetch error:', error); return }
    setCfPoLines(data || [])

    // Fetch IPCs tied to this PO's contract (material via subcontracts.po_id, equipment via equipment_contracts.po_id)
    const [{ data: matContracts }, { data: eqContracts }] = await Promise.all([
      supabase.from('subcontracts').select('id').eq('po_id', poId),
      supabase.from('equipment_contracts').select('id').eq('po_id', poId),
    ])
    const matIds = (matContracts||[]).map(c=>c.id)
    const eqIds  = (eqContracts||[]).map(c=>c.id)
    if (!matIds.length && !eqIds.length) return
    const orParts = []
    if (matIds.length) orParts.push(`subcontract_id.in.(${matIds.join(',')})`)
    if (eqIds.length)  orParts.push(`equipment_contract_id.in.(${eqIds.join(',')})`)
    const { data: ipcData, error: ipcErr } = await supabase
      .from('ipc_certificates')
      .select('id, ipc_number, net_payable, status, approval_stage')
      .or(orParts.join(','))
      .order('ipc_number')
    if (ipcErr) { console.error('IPC fetch error:', ipcErr); return }
    setCfIpcOptions(ipcData || [])
  }

  function handleCFSubCodeSelect(subCode, lineDesc = '') {
    const pkg = cfPackages.find(p => p.sub_code?.trim().toLowerCase() === subCode?.trim().toLowerCase())
    const line = cfPoLines.find(l => l.sub_code === subCode)
    setCfForm(p => ({
      ...p,
      sub_code: subCode,
      cf_package: pkg?.cf_package || '',
      sub_code_desc: pkg?.sub_code_description || lineDesc || line?.description || ''
    }))
  }

  async function saveCFEntry() {
    const hasLineAmounts = Object.values(cfLineAmounts).some(v => parseFloat(v) > 0)
    const hasScAmounts = Object.values(cfScAmounts).some(v => parseFloat(v) > 0)

    // ── Petty Cash mode ──
    if (cfForm.petty_cash) {
      if (!cfForm.date || !selectedPcVoucher || !Object.values(pcLineAmounts).some(v => parseFloat(v) > 0)) return
      const overLines = pcVoucherLines
        .filter(l => parseFloat(pcLineAmounts[l.sub_code]) > 0)
        .map(l => checkCeiling(getPaidForPCVoucher(selectedPcVoucher, l.sub_code === '—' ? null : l.sub_code), pcLineAmounts[l.sub_code], l.inc))
        .filter(c => c && c.over)
      if (overLines.length > 0) {
        const msg = `فيه تخطي عن قيمة الـ voucher في ${overLines.length} بند بإجمالي زيادة ${overLines.reduce((s,c)=>s+c.overBy,0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR. متابعة الحفظ؟`
        if (!window.confirm(msg)) return
      }
      const entries = pcVoucherLines
        .filter(l => parseFloat(pcLineAmounts[l.sub_code]) > 0)
        .map(l => {
          const pkg = cfPackages.find(p => p.sub_code?.trim().toLowerCase() === (l.sub_code||'').trim().toLowerCase())
          return {
            project_id:    projectId,
            date:          cfForm.date,
            po_id:         null,
            payee:         `Petty Cash — ${selectedPcVoucher}`,
            sub_code:      l.sub_code !== '—' ? l.sub_code : null,
            cf_package:    pkg?.cf_package || cfForm.cf_package || null,
            sub_code_desc: pkg?.sub_code_description || l.desc || null,
            amount_inc_vat: parseFloat(pcLineAmounts[l.sub_code]) || 0,
            notes:         `[Petty Cash] Voucher: ${selectedPcVoucher}`,
            is_opening:    cfForm.is_opening,
            budget_type:   pkg?.budget_type || 'direct',
          }
        })
      if (!entries.length) return
      await supabase.from('cf_payment_entries').insert(entries)
      setCfShowModal(false)
      setCfForm({ date: '', po_id: '', payee: '', sub_code: '', cf_package: '', sub_code_desc: '', amount_inc_vat: '', notes: '', is_opening: true, no_po: false, petty_cash: false })
      setSelectedPcVoucher(null); setPcVouchers([]); setPcVoucherLines([]); setPcLineAmounts({}); setPcSearch('')
      fetchCFEntries()
      return
    }

    if (!cfForm.date || (!cfForm.po_id && !cfForm.no_po)) return
    if (!hasLineAmounts && !hasScAmounts && !cfForm.amount_inc_vat) return
    const po = availablePOs.find(p => p.id === cfForm.po_id)

    if (!cfForm.no_po && cfForm.po_id) {
      if (hasLineAmounts || hasScAmounts) {
        const subCodes = [...new Set(cfPoLines.map(l => l.sub_code).filter(Boolean))]
        const overLines = subCodes.map(sc => {
          const lines = cfPoLines.filter(l => l.sub_code === sc)
          const total = parseFloat(cfScAmounts[sc]) > 0
            ? parseFloat(cfScAmounts[sc])
            : lines.reduce((s, l) => s + (parseFloat(cfLineAmounts[l.id]) || 0), 0)
          const scTotal = lines.reduce((s, l) => s + (parseFloat(l.amount_inc_vat) || 0), 0)
          if (!total) return null
          return checkCeiling(getPaidForPOLine(cfForm.po_id, sc), total, scTotal)
        }).filter(c => c && c.over)
        if (overLines.length > 0) {
          const msg = `فيه تخطي عن قيمة الـ PO line في ${overLines.length} بند بإجمالي زيادة ${overLines.reduce((s,c)=>s+c.overBy,0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR. متابعة الحفظ؟`
          if (!window.confirm(msg)) return
        }
      } else {
        const c = checkCeiling(getPaidForPOTotal(cfForm.po_id), cfForm.amount_inc_vat, parseFloat(po?.total_inc_vat))
        if (c && c.over) {
          const msg = `فيه تخطي عن قيمة الـ PO الكلية بمقدار ${c.overBy.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR. متابعة الحفظ؟`
          if (!window.confirm(msg)) return
        }
      }
    }

    if (!cfForm.no_po && (hasLineAmounts || hasScAmounts)) {
      // Group by sub_code (unique) — use sc-level amount if set, otherwise sum per-line amounts
      const subCodes = [...new Set(cfPoLines.map(l => l.sub_code).filter(Boolean))]
      const findPkg = (sc) => cfPackages.find(p => p.sub_code?.trim().toLowerCase() === sc?.trim().toLowerCase())
      const entries = subCodes
        .map(sc => {
          const lines = cfPoLines.filter(l => l.sub_code === sc)
          const total = parseFloat(cfScAmounts[sc]) > 0
            ? parseFloat(cfScAmounts[sc])
            : lines.reduce((s, l) => s + (parseFloat(cfLineAmounts[l.id]) || 0), 0)
          if (!total) return null
          const pkg = findPkg(sc)
          return {
            project_id:    projectId,
            date:          cfForm.date,
            po_id:         cfForm.po_id,
            ipc_id:        cfForm.ipc_id || null,
            payee:         po?.vendor || '',
            sub_code:      sc,
            cf_package:    pkg?.cf_package || '',
            sub_code_desc: pkg?.sub_code_description || lines[0]?.description || '',
            amount_inc_vat: total,
            notes:         cfForm.notes,
            is_opening:    cfForm.is_opening,
            budget_type:   pkg?.budget_type || 'direct',
          }
        })
        .filter(Boolean)
      if (!entries.length) return
      await supabase.from('cf_payment_entries').insert(entries)
    } else {
      // Total PO or single amount or no-PO payment
      const payload = {
        project_id:    projectId,
        date:          cfForm.date,
        po_id:         cfForm.no_po ? null : (cfForm.po_id || null),
        ipc_id:        cfForm.no_po ? null : (cfForm.ipc_id || null),
        payee:         cfForm.no_po ? cfForm.payee : (po?.vendor || cfForm.payee || ''),
        sub_code:      cfForm.sub_code || null,
        cf_package:    cfForm.cf_package || null,
        sub_code_desc: cfForm.sub_code_desc || null,
        amount_inc_vat: parseFloat(cfForm.amount_inc_vat) || 0,
        notes:         cfForm.no_po ? `[No PO] ${cfForm.notes || ''}`.trim() : (cfForm.notes || null),
        is_opening:    cfForm.is_opening,
        budget_type:   cfPackages.find(p => p.sub_code?.trim().toLowerCase() === (cfForm.sub_code||'').trim().toLowerCase())?.budget_type || 'direct',
        category:      cfForm.category || 'invoice',
        payment_type:  cfForm.payment_type || 'subcontractor_supplier',
      }
      await supabase.from('cf_payment_entries').insert(payload)
    }

    setCfShowModal(false)
    setCfForm({ date: '', po_id: '', payee: '', sub_code: '', cf_package: '', sub_code_desc: '', amount_inc_vat: '', notes: '', is_opening: true, no_po: false, petty_cash: false, category: 'invoice', payment_type: 'subcontractor_supplier' })
    setCfPoLines([])
    setCfPoSearch('')
    setCfLineAmounts({}); setCfScAmounts({})
    setCfExpandedSc({})
    setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({})
    setPcVouchers([])
    setPcSearch('')
    fetchCFEntries()
  }

  async function deleteCFEntry(id) {
    if (!window.confirm('Delete this payment?')) return
    await supabase.from('cf_payment_entries').delete().eq('id', id)
    fetchCFEntries()
  }

  async function bulkDeleteCFEntries() {
    const ids = [...cfSelected]
    if (!ids.length) return
    if (!window.confirm(`Delete ${ids.length} selected payments? This cannot be undone.`)) return
    const BATCH = 100
    for (let i = 0; i < ids.length; i += BATCH) {
      const chunk = ids.slice(i, i + BATCH)
      await supabase.from('cf_payment_entries').delete().in('id', chunk)
    }
    setCfSelected(new Set())
    await fetchCFEntries()
  }

  function downloadBpTemplate(mode) {
    let headers, sample
    if (mode === 'nopo') {
      headers = ['Date', 'Payee', 'Sub Code', 'Amount Inc VAT', 'Notes', 'Opening Balance (Yes/No)', 'Category', 'Payment Type']
      sample = [['2026-01-15', 'ABC Supplier', 'A-100', 15000, 'example note', 'No', 'Invoice', 'Subcontractor / Supplier']]
    } else if (mode === 'petty') {
      headers = ['Voucher No', 'Date', 'Sub Code', 'Amount Inc VAT', 'Opening Balance (Yes/No)', 'Category', 'Payment Type']
      sample = [['PC-001', '2026-01-15', 'A-100', 5000, 'No', 'Invoice', 'Others']]
    } else {
      headers = ['PO Number', 'Date', 'PO Line Description', 'Amount Inc VAT', 'Notes', 'Opening Balance (Yes/No)', 'Category', 'Payment Type']
      sample = [['PO-0001', '2026-01-15', 'Supply of ready mix concrete C30', 20000, 'example note', 'No', 'Invoice', 'Subcontractor / Supplier']]
    }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sample])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, `payment_upload_${mode}_template.xlsx`)
  }

  function openBpUpload(mode) {
    setBpMode(mode)
    setBpRows([])
    setBpUnmatched([])
    setBpError('')
    if (mode === 'petty' && pcVouchers.length === 0) fetchPCVouchers()
  }

  function handleBpFile(file) {
    if (!file || !bpMode) return
    setBpError('')
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
        if (!rows.length) { setBpError('الملف فارغ'); return }
        const parsed = rows.map((r, idx) => {
          const keys = Object.keys(r).map(k => k.toLowerCase().replace(/\s+/g,'_'))
          const vals = Object.values(r)
          const get = (...names) => { for (const n of names) { const i = keys.findIndex(k=>k.includes(n)); if(i>=0 && vals[i]!=='') return String(vals[i]).trim() } return '' }
          const dateRaw = get('date')
          let date = dateRaw
          if (dateRaw && !isNaN(dateRaw) && !dateRaw.includes('-') && !dateRaw.includes('/')) {
            const d = XLSX.SSF.parse_date_code(Number(dateRaw))
            if (d) date = `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`
          }
          const base = {
            _row: idx + 2, date,
            sub_code: get('sub_code','subcode'),
            po_line_desc: get('po_line_description','line_description','description'),
            amount_inc_vat: parseFloat(get('amount_inc_vat','amount')) || 0,
            notes: get('notes'),
            is_opening: ['yes','y','true','1'].includes(get('opening_balance','opening').toLowerCase()),
            category: get('category'),
            payment_type: get('payment_type'),
            _matched_sub_code: null,
            _matched_line_id: null,
          }
          if (bpMode === 'nopo') base.payee = get('payee')
          else if (bpMode === 'petty') { base.voucher_no = get('voucher_no','voucher'); base._matched_voucher = null }
          else { base.po_number = get('po_number','po_no','po'); base._matched_po_id = null }
          return base
        }).filter(r => r.date && r.amount_inc_vat > 0)

        // Resolve Category / Payment Type free text to internal values (case-insensitive match on value or label)
        const catList = getAllCategories()
        const ptList  = getAllPaymentTypes()
        parsed.forEach(r => {
          if (r.category) {
            const c = catList.find(x => x.value.toLowerCase() === r.category.toLowerCase() || x.label.toLowerCase() === r.category.toLowerCase())
            r.category = c ? c.value : 'invoice'
          } else {
            r.category = 'invoice'
          }
          if (r.payment_type) {
            const p = ptList.find(x => x.value.toLowerCase() === r.payment_type.toLowerCase() || x.label.toLowerCase() === r.payment_type.toLowerCase())
            r.payment_type = p ? p.value : 'subcontractor_supplier'
          } else {
            r.payment_type = 'subcontractor_supplier'
          }
        })

        const unmatched = []
        for (const r of parsed) {
          if (bpMode === 'nopo' && r.sub_code) {
            const found = allSubCodes.find(sc => sc.code?.trim().toLowerCase() === r.sub_code.trim().toLowerCase())
            if (found) r._matched_sub_code = found.code
            else unmatched.push({ ...r, _reason: 'subcode' })
          }
          if (bpMode === 'po') {
            const po = availablePOs.find(p => p.po_number?.trim().toLowerCase() === r.po_number?.trim().toLowerCase())
            if (po) {
              r._matched_po_id = po.id
              // Match PO line by ref_code first, then description
              const lines = poLinesMap[po.id] || []
              const desc = r.po_line_desc?.trim().toLowerCase()
              let line = lines.find(l => l.ref_code?.trim().toLowerCase() === r.po_line_desc?.trim().toLowerCase())
              if (!line && desc) line = lines.find(l => l.description?.trim().toLowerCase() === desc)
              if (line) { r._matched_line_id = line.id; r._matched_sub_code = line.sub_code }
              else unmatched.push({ ...r, _reason: 'line' })
            } else {
              unmatched.push({ ...r, _reason: 'po' })
            }
          }
          if (bpMode === 'petty') {
            const v = pcVouchers.find(v => v.voucher_no?.trim().toLowerCase() === r.voucher_no?.trim().toLowerCase())
            if (v) r._matched_voucher = v.voucher_no
            else unmatched.push({ ...r, _reason: 'voucher' })
          }
          if (bpMode === 'petty' && r.sub_code) {
            const found = allSubCodes.find(sc => sc.code?.trim().toLowerCase() === r.sub_code.trim().toLowerCase())
            if (found) r._matched_sub_code = found.code
            else unmatched.push({ ...r, _reason: (unmatched.find(u=>u._row===r._row)?._reason || '') + '_subcode' })
          }
        }
        setBpRows(parsed)
        setBpUnmatched(unmatched)
      } catch (err) {
        setBpError('خطأ في قراءة الملف: ' + err.message)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  function computeBpCeilingWarnings() {
    const warnings = []
    if (bpMode === 'po') {
      const byPo = {}
      const byLine = {}
      bpRows.forEach(r => {
        if (!r._matched_po_id) return
        byPo[r._matched_po_id] = (byPo[r._matched_po_id]||0) + r.amount_inc_vat
        if (r._matched_line_id) byLine[r._matched_line_id] = (byLine[r._matched_line_id]||0) + r.amount_inc_vat
      })
      Object.entries(byLine).forEach(([lineId, sum]) => {
        const row = bpRows.find(r => r._matched_line_id === lineId)
        const po = availablePOs.find(p => p.id === row?._matched_po_id)
        const line = (poLinesMap[row?._matched_po_id] || []).find(l => l.id === lineId)
        const paidBefore = getPaidForPOLine(row._matched_po_id, line?.sub_code)
        const c = checkCeiling(paidBefore, sum, parseFloat(line?.amount_inc_vat))
        if (c && c.over) warnings.push({ key: lineId, label: `PO ${po?.po_number} — Sub Code: ${line?.sub_code || '—'} — ${line?.ref_code || line?.description?.slice(0,30)}`, overBy: c.overBy })
      })
      Object.entries(byPo).forEach(([poId, sum]) => {
        const po = availablePOs.find(p => p.id === poId)
        const c = checkCeiling(getPaidForPOTotal(poId), sum, parseFloat(po?.total_inc_vat))
        if (c && c.over && !warnings.some(w => w.key.toString().startsWith('PO_TOTAL_'+poId))) {
          warnings.push({ key: 'PO_TOTAL_'+poId, label: `PO ${po?.po_number} (Total)`, overBy: c.overBy })
        }
      })
    }
    if (bpMode === 'petty') {
      const byVoucherSc = {}
      bpRows.forEach(r => {
        if (!r._matched_voucher) return
        const key = `${r._matched_voucher}::${r._matched_sub_code || ''}`
        byVoucherSc[key] = (byVoucherSc[key]||0) + r.amount_inc_vat
      })
      Object.entries(byVoucherSc).forEach(([key, sum]) => {
        const [voucherNo, subCode] = key.split('::')
        const voucher = pcVouchers.find(v => v.voucher_no === voucherNo)
        const ceiling = subCode ? null : parseFloat(voucher?.total_inc)
        const paidBefore = getPaidForPCVoucher(voucherNo, subCode || null)
        const c = checkCeiling(paidBefore, sum, ceiling)
        if (c && c.over) warnings.push({ key, label: `Voucher ${voucherNo}${subCode ? ' / ' + subCode : ''}`, overBy: c.overBy })
      })
    }
    return warnings
  }

  function computeBpDuplicateWarnings(validRows) {
    return validRows.filter(r => {
      if (bpMode === 'nopo') {
        return cfEntries.some(e => e.date === r.date && Math.abs((parseFloat(e.amount_inc_vat)||0) - r.amount_inc_vat) < 0.01 && (e.payee||'') === (r.payee||''))
      }
      if (bpMode === 'petty') {
        const payee = `Petty Cash — ${r._matched_voucher}`
        return cfEntries.some(e => e.date === r.date && e.payee === payee && (e.sub_code||null) === (r._matched_sub_code||null) && Math.abs((parseFloat(e.amount_inc_vat)||0) - r.amount_inc_vat) < 0.01)
      }
      return cfEntries.some(e => e.date === r.date && e.po_id === r._matched_po_id && (e.sub_code||null) === (r._matched_sub_code||null) && Math.abs((parseFloat(e.amount_inc_vat)||0) - r.amount_inc_vat) < 0.01)
    })
  }

  async function confirmBpImport() {
    if (bpUnmatched.length > 0) { setBpError('فيه صفوف محتاجة مطابقة يدوية الأول'); return }
    const validRows = bpRows.filter(r => bpMode === 'nopo' || (bpMode === 'po' ? r._matched_po_id : r._matched_voucher))
    if (!validRows.length) { setBpError('مفيش صفوف صالحة للرفع'); return }

    const dupes = computeBpDuplicateWarnings(validRows)
    const warnings = computeBpCeilingWarnings().sort((a, b) => b.overBy - a.overBy)
    if (dupes.length > 0 || warnings.length > 0) {
      setBpConfirmData({ dupes, warnings, validRows })
      return
    }
    await runBpImport(validRows)
  }

  async function runBpImport(validRows) {
    setBpUploading(true)
    const payload = validRows.map(r => {
      const pkg = cfPackages.find(p => p.sub_code?.trim().toLowerCase() === (r._matched_sub_code||'').trim().toLowerCase())
      if (bpMode === 'nopo') {
        return {
          project_id: projectId, date: r.date, po_id: null,
          payee: r.payee || '', sub_code: r._matched_sub_code || null,
          cf_package: pkg?.cf_package || null, sub_code_desc: pkg?.sub_code_description || null,
          amount_inc_vat: r.amount_inc_vat, notes: `[No PO] ${r.notes || ''}`.trim(),
          is_opening: r.is_opening, budget_type: pkg?.budget_type || 'direct', category: r.category, payment_type: r.payment_type,
        }
      }
      if (bpMode === 'petty') {
        return {
          project_id: projectId, date: r.date, po_id: null,
          payee: `Petty Cash — ${r._matched_voucher}`,
          sub_code: r._matched_sub_code || null,
          cf_package: pkg?.cf_package || null, sub_code_desc: pkg?.sub_code_description || null,
          amount_inc_vat: r.amount_inc_vat, notes: `[Petty Cash] Voucher: ${r._matched_voucher}`,
          is_opening: r.is_opening, budget_type: pkg?.budget_type || 'direct', category: r.category, payment_type: r.payment_type,
        }
      }
      const po = availablePOs.find(p => p.id === r._matched_po_id)
      return {
        project_id: projectId, date: r.date, po_id: r._matched_po_id,
        payee: po?.vendor || '', sub_code: r._matched_sub_code || null,
        cf_package: pkg?.cf_package || null, sub_code_desc: pkg?.sub_code_description || null,
        amount_inc_vat: r.amount_inc_vat, notes: r.notes || null,
        is_opening: r.is_opening, budget_type: pkg?.budget_type || 'direct', category: r.category, payment_type: r.payment_type,
      }
    })

    const { error } = await supabase.from('cf_payment_entries').insert(payload)
    setBpUploading(false)
    if (error) { setBpError('خطأ في الحفظ: ' + error.message); return }
    setBpMode(null); setBpRows([]); setBpUnmatched([]); setBpConfirmData(null)
    fetchCFEntries()
  }

  function resolveBpUnmatchedRow(row, resolvedValue) {
    setBpRows(prev => prev.map(r => {
      if (r._row !== row._row) return r
      if (row._reason?.includes('po')) return { ...r, _matched_po_id: resolvedValue }
      if (row._reason === 'line') {
        const line = (poLinesMap[r._matched_po_id] || []).find(l => l.id === resolvedValue)
        return { ...r, _matched_line_id: resolvedValue, _matched_sub_code: line?.sub_code || null }
      }
      if (row._reason?.includes('voucher')) return { ...r, _matched_voucher: resolvedValue }
      return { ...r, _matched_sub_code: resolvedValue }
    }))
    setBpUnmatched(prev => prev.filter(r => r._row !== row._row))
  }

  async function deletePCVoucherEntries(voucherNo) {
    if (!window.confirm(`حذف كل مدفوعات Petty Cash — ${voucherNo}؟`)) return
    await supabase.from('cf_payment_entries')
      .delete()
      .eq('project_id', projectId)
      .eq('payee', `Petty Cash — ${voucherNo}`)
    fetchCFEntries()
  }

  async function fetchPCVouchers() {
    if (!projectId) return
    setPcLoading(true)
    const { data } = await supabase
      .from('petty_cash_vouchers')
      .select('voucher_no, date_of_voucher, amount_exc_vat, amount_inc_vat')
      .eq('project_id', projectId)
      .order('voucher_no', { ascending: true })
    // Group by voucher_no
    const grouped = {}
    for (const r of (data || [])) {
      const vn = r.voucher_no || '—'
      if (!grouped[vn]) grouped[vn] = { voucher_no: vn, date: r.date_of_voucher, total_exc: 0, total_inc: 0 }
      grouped[vn].total_exc += parseFloat(r.amount_exc_vat) || 0
      grouped[vn].total_inc += parseFloat(r.amount_inc_vat) || 0
    }
    setPcVouchers(Object.values(grouped))
    setPcLoading(false)
  }

  async function fetchPCLines(voucher_no) {
    const { data } = await supabase
      .from('petty_cash_vouchers')
      .select('id, sub_code, cost_code_desc, item_desc, amount_exc_vat, amount_inc_vat')
      .eq('project_id', projectId)
      .eq('voucher_no', voucher_no)
      .order('sub_code')
    // Group by sub_code
    const grouped = {}
    for (const r of (data || [])) {
      const sc = r.sub_code || '—'
      if (!grouped[sc]) grouped[sc] = { sub_code: sc, desc: r.cost_code_desc || r.item_desc || '', exc: 0, inc: 0 }
      grouped[sc].exc += parseFloat(r.amount_exc_vat) || 0
      grouped[sc].inc += parseFloat(r.amount_inc_vat) || 0
    }
    const lines = Object.values(grouped)
    setPcVoucherLines(lines)
    // Pre-fill all amounts
    const amts = {}
    lines.forEach(l => { amts[l.sub_code] = l.inc.toFixed(2) })
    setPcLineAmounts(amts)
  }

  async function openEditCFEntry(e) {
    setCfEditEntry(e)
    setCfEditForm({ date: e.date || '', amount_inc_vat: e.amount_inc_vat || '', notes: e.notes || '', is_opening: !!e.is_opening, ipc_id: e.ipc_id || '' })
    setCfEditIpcOptions([])
    if (!e.po_id) return
    const [{ data: matContracts }, { data: eqContracts }] = await Promise.all([
      supabase.from('subcontracts').select('id').eq('po_id', e.po_id),
      supabase.from('equipment_contracts').select('id').eq('po_id', e.po_id),
    ])
    const matIds = (matContracts||[]).map(c=>c.id)
    const eqIds  = (eqContracts||[]).map(c=>c.id)
    if (!matIds.length && !eqIds.length) return
    const orParts = []
    if (matIds.length) orParts.push(`subcontract_id.in.(${matIds.join(',')})`)
    if (eqIds.length)  orParts.push(`equipment_contract_id.in.(${eqIds.join(',')})`)
    const { data: ipcData } = await supabase
      .from('ipc_certificates')
      .select('id, ipc_number, net_payable, status, approval_stage')
      .or(orParts.join(','))
      .order('ipc_number')
    setCfEditIpcOptions(ipcData || [])
  }

  async function saveEditCFEntry() {
    if (!cfEditForm.date || !cfEditForm.amount_inc_vat) return
    const { error, data } = await supabase.from('cf_payment_entries').update({
      date: cfEditForm.date,
      amount_inc_vat: parseFloat(cfEditForm.amount_inc_vat) || 0,
      notes: cfEditForm.notes,
      is_opening: cfEditForm.is_opening,
      ipc_id: cfEditForm.ipc_id || null,
    }).eq('id', cfEditEntry.id).select()
    if (error) { alert('فشل الحفظ: ' + error.message); return }
    if (!data || data.length === 0) { alert('لم يتم تحديث أي صف — تحقق من صلاحيات RLS.'); return }
    setCfEditEntry(null)
    fetchCFEntries()
  }

  // Parse Excel dictionary upload
  async function handleCFDictUpload(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
        const parsed = rows.map(r => {
          const keys = Object.keys(r).map(k => k.toLowerCase().replace(/\s+/g,'_'))
          const vals = Object.values(r)
          const get = (...names) => { for (const n of names) { const i = keys.findIndex(k=>k.includes(n)); if(i>=0 && vals[i]!=='') return String(vals[i]).trim() } return '' }
          return {
            project_id: projectId,
            cf_package: get('cash_flow','package','cf_package'),
            sub_code: get('sub_code','subcode'),
            sub_code_description: get('sub_code_description','description','desc'),
            budget_type: (() => {
              const raw = get('budget_type','indirect','type','budget').toLowerCase()
              return raw === 'indirect' ? 'indirect' : 'direct'
            })(),
          }
        }).filter(r => r.cf_package && r.sub_code)
        setCfUploadPreview(parsed)
      } catch(err) { alert('Error parsing file: ' + err.message) }
    }
    reader.readAsArrayBuffer(file)
  }

  async function confirmCFDictUpload() {
    if (!cfUploadPreview?.length) return
    setCfUploading(true)
    // Split: update existing via upsert, insert new ones separately
    const existing = new Set(cfPackages.map(p => p.sub_code))
    const toUpdate = cfUploadPreview.filter(r => existing.has(r.sub_code))
    const toInsert = cfUploadPreview.filter(r => !existing.has(r.sub_code))
    const errors = []
    if (toUpdate.length) {
      const { error } = await supabase.from('cf_package_dictionary').upsert(toUpdate, {
        onConflict: 'project_id,sub_code', ignoreDuplicates: false
      })
      if (error) errors.push('Update error: ' + error.message)
    }
    if (toInsert.length) {
      const { error } = await supabase.from('cf_package_dictionary').insert(toInsert)
      if (error) errors.push('Insert error: ' + error.message)
    }
    if (errors.length) alert(errors.join('\n'))
    setCfUploading(false)
    setCfUploadPreview(null)
    fetchCFPackages()
  }

  async function fetchPOLines() {
    if (!projectId) return
    setPoLinesLoading(true)
    const { data: pos, error } = await supabase
      .from('purchase_orders')
      .select('id, po_number, vendor, total_inc_vat')
      .eq('project_id', projectId)
      .order('po_number')
    if (error || !pos?.length) { setPoLines([]); setPoLinesLoading(false); return }
    const { data: payments } = await supabase
      .from('cf_payment_entries')
      .select('po_id, amount_inc_vat')
      .eq('project_id', projectId)
    const paidMap = {}
    payments?.forEach(p => {
      if (p.po_id) paidMap[p.po_id] = (paidMap[p.po_id] || 0) + parseFloat(p.amount_inc_vat || 0)
    })
    setPoLines(pos.map(po => ({
      id: po.id,
      po_number: po.po_number,
      vendor: po.vendor,
      po_amount: parseFloat(po.total_inc_vat) || 0,
      paid: paidMap[po.id] || 0,
    })))
    setPoLinesLoading(false)
  }


  async function fetchCashOut() {
    // NOTE: finance_transactions table schema no longer matches this legacy query
    // (no transaction_type/date/PO-subcontract relations columns exist on it anymore,
    // and the table currently holds 0 rows). Returning empty directly avoids a 400 error
    // until this screen is redesigned against the table's current shape.
    setCashOutEntries([])
    calcStats([], cashInEntries, cfEntries)
  }

  async function fetchCashIn() {
    const { data } = await supabase
      .from('cash_in_entries')
      .select('*')
      .eq('project_id', projectId)
      .order('period_month', { ascending: false })
    setCashInEntries(data || [])
    calcStats(cashOutEntries, data || [], cfEntries)
  }

  // Cash Out Summary modal
  const [showOutSummaryModal, setShowOutSummaryModal] = useState(false)
  const [editOutSummary, setEditOutSummary]           = useState(null)
  const [outSummaryForm, setOutSummaryForm]           = useState({ date: '', payment_type: '', amount: '', notes: '' })
  // Note: openNewOutSummary / openEditOutSummary / saveOutSummary / deleteOutSummaryEntry were removed —
  // Cash Out is now derived automatically from cf_payment_entries and is read-only.

  // cashOutSummary is now DERIVED from cf_payment_entries (Supplier/Subcontracts/Petty Cash), not manually entered.
  // Kept the same shape ({payment_type, period_month, amount, is_opening}) so all existing dashboards/reports
  // that read cashOutSummary continue to work unchanged.
  useEffect(() => {
    const rows = []
    const openingTotals = {} // payment_type -> summed amount
    const monthlyTotals = {} // `${payment_type}::${period_month}` -> summed amount
    for (const e of cfEntries) {
      const t = e.payment_type || 'subcontractor_supplier'
      const amt = -(parseFloat(e.amount_inc_vat) || 0) // cash out convention: negative
      if (e.is_opening) {
        openingTotals[t] = (openingTotals[t] || 0) + amt
      } else {
        const m = e.date?.slice(0, 7)
        if (!m) continue
        const key = `${t}::${m}`
        monthlyTotals[key] = (monthlyTotals[key] || 0) + amt
      }
    }
    Object.entries(openingTotals).forEach(([t, amount]) => {
      rows.push({ id: `opening_${t}`, project_id: projectId, payment_type: t, period_month: 'opening', amount, is_opening: true })
    })
    Object.entries(monthlyTotals).forEach(([key, amount]) => {
      const [t, m] = key.split('::')
      rows.push({ id: key, project_id: projectId, payment_type: t, period_month: m, amount, is_opening: false })
    })
    setCashOutSummary(rows)
  }, [cfEntries, projectId])

  async function fetchFinanceNotes() {
    const { data } = await supabase
      .from('finance_notes')
      .select('note_key, note_text')
      .eq('project_id', projectId)
    const map = {}
    ;(data||[]).forEach(n => { map[n.note_key] = n.note_text })
    setFinanceNotes(map)
  }

  async function saveFinanceNote(key, text) {
    await supabase.from('finance_notes').upsert({
      project_id: projectId,
      note_key: key,
      note_text: text,
      updated_at: new Date().toISOString()
    }, { onConflict: 'project_id,note_key' })
    setFinanceNotes(p => ({ ...p, [key]: text }))
    setEditingNote(null)
  }

  async function fetchPOs() {
    const { data: pos } = await supabase
      .from('purchase_orders')
      .select('id, po_number, vendor, total_inc_vat')
      .eq('project_id', projectId)
      .order('po_number')
    setAvailablePOs(pos || [])
    // Fetch all PO lines in batches (avoid Supabase's default 1000-row cap when project has many POs)
    if (pos?.length) {
      const poIds = pos.map(p => p.id)
      const map = {}
      const BATCH = 50 // po ids per query, keeps result rows well under 1000
      for (let i = 0; i < poIds.length; i += BATCH) {
        const chunk = poIds.slice(i, i + BATCH)
        const { data: lines } = await supabase
          .from('po_line_items')
          .select('po_id, id, ref_code, sub_code, description, amount_inc_vat')
          .in('po_id', chunk)
        if (lines) {
          lines.forEach(l => {
            if (!map[l.po_id]) map[l.po_id] = []
            map[l.po_id].push(l)
          })
        }
      }
      setPoLinesMap(map)
    }
  }

  async function fetchContracts() {
    const { data } = await supabase
      .from('subcontracts')
      .select('id, contract_no, contractor_name, contract_value')
      .eq('project_id', projectId)
      .order('contract_no')
    setAvailableContracts(data || [])
  }

  async function fetchCostCodes() {
    const { data } = await supabase.from('cost_codes').select('id, code, description').eq('project_id', projectId).eq('is_active', true).order('code')
    .eq('project_id', projectId)
    setCostCodes(data || [])
  }

  function calcStats(out, inn, cf) {
    const cfData    = cf !== undefined ? cf : cfEntries
    const totalCF   = cfData.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
    const totalIn   = inn.reduce((s, e) => s + (parseFloat(e.collected_amount) || parseFloat(e.amount) || 0), 0)
    // Cash out summary: negative = outflow, positive = income (e.g. VAT refund)
    const totalOut  = cashOutSummary.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0) // keeps sign
    setStats({ totalOut: Math.abs(Math.min(totalOut, 0)), totalIn, totalCF, balance })
  }

  // ===================== CASH OUT =====================
  function openNewOut() {
    setOutForm({
      date: new Date().toISOString().split('T')[0],
      payment_type: 'subcontractor_supplier', po_id: '', contract_id: '',
      payroll_period: '', petty_ref: '', payee: '', cost_code_id: '',
      amount_exc_vat: '', vat_pct: '0', vat_amount: '0', amount_inc_vat: '',
      payment_method: 'bank_transfer', notes: '', status: 'draft'
    })
    setEditOut(null)
    setShowOutModal(true)
  }

  function openEditOut(e) {
    setOutForm({
      date: e.date || '',
      payment_type: e.payment_type || 'subcontractor_supplier',
      po_id: e.po_id || '',
      contract_id: e.contract_id || '',
      payroll_period: e.payroll_period || '',
      petty_ref: e.petty_ref || '',
      payee: e.payee || '',
      cost_code_id: e.cost_code_id || '',
      amount_exc_vat: e.amount_exc_vat || '',
      vat_pct: e.vat_pct || '0',
      vat_amount: e.vat_amount || '0',
      amount_inc_vat: e.amount_inc_vat || '',
      payment_method: e.payment_method || 'bank_transfer',
      notes: e.notes || '',
      status: e.status || 'draft'
    })
    setEditOut(e)
    setShowOutModal(true)
  }

  function handleOutAmountCalc(field, value) {
    const updated = { ...outForm, [field]: value }
    const exc = parseFloat(updated.amount_exc_vat) || 0
    const vat = parseFloat(updated.vat_pct) || 0
    const vatAmt = exc * vat / 100
    updated.vat_amount = vatAmt.toFixed(2)
    updated.amount_inc_vat = (exc + vatAmt).toFixed(2)
    setOutForm(updated)
  }

  function handlePOSelect(poId) {
    const po = availablePOs.find(p => p.id === poId)
    if (po) setOutForm(f => ({ ...f, po_id: poId, payee: po.vendor || f.payee }))
    else setOutForm(f => ({ ...f, po_id: '' }))
  }

  function handleContractSelect(cId) {
    const c = availableContracts.find(c => c.id === cId)
    if (c) setOutForm(f => ({ ...f, contract_id: cId, payee: c.contractor_name || f.payee }))
    else setOutForm(f => ({ ...f, contract_id: '' }))
  }

  async function saveOut() {
    if (!outForm.date || !outForm.amount_inc_vat) {
      alert('Date and Amount are required')
      return
    }
    const payload = {
      project_id: projectId,
      transaction_type: 'cash_out',
      date: outForm.date,
      payment_type: outForm.payment_type,
      po_id: outForm.po_id || null,
      contract_id: outForm.contract_id || null,
      payroll_period: outForm.payroll_period || null,
      petty_ref: outForm.petty_ref || null,
      payee: outForm.payee,
      cost_code_id: outForm.cost_code_id || null,
      amount_exc_vat: parseFloat(outForm.amount_exc_vat) || 0,
      vat_pct: parseFloat(outForm.vat_pct) || 0,
      vat_amount: parseFloat(outForm.vat_amount) || 0,
      amount_inc_vat: parseFloat(outForm.amount_inc_vat) || 0,
      amount: parseFloat(outForm.amount_inc_vat) || 0,
      payment_method: outForm.payment_method,
      notes: outForm.notes,
      status: outForm.status
    }
    let error
    if (editOut) {
      ;({ error } = await supabase.from('finance_transactions').update(payload).eq('id', editOut.id))
    } else {
      ;({ error } = await supabase.from('finance_transactions').insert(payload))
    }
    if (error) { alert(error.message); return }
    setShowOutModal(false)
    fetchCashOut()
  }

  async function deleteOut(id) {
    await deleteEntityDocuments(id)
    if (!window.confirm('حذف؟')) return
    await supabase.from('finance_transactions').delete().eq('id', id)
    fetchCashOut()
  }

  async function postTransaction(id) {
    await supabase.from('finance_transactions').update({ status: 'posted' }).eq('id', id)
    fetchCashOut()
  }

  // ===================== CASH IN =====================
  function openNewIn() {
    setInForm({
      date: new Date().toISOString().split('T')[0],
      invoice_no: '', collected_amount: '', claimed_amount: '', certified_amount: '',
      notes: '', is_opening: false
    })
    setEditIn(null)
    setShowInModal(true)
  }

  function openEditIn(e) {
    setInForm({
      date: e.period_month || e.date || '',
      invoice_no: e.invoice_no || '',
      collected_amount: e.collected_amount || '',
      claimed_amount: e.claimed_amount || '',
      certified_amount: e.certified_amount || '',
      notes: e.notes || '',
      is_opening: !!e.is_opening
    })
    setEditIn(e)
    setShowInModal(true)
  }

  async function saveIn() {
    if (!inForm.date || !inForm.collected_amount) {
      alert('Date and Collected Amount are required')
      return
    }
    const payload = {
      project_id:       projectId,
      period_month:     inForm.date,
      invoice_no:       inForm.invoice_no || null,
      collected_amount: parseFloat(inForm.collected_amount) || 0,
      claimed_amount:   parseFloat(inForm.claimed_amount) || null,
      certified_amount: parseFloat(inForm.certified_amount) || null,
      notes:            inForm.notes || null,
      is_opening:       inForm.is_opening,
    }
    let error
    if (editIn) {
      ;({ error } = await supabase.from('cash_in_entries').update(payload).eq('id', editIn.id))
    } else {
      ;({ error } = await supabase.from('cash_in_entries').insert(payload))
    }
    if (error) { alert(error.message); return }
    setShowInModal(false)
    fetchCashIn()
  }

  async function deleteIn(id) {
    if (!window.confirm('حذف؟')) return
    await supabase.from('cash_in_entries').delete().eq('id', id)
    fetchCashIn()
  }

  // ===================== CASH IN — NEW (parallel, isolated) =====================
  // This block is entirely independent from the legacy Cash In functions above.
  // It reuses the existing backend (cash_in_entries, cash_in_allocations, triggers, RPC)
  // as the sole source of truth — no business rules are duplicated here.

  // Cash In — New is visually independent from the old data: only rows created from this
  // moment onward (via this tab) are shown, so the page starts empty regardless of any
  // pre-existing cash_in_entries rows. Nothing is deleted or modified — this is a display filter only.
  const CIN2_CUTOFF = '2026-08-24T09:30:52.000Z'

  async function cin2FetchAll() {
    const [entriesRes, allocRes, planRes, settingsRes, rulesRes, projectRes, ecScheduleRes, otherMovementsRes, categoryMasterRes] = await Promise.all([
      supabase.from('cash_in_entries').select('*').eq('project_id', projectId).gte('created_at', CIN2_CUTOFF).order('period_month', { ascending: false }),
      supabase.from('cash_in_allocations').select('*').eq('project_id', projectId).gte('created_at', CIN2_CUTOFF).order('created_at', { ascending: false }),
      supabase.from('cash_in_planning').select('*').eq('project_id', projectId).order('period_month', { ascending: false }),
      supabase.from('cashflow_settings').select('*').eq('project_id', projectId).maybeSingle(),
      supabase.from('project_cashin_rules').select('*').eq('project_id', projectId).maybeSingle(),
      supabase.from('projects').select('contract_value').eq('id', projectId).maybeSingle(),
      supabase.from('expected_collection_schedule_rules').select('*').eq('project_id', projectId).order('offset_months', { ascending: true }),
      supabase.from('cash_in_other_movements').select('*').eq('project_id', projectId).order('period_month', { ascending: false }),
      supabase.from('cash_in_category_master').select('*').eq('project_id', projectId).order('name', { ascending: true }),
    ])
    // Note: this local variable is intentionally NOT written into the shared cashInEntries
    // state (that state remains the old tab's data, untouched, and is also read by
    // buildCashFlow()/generateCashFlowReport()). Cash In — New uses its own isolated state.
    setCin2Entries(entriesRes.data || [])
    setCin2Allocations(allocRes.data || [])
    setCin2Planning(planRes.data || [])
    setCin2Settings(settingsRes.data || null)
    setCin2Rules(rulesRes.data || null)
    setCin2Project(projectRes.data || null)
    setCin2ECScheduleRules(ecScheduleRes.data || [])
    setCin2OtherMovements(otherMovementsRes.data || [])
    setCin2CategoryMaster(categoryMasterRes.data || [])
  }

  useEffect(() => { if (activeTab === 'cashin_new' && projectId) { cin2FetchAll(); cout2FetchAll() } }, [activeTab, projectId])

  function cin2OpenCollection() {
    setCin2CollectionForm({ date: cin2Month + '-01', collected_amount: '', notes: '' })
    setCin2ShowCollectionModal(true)
  }

  async function cin2SaveCollection() {
    if (!cin2CollectionForm.date || !cin2CollectionForm.collected_amount) {
      alert('Date and Amount are required')
      return
    }
    const payload = {
      project_id: projectId,
      period_month: cin2CollectionForm.date,
      transaction_type: 'collection',
      collected_amount: parseFloat(cin2CollectionForm.collected_amount) || 0,
      collection_date: cin2CollectionForm.date,
      notes: cin2CollectionForm.notes || null,
      is_opening: false,
    }
    const { error } = await supabase.from('cash_in_entries').insert(payload)
    if (error) { alert(error.message); return }
    setCin2ShowCollectionModal(false)
    cin2FetchAll()
  }

  function cin2OpenAdvance() {
    setCin2AdvanceForm({ date: cin2Month + '-01', collected_amount: '', notes: '' })
    setCin2ShowAdvanceModal(true)
  }

  async function cin2SaveAdvance() {
    if (!cin2AdvanceForm.date || !cin2AdvanceForm.collected_amount) {
      alert('Date and Amount are required')
      return
    }
    const payload = {
      project_id: projectId,
      period_month: cin2AdvanceForm.date,
      transaction_type: 'advance_received',
      collected_amount: parseFloat(cin2AdvanceForm.collected_amount) || 0,
      collection_date: cin2AdvanceForm.date,
      notes: cin2AdvanceForm.notes || null,
      is_opening: false,
    }
    // check_advance_recovery_limit() trigger is the sole enforcement point — not duplicated here.
    const { error } = await supabase.from('cash_in_entries').insert(payload)
    if (error) { alert(error.message); return }
    setCin2ShowAdvanceModal(false)
    cin2FetchAll()
  }

  // ── Cash In Rules (project_cashin_rules) — new configurable Advance/Retention engine ──
  async function cin2SaveRules() {
    const f = cin2RulesForm

    // Validate split percentages sum to 100 before writing anything (Section: "Validate
    // that total = 100%"). No partial/invalid save is persisted.
    if (f.expected_collection_method === 'split') {
      const total = cin2ECSplitRows.reduce((s,r) => s + (parseFloat(r.percentage) || 0), 0)
      if (Math.abs(total - 100) > 0.01) {
        alert(`Split percentages must total 100%. Current total: ${total}%`)
        return
      }
    }

    const payload = {
      project_id: projectId,
      has_advance: !!f.has_advance,
      advance_recovery_method: f.has_advance ? f.advance_recovery_method : null,
      advance_recovery_value: f.has_advance && f.advance_recovery_method !== 'manual' ? (parseFloat(f.advance_recovery_value) || 0) : null,
      advance_recovery_start_type: f.has_advance ? f.advance_recovery_start_type : null,
      advance_recovery_start_value: f.has_advance ? (f.advance_recovery_start_value || null) : null,
      advance_recovery_stop_type: f.has_advance ? f.advance_recovery_stop_type : null,
      advance_recovery_stop_value: f.has_advance ? (f.advance_recovery_stop_value || null) : null,
      has_retention: !!f.has_retention,
      retention_method: f.has_retention ? f.retention_method : null,
      retention_value: f.has_retention && f.retention_method !== 'manual' ? (parseFloat(f.retention_value) || 0) : null,
      retention_start_type: f.has_retention ? f.retention_start_type : null,
      retention_start_value: f.has_retention ? (f.retention_start_value || null) : null,
      retention_cap_type: f.has_retention ? f.retention_cap_type : null,
      retention_cap_value: f.has_retention && f.retention_cap_type !== 'no_cap' ? (parseFloat(f.retention_cap_value) || 0) : null,
      expected_collection_method: f.expected_collection_method || 'same_month',
      expected_collection_lag_months: f.expected_collection_method === 'fixed_lag' ? (parseInt(f.expected_collection_lag_months) || 0) : null,
      updated_at: new Date().toISOString(),
    }
    let error
    if (cin2Rules?.id) {
      ;({ error } = await supabase.from('project_cashin_rules').update(payload).eq('id', cin2Rules.id))
    } else {
      ;({ error } = await supabase.from('project_cashin_rules').insert(payload))
    }
    if (error) { alert(error.message); return }

    // Replace the split schedule rows for this project (delete-then-insert keeps this
    // simple and avoids partial-update ambiguity for a small config table).
    if (f.expected_collection_method === 'split') {
      await supabase.from('expected_collection_schedule_rules').delete().eq('project_id', projectId)
      const rows = cin2ECSplitRows.map(r => ({
        project_id: projectId, offset_months: parseInt(r.offset_months) || 0, percentage: parseFloat(r.percentage) || 0,
      }))
      const { error: schedErr } = await supabase.from('expected_collection_schedule_rules').insert(rows)
      if (schedErr) { alert(schedErr.message); return }
    } else {
      // Method changed away from 'split' — clear any stale schedule rows for this project.
      await supabase.from('expected_collection_schedule_rules').delete().eq('project_id', projectId)
    }

    setCin2ShowRulesModal(false)
    cin2FetchAll()
  }

  // Controlled reset: deletes this project's project_cashin_rules row and all
  // cert_finance_detail rows atomically via the reset_cashin_rules() RPC. Collection
  // and advance_received transactions, Scheduling data, and all other projects are
  // never touched — enforced by the RPC's own scoping, not duplicated here.
  async function cin2ResetRules() {
    if (cin2ResetConfirmText.trim().toUpperCase() !== 'RESET') {
      alert('Type RESET to confirm.')
      return
    }
    setCin2Resetting(true)
    const { data, error } = await supabase.rpc('reset_cashin_rules', { p_project_id: projectId })
    setCin2Resetting(false)
    if (error) { alert(error.message); return }
    setCin2ShowResetConfirm(false)
    setCin2ShowRulesModal(false)
    setCin2ResetConfirmText('')
    setCin2RulesForm({
      has_advance: false, advance_recovery_method: 'percentage_certified', advance_recovery_value: '',
      advance_recovery_start_type: 'first_certificate', advance_recovery_start_value: '',
      advance_recovery_stop_type: 'until_fully_recovered', advance_recovery_stop_value: '',
      has_retention: false, retention_method: 'percentage', retention_value: '',
      retention_start_type: 'first_certificate', retention_start_value: '',
      retention_cap_type: 'no_cap', retention_cap_value: '',
    })
    cin2FetchAll()
    alert(`Reset complete. Rules removed: ${data.rules_deleted}. Certification-finance-detail rows removed: ${data.cert_finance_detail_rows_deleted}.`)
  }

  function cin2OpenRules() {
    if (cin2Rules) {
      setCin2RulesForm({
        has_advance: !!cin2Rules.has_advance,
        advance_recovery_method: cin2Rules.advance_recovery_method || 'percentage_certified',
        advance_recovery_value: cin2Rules.advance_recovery_value ?? '',
        advance_recovery_start_type: cin2Rules.advance_recovery_start_type || 'first_certificate',
        advance_recovery_start_value: cin2Rules.advance_recovery_start_value ?? '',
        advance_recovery_stop_type: cin2Rules.advance_recovery_stop_type || 'until_fully_recovered',
        advance_recovery_stop_value: cin2Rules.advance_recovery_stop_value ?? '',
        has_retention: !!cin2Rules.has_retention,
        retention_method: cin2Rules.retention_method || 'percentage',
        retention_value: cin2Rules.retention_value ?? '',
        retention_start_type: cin2Rules.retention_start_type || 'first_certificate',
        retention_start_value: cin2Rules.retention_start_value ?? '',
        retention_cap_type: cin2Rules.retention_cap_type || 'no_cap',
        retention_cap_value: cin2Rules.retention_cap_value ?? '',
        expected_collection_method: cin2Rules.expected_collection_method || 'same_month',
        expected_collection_lag_months: cin2Rules.expected_collection_lag_months ?? '',
      })
    }
    setCin2ECSplitRows(cin2ECScheduleRules.length > 0
      ? cin2ECScheduleRules.map(r => ({ offset_months: r.offset_months, percentage: r.percentage }))
      : [{ offset_months: 0, percentage: 100 }])
    setCin2ShowRulesModal(true)
  }


  // Shared start-condition evaluator for both Advance Recovery and Retention.
  // certificateNumber = 1-based chronological order among months with certified_amount > 0
  // (derived client-side from cin2Planning; there is no stored "certificate number" column).
  function startConditionMet(startType, startValue, certificateNumber, periodMonth) {
    if (!startType || startType === 'first_certificate') return true
    if (startType === 'manual') return false // manual start is never auto-satisfied; Finance always enters manually
    if (startType === 'certificate_number') return certificateNumber >= (parseInt(startValue) || 1)
    if (startType === 'date') return startValue ? periodMonth >= startValue.slice(0,7) : true
    return true
  }

  // Suggestion only — never written to the database directly, never bypasses the
  // check_advance_recovery_limit() trigger, which remains the sole enforcement authority.
  function calcSuggestedAdvanceRecovery(rules, certified, certificateNumber, periodMonth, cumulativeReceived, cumulativeRecoveredSoFar) {
    if (!rules || !rules.has_advance) return 0                    // Section 8 / 11: no rule → no fabricated suggestion
    if (cumulativeReceived <= 0) return 0                          // Section 8: no valid Advance Received balance → no suggestion
    const remaining = cumulativeReceived - cumulativeRecoveredSoFar
    if (remaining <= 0) return 0                                   // Section 8: nothing left to recover
    if (!startConditionMet(rules.advance_recovery_start_type, rules.advance_recovery_start_value, certificateNumber, periodMonth)) return 0

    let calculated
    if (rules.advance_recovery_method === 'percentage_certified') calculated = certified * ((parseFloat(rules.advance_recovery_value)||0) / 100)
    else if (rules.advance_recovery_method === 'fixed_amount') calculated = parseFloat(rules.advance_recovery_value) || 0
    else return 0 // 'manual' → Section 11: no automatic suggestion

    return Math.min(calculated, remaining) // Section 9/10: never exceed remaining unrecovered Advance
  }

  function calcSuggestedRetention(rules, certified, certificateNumber, periodMonth, cumulativeRetentionSoFar, contractValue) {
    if (!rules || !rules.has_retention) return 0                   // Section 10/12: no rule → Retention = 0, no fabrication
    if (!startConditionMet(rules.retention_start_type, rules.retention_start_value, certificateNumber, periodMonth)) return 0

    let calculated
    if (rules.retention_method === 'percentage') calculated = certified * ((parseFloat(rules.retention_value)||0) / 100)
    else if (rules.retention_method === 'fixed_amount') calculated = parseFloat(rules.retention_value) || 0
    else return 0 // 'manual' → Section 11: no automatic suggestion

    if (rules.retention_cap_type === 'max_amount') {
      const remaining = (parseFloat(rules.retention_cap_value)||0) - cumulativeRetentionSoFar
      calculated = Math.min(calculated, Math.max(remaining, 0))
    } else if (rules.retention_cap_type === 'max_pct_contract' && contractValue > 0) {
      const capAmount = contractValue * ((parseFloat(rules.retention_cap_value)||0) / 100)
      const remaining = capAmount - cumulativeRetentionSoFar
      calculated = Math.min(calculated, Math.max(remaining, 0))
    }
    return Math.max(calculated, 0)
  }

  // ── Expected Collection timing (FORECAST ONLY) ──
  // This produces a distribution of a certified month's Net Certified amount across one
  // or more FUTURE target months, per the project's configured method. It never writes to
  // cash_in_entries and never creates any transaction — it is purely a display-time
  // calculation consumed by both ERP View and Cash Schedule View from the same source.
  function addMonths(ym, n) {
    const [y, m] = ym.split('-').map(Number)
    const d = new Date(Date.UTC(y, m - 1 + n, 1))
    return d.toISOString().slice(0, 7)
  }

  // Returns an array of { targetMonth, amount } — the amount to be forecast as Expected
  // Collection in each target month, for a single certification's net amount.
  function calcExpectedCollectionSchedule(rules, scheduleRules, netCertifiedAmount, certifiedMonth) {
    if (!netCertifiedAmount) return []
    const method = rules?.expected_collection_method || 'same_month'

    if (method === 'fixed_lag') {
      const lag = parseInt(rules?.expected_collection_lag_months) || 0
      return [{ targetMonth: addMonths(certifiedMonth, lag), amount: netCertifiedAmount }]
    }

    if (method === 'split') {
      // scheduleRules: [{ offset_months, percentage }, ...]. Total should equal 100 (validated
      // at save time in cin2SaveRules) — if it doesn't for any reason, the distributed amounts
      // simply won't sum to the full netCertifiedAmount, which is preferable to silently
      // fabricating an extra allocation.
      if (!scheduleRules || scheduleRules.length === 0) return [{ targetMonth: certifiedMonth, amount: netCertifiedAmount }]
      return scheduleRules.map(sr => ({
        targetMonth: addMonths(certifiedMonth, sr.offset_months),
        amount: netCertifiedAmount * ((parseFloat(sr.percentage) || 0) / 100),
      }))
    }

    // 'same_month' (default/fallback)
    return [{ targetMonth: certifiedMonth, amount: netCertifiedAmount }]
  }

  // ── Other Income / Other Deduction — independent Actual-side cash movements. ──
  // No Shift/Lag (Section 6/13 of spec: the user assigns a movement directly to a month).
  function cin2CategoryName(categoryId) {
    return cin2CategoryMaster.find(c => c.id === categoryId)?.name || '(unknown category)'
  }

  function cin2OpenAddMovement(type, month) {
    setCin2ShowAddMovementModal({ type, month: month || cin2Month, category_id: '', new_category_name: '', amount: '', notes: '' })
  }

  async function cin2SaveOtherMovement() {
    const f = cin2ShowAddMovementModal
    if (!f?.amount || !f?.month) { alert('Amount and Month are required'); return }
    let categoryId = f.category_id
    // "Create new category" flow — inserts into the reusable Category Master once,
    // then uses it for this (and future) transactions, instead of typing free text
    // every month (Section 5 of spec).
    if (categoryId === '__new__') {
      const name = (f.new_category_name || '').trim()
      if (!name) { alert('Category name is required'); return }
      const { data: newCat, error: catError } = await supabase.from('cash_in_category_master')
        .insert({ project_id: projectId, name, type: f.type, entered_by: cin2EnteredByUserId })
        .select('id').single()
      if (catError) { alert('Failed to create category: ' + catError.message); return }
      categoryId = newCat.id
    }
    if (!categoryId) { alert('Please select or create a category'); return }
    const { error } = await supabase.from('cash_in_other_movements').insert({
      project_id: projectId,
      type: f.type,
      category_id: categoryId,
      amount: parseFloat(f.amount),
      period_month: f.month.slice(0, 7) + '-01',
      source: 'manual',
      notes: f.notes || null,
      entered_by: cin2EnteredByUserId,
    })
    if (error) { alert('Failed to save: ' + error.message); return }
    setCin2ShowAddMovementModal(null)
    cin2FetchAll()
  }

  async function cin2DeleteOtherMovement(id) {
    if (!window.confirm('Delete this entry?')) return
    await supabase.from('cash_in_other_movements').delete().eq('id', id)
    cin2FetchAll()
  }

  async function cin2AddCategory(name, type) {
    const trimmed = (name || '').trim()
    if (!trimmed) return
    const { error } = await supabase.from('cash_in_category_master')
      .insert({ project_id: projectId, name: trimmed, type, entered_by: cin2EnteredByUserId })
    if (error) { alert('Failed to add category: ' + error.message); return }
    cin2FetchAll()
  }

  async function cin2ToggleCategoryActive(id, isActive) {
    await supabase.from('cash_in_category_master').update({ is_active: !isActive }).eq('id', id)
    cin2FetchAll()
  }

  // ── Certification details (Retention Held / Advance Recovered / Other Deduction — Finance-owned actual values) ──
  // Client Invoice and Certified Amount are read-only from Scheduling and are NEVER written here.
  // One certification row per month (upsert), consistent with the monthly schedule design.
  // The suggested value (from the rule engine above) is shown only as a starting point in
  // the form; the value actually saved is whatever Finance confirms/edits.
  function cin2OpenCertDetail(monthRow) {
    setCin2CertDetailMonth(monthRow.ym)
    const existing = cin2Entries.find(e => e.transaction_type === 'cert_finance_detail' && (e.period_month||'').slice(0,7) === monthRow.ym)
    setCin2CertDetailForm({
      retention_held_amount: existing ? (existing.retention_held_amount ?? '') : (monthRow.retentionSuggested || ''),
      advance_recovered_amount: existing ? (existing.advance_recovered_amount ?? '') : (monthRow.advanceRecoverySuggested || ''),
      other_deduction_amount: existing ? (existing.other_deduction_amount ?? '') : '',
      other_deduction_description: existing ? (existing.other_deduction_description ?? '') : '',
    })
    setCin2ShowCertDetailModal(true)
  }

  async function cin2SaveCertDetail() {
    const ym = cin2CertDetailMonth
    if (!ym) return
    const existing = cin2Entries.find(e => e.transaction_type === 'cert_finance_detail' && (e.period_month||'').slice(0,7) === ym)
    const retentionVal = cin2CertDetailForm.retention_held_amount === '' ? 0 : parseFloat(cin2CertDetailForm.retention_held_amount) || 0
    const recoveryVal = cin2CertDetailForm.advance_recovered_amount === '' ? 0 : parseFloat(cin2CertDetailForm.advance_recovered_amount) || 0
    const deductionVal = cin2CertDetailForm.other_deduction_amount === '' ? 0 : parseFloat(cin2CertDetailForm.other_deduction_amount) || 0
    const deductionDesc = cin2CertDetailForm.other_deduction_description || null

    let error
    if (existing) {
      // Update the existing certification row for this month — only the Finance-owned
      // fields change; Client Invoice/Certified are never touched here (they don't
      // exist on this row at all — they live in cash_in_planning, not cash_in_entries).
      ;({ error } = await supabase.from('cash_in_entries')
        .update({
          retention_held_amount: retentionVal,
          advance_recovered_amount: recoveryVal,
          other_deduction_amount: deductionVal,
          other_deduction_description: deductionDesc,
        })
        .eq('id', existing.id))
    } else {
      // No certification row yet for this month — create one purely to hold the
      // Finance-owned actual Retention/Advance-Recovery/Other-Deduction values.
      // certified_amount and claimed_amount are intentionally left out (Scheduling
      // owns Certified via cash_in_planning; this row is not a copy of it).
      ;({ error } = await supabase.from('cash_in_entries').insert({
        project_id: projectId,
        period_month: ym + '-01',
        transaction_type: 'cert_finance_detail',
        collected_amount: 0,
        retention_held_amount: retentionVal,
        advance_recovered_amount: recoveryVal,
        other_deduction_amount: deductionVal,
        other_deduction_description: deductionDesc,
        is_opening: false,
      }))
    }
    // check_advance_recovery_limit() trigger is the final enforcement layer for
    // advance_recovered_amount — its rejection message (if any) is shown as-is.
    if (error) { alert(error.message); return }
    setCin2ShowCertDetailModal(false)
    setCin2CertDetailMonth(null)
    cin2FetchAll()
  }

  function cin2OpenAllocate(collectionRow) {
    setCin2AllocateTarget(collectionRow)
    setCin2AllocateForm({ certification_id: '', allocated_amount: '' })
    setCin2ShowAllocateModal(true)
  }

  async function cin2SaveAllocation() {
    if (!cin2AllocateTarget || !cin2AllocateForm.certification_id || !cin2AllocateForm.allocated_amount) {
      alert('Certification and Amount are required')
      return
    }
    const payload = {
      collection_id: cin2AllocateTarget.id,
      certification_id: cin2AllocateForm.certification_id,
      allocated_amount: parseFloat(cin2AllocateForm.allocated_amount) || 0,
      project_id: projectId,
    }
    // DB triggers (collection limit, certification limit, same-project) are the sole
    // source of truth. Any rejection is shown to the user via the trigger's own message.
    const { error } = await supabase.from('cash_in_allocations').insert(payload)
    if (error) { alert(error.message); return }
    setCin2ShowAllocateModal(false)
    setCin2AllocateTarget(null)
    cin2FetchAll()
  }

  async function cin2ReverseAllocation(allocationId, reason) {
    if (!reason || !reason.trim()) {
      alert('A reversal reason is required')
      return
    }
    // Authorization (CashIn.ReverseAllocation) is enforced inside reverse_allocation() itself.
    const { error } = await supabase.rpc('reverse_allocation', {
      p_allocation_id: allocationId,
      p_reason: reason,
    })
    if (error) { alert(error.message); return }
    setCin2ReverseReasonFor(null)
    setCin2ReverseReason('')
    cin2FetchAll()
  }

  // Excel template/upload: Finance-owned actual cash transactions only.
  // Month | Transaction Type | Collected Amount | Reference
  // Transaction Type accepts 'collection' or 'advance_received' ONLY — both write to the
  // same cash_in_entries backend, with identical DB-trigger validation (no duplicate
  // business rules here). Certified Amount / Client Invoice are NEVER part of this upload —
  // they belong exclusively to Scheduling. Retention Held / Advance Recovered / Other
  // Deduction are also NOT uploaded here — those are entered per-month via the
  // "Record Retention / Advance Recovery / Deductions" form (cin2SaveCertDetail), which
  // performs a safe update-or-create against the single per-month detail row, avoiding
  // the duplicate-row risk that a bulk Excel insert of that same data would create.
  function cin2DownloadTemplate() {
    const rows = [
      { Month: cin2Month + '-15', 'Row Type': 'Collection', 'Transaction Type': 'collection', 'Collected Amount': 0, 'Collection Date': cin2Month + '-15', Reference: '' },
      { Month: cin2Month + '-05', 'Row Type': 'Advance', 'Transaction Type': 'advance_received', 'Collected Amount': 0, 'Collection Date': cin2Month + '-05', Reference: '' },
      { 'Row Type': 'Other Income', Month: cin2Month, Category: 'Loan', Amount: 0, Notes: '' },
      { 'Row Type': 'Other Deduction', Month: cin2Month, Category: 'Bank Charges', Amount: 0, Notes: '' },
    ]
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'cash in')
    XLSX.writeFile(wb, 'cash_in_template.xlsx')
  }

  function cin2HandleUploadFile(file) {
    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
        setCin2UploadRows(rows)
        setCin2UploadError('')
      } catch (e) {
        setCin2UploadError('Could not read file: ' + e.message)
      }
    }
    reader.readAsBinaryString(file)
  }

  async function cin2ConfirmUpload() {
    if (cin2UploadRows.length === 0) return
    setCin2Uploading(true)
    // Cash In Excel upload never requires/accepts Client Invoice or Certified Amount —
    // those belong to Scheduling. Only actual Finance-owned Collection/Advance transactions
    // are written here, using the same cash_in_entries backend and the same DB-trigger
    // validation as manual entry (no separate business rules for Excel vs manual).
    //
    // "Row Type" column routes each row to the correct destination. Rows with no Row Type
    // (or 'Collection'/'Advance') fall through to the EXISTING, UNCHANGED cash_in_entries
    // path below — this preserves the old template's behavior exactly. Other Income /
    // Other Deduction rows are diverted to cash_in_other_movements BEFORE reaching the old
    // collection/advance classification logic, so they can never be miscategorized as
    // 'collection'. Category names are matched against the Category Master by exact name
    // (case-insensitive); an unrecognized category is created automatically (Section 11:
    // "handle it explicitly" — we surface exactly which new categories were created, rather
    // than silently rejecting the row or forcing it into an existing category).
    const otherMovementRows = []
    const cashInRows = []
    cin2UploadRows.forEach(r => {
      const rowType = String(r['Row Type'] || r.row_type || '').trim().toLowerCase()
      if (rowType === 'other income') otherMovementRows.push({ r, type: 'income' })
      else if (rowType === 'other deduction') otherMovementRows.push({ r, type: 'deduction' })
      else cashInRows.push(r)  // Collection / Advance / blank — existing path, untouched
    })

    // ── Other Income / Other Deduction — category name preserved exactly as uploaded ──
    if (otherMovementRows.length > 0) {
      const newlyCreatedCategories = []
      for (const { r, type } of otherMovementRows) {
        const categoryName = String(r['Category'] ?? r.category ?? '').trim()
        const monthRaw = String(r['Month'] ?? r.month ?? r.date ?? '').slice(0, 7)
        const amount = parseFloat(r['Amount'] ?? r.amount) || 0
        if (!categoryName || !monthRaw || amount <= 0) continue
        let match = cin2CategoryMaster.find(c => c.type === type && c.name.toLowerCase() === categoryName.toLowerCase())
        if (!match) {
          const { data: newCat, error: catError } = await supabase.from('cash_in_category_master')
            .insert({ project_id: projectId, name: categoryName, type, entered_by: cin2EnteredByUserId })
            .select('id, name, type').single()
          if (catError) { setCin2Uploading(false); alert('Failed to create category "' + categoryName + '": ' + catError.message); return }
          match = newCat
          newlyCreatedCategories.push(categoryName + ' (' + type + ')')
        }
        const { error } = await supabase.from('cash_in_other_movements').insert({
          project_id: projectId, type, category_id: match.id, amount,
          period_month: monthRaw + '-01', source: 'upload',
          notes: r['Notes'] ?? r.notes ?? null, entered_by: cin2EnteredByUserId,
        })
        if (error) { setCin2Uploading(false); alert('Upload failed: ' + error.message); return }
      }
      if (newlyCreatedCategories.length > 0) {
        alert('New categories created from upload: ' + newlyCreatedCategories.join(', '))
      }
    }

    // ── Collection / Advance — EXISTING path, byte-for-byte unchanged ──
    const payload = cashInRows.map(r => {
      const rawType = String(r['Transaction Type'] || r.transaction_type || 'collection').trim().toLowerCase()
      const ttype = rawType === 'advance_received' || rawType === 'advance' ? 'advance_received' : 'collection'
      const rawDate = r['Month'] || r.date
      const collectionDate = r['Collection Date'] || r.collection_date || rawDate
      const reference = r['Reference'] ?? r.notes ?? null
      return {
        project_id: projectId,
        period_month: rawDate,
        transaction_type: ttype,
        collected_amount: parseFloat(r['Collected Amount'] ?? r.collected_amount) || 0,
        collection_date: collectionDate,
        notes: reference,
        is_opening: false,
      }
    }).filter(r => r.period_month && r.collected_amount > 0)
    if (payload.length > 0) {
      const { error } = await supabase.from('cash_in_entries').insert(payload)
      if (error) { setCin2Uploading(false); alert(error.message); return }
    }

    setCin2Uploading(false)
    setCin2ShowUpload(false)
    setCin2UploadRows([])
    cin2FetchAll()
  }

  // ===================== UPLOAD =====================
  function downloadTemplate(type) {
    let data, filename
    if (type === 'out') {
      data = [{
        date: '2026-01-15', payment_type: 'subcontractor_supplier',
        po_number: 'PO-0001', contract_no: '',
        payroll_period: '', petty_ref: '',
        payee: 'Supplier Name', cost_code: '41.001',
        amount_exc_vat: 10000, vat_pct: 15,
        payment_method: 'bank_transfer', notes: '', status: 'draft'
      }]
      filename = 'cash_out_template.xlsx'
    } else {
      // Cash In template — pivot format: rows=payment types, cols=months
      const months = []
      const start = new Date(2024, 3, 1) // Apr 2024
      for (let i = 0; i < 36; i++) {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1)
        months.push(d)
      }
      const header = ['Date', 'Opening', ...months.map(d => d)]
      const advRow = ['Advance payment', null, ...months.map(() => null)]
      const progRow = ['Progress payment', null, ...months.map(() => null)]
      const ws = XLSX.utils.aoa_to_sheet([header, advRow, progRow])
      // Format date cells in header row (skip col 0 = Date label, col 1 = Opening label)
      months.forEach((d, i) => {
        const cell = ws[XLSX.utils.encode_cell({ r: 0, c: i + 2 })]
        if (cell) {
          cell.t = 'd'
          cell.v = d
          cell.z = 'YYYY-MM-DD'
        }
      })
      const wb2 = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb2, ws, 'Cash In')
      XLSX.writeFile(wb2, 'cash_in_template.xlsx')
      return
    }
  }

  function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    setUploadError('')
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'binary' })
        // Pick sheet by name or index
        const sheetName = uploadTarget === 'in'
          ? (wb.SheetNames.find(n => n.toLowerCase().includes('cash in') || n.toLowerCase().includes('cashin')) || wb.SheetNames[0])
          : (wb.SheetNames.find(n => n.toLowerCase().includes('cash out') || n.toLowerCase().includes('cashout')) || wb.SheetNames[0])
        const ws = wb.Sheets[sheetName]
        const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null })
        if (!aoa || aoa.length < 2) { setUploadError('الملف فاضي أو format غلط'); return }

        // Detect if first column is sort_order (header row[0] is number or 'sort order')
        const firstHeader = String(aoa[0]?.[0] || '').toLowerCase().trim()
        const hasSortCol  = firstHeader === 'sort order' || firstHeader === 'sort_order' || typeof aoa[1]?.[0] === 'number'
        const typeCol     = hasSortCol ? 1 : 0
        const dateStartCol= hasSortCol ? 2 : 1
        const headerRow   = aoa[0]
        const rows = []
        const typeOrder = {}
        for (let r = 1; r < aoa.length; r++) {
          const sortVal    = hasSortCol ? aoa[r][0] : null
          const paymentType= String(aoa[r][typeCol] || '').trim()
          if (!paymentType) continue
          const ptLow = paymentType.toLowerCase()
          if (ptLow.includes('sub total') || ptLow === 'total cah out' || ptLow === 'total cash out' || ptLow === 'total') continue
          const order = (typeof sortVal === 'number') ? sortVal : (Object.keys(typeOrder).length)
          if (!(paymentType in typeOrder)) typeOrder[paymentType] = order
          for (let c = dateStartCol; c < headerRow.length; c++) {
            const rawDate = headerRow[c]
            const rawAmt  = aoa[r][c]
            if (rawDate === null || rawDate === undefined) continue
            if (rawAmt === null || rawAmt === undefined || rawAmt === '' || rawAmt === 0) continue
            const amt = typeof rawAmt === 'number' ? rawAmt : parseFloat(String(rawAmt).replace(/[, ]/g, '')) || 0
            if (amt === 0) continue
            const isOpeningCol = typeof rawDate === 'string' && rawDate.trim().toLowerCase() === 'opening'
            if (isOpeningCol) {
              rows.push({ date: null, payment_type: paymentType, amount: amt, sort_order: typeOrder[paymentType], is_opening: true })
              continue
            }
            const serial = typeof rawDate === 'number' ? rawDate : parseFloat(rawDate)
            if (isNaN(serial)) continue
            const d = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000)
            const dateStr = d.toISOString().split('T')[0]
            rows.push({ date: dateStr, payment_type: paymentType, amount: amt, sort_order: typeOrder[paymentType], is_opening: false })
          }
        }
        if (rows.length === 0) { setUploadError('مفيش بيانات في الملف'); return }
        setUploadRows(rows)
      } catch (err) { setUploadError('خطأ في قراءة الملف: ' + err.message) }
    }
    reader.readAsBinaryString(file)
  }

  async function confirmUpload() {
    setUploading(true)
    if (uploadMode === 'replace') {
      if (uploadTarget === 'out') {
        await supabase.from('cash_out_summary').delete().eq('project_id', projectId)
      } else {
        await supabase.from('cash_in_entries').delete().eq('project_id', projectId)
      }
    }
    let success = 0, failed = 0
    for (const row of uploadRows) {
      if (uploadTarget === 'out') {
        const payload = {
          project_id:   projectId,
          period_month: row.date,
          payment_type: row.payment_type,
          amount:       parseFloat(row.amount) || 0,
          notes:        row.notes || null,
          sort_order:   row.sort_order ?? 0,
        }
        const { error } = await supabase.from('cash_out_summary').insert(payload)
        if (error) { console.error('cash_out error:', JSON.stringify(error)); failed++ } else success++
      } else {
        const payload = {
          project_id:       projectId,
          period_month:     row.is_opening ? '1900-01-01' : row.date,
          invoice_no:       row.payment_type || null,
          collected_amount: parseFloat(row.amount) || 0,
          notes:            row.notes || null,
          is_opening:       !!row.is_opening,
        }
        const { error } = await supabase.from('cash_in_entries').insert(payload)
        if (error) { console.error('cash_in error:', JSON.stringify(error)); failed++ } else success++
      }
    }
    alert(`✅ تم رفع ${success} سجل${failed > 0 ? ` — فشل ${failed}` : ''}`)
    setUploadRows([])
    setShowUpload(false)
    if (fileRef.current) fileRef.current.value = ''
    fetchCashIn() // uploadTarget 'out' path is unreachable now — Cash Out is derived from cf_payment_entries
  }

  function exportCashOut() {
    const rows = filteredOut.map(e => ({
      'Date': e.date,
      'Payment Type': e.payment_type,
      'PO No': e.purchase_orders?.po_number || '',
      'Contract No': e.subcontracts?.contract_no || '',
      'Payee': e.payee,
      'Cost Code': e.cost_codes?.code || '',
      'Exc. VAT': e.amount_exc_vat,
      'VAT %': e.vat_pct,
      'VAT Amount': e.vat_amount,
      'Inc. VAT': e.amount_inc_vat,
      'Method': e.payment_method,
      'Status': e.status,
      'Notes': e.notes,
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Out')
    XLSX.writeFile(wb, 'cash_out.xlsx')
  }

  // ===================== CASH FLOW =====================
  function buildCashFlow() {
    const allMonths = [...new Set([
      ...cashInEntries.map(e => e.period_month),
      ...cashOutSummary.map(e => e.period_month),
    ])].filter(Boolean).sort()

    let cumIn = 0, cumOut = 0
    return allMonths.map(m => {
      const inAmt  = cashInEntries.filter(e => e.period_month === m).reduce((s,e) => s + (parseFloat(e.collected_amount)||0), 0)
      const outAmt = cashOutSummary.filter(e => e.period_month === m).reduce((s,e) => s + (parseFloat(e.amount)||0), 0) // signed
      cumIn  += inAmt
      cumOut += outAmt
      const balance = cumIn + cumOut // cumOut is negative
      return {
        label: new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'}),
        cashIn: inAmt,
        cashOut: Math.abs(Math.min(outAmt, 0)),
        income: Math.max(outAmt, 0),
        cumIn, cumOut: cumOut, balance,
      }
    })
  }

  const totalIn  = cashInEntries.reduce((s,e) => s + (parseFloat(e.collected_amount)||0), 0)
  const totalOut = cashOutSummary.reduce((s,e) => s + (parseFloat(e.amount)||0), 0)
  const balance  = totalIn + totalOut

  useEffect(() => { if (activeTab === 'po_lines') fetchPOLines() }, [activeTab, projectId])

  const filteredOut = cashOutEntries.filter(e => {
    if (filterMonth !== 'all' && new Date(e.date).getMonth() + 1 !== parseInt(filterMonth)) return false
    if (filterYear !== 'all' && new Date(e.date).getFullYear() !== parseInt(filterYear)) return false
    if (filterType !== 'all' && e.payment_type !== filterType) return false
    return true
  })

  const filteredIn = cashInEntries.filter(e => {
    const d = e.period_month || e.date
    if (filterMonth !== 'all' && new Date(d).getMonth() + 1 !== parseInt(filterMonth)) return false
    if (filterYear !== 'all' && new Date(d).getFullYear() !== parseInt(filterYear)) return false
    return true
  })

  const cashFlowRows = buildCashFlow()
  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
  const fmtSigned = (n) => (n >= 0 ? '+' : '') + fmt(n)

  function generateCashFlowReport() {
    const totalIn2  = cashInEntries.reduce((s,e) => s+(parseFloat(e.collected_amount)||0), 0)
    const totalOut2 = cashOutSummary.reduce((s,e) => s+(parseFloat(e.amount)||0), 0)
    const bal2      = totalIn2 + totalOut2
    const inTypesR  = [...new Set(cashInEntries.map(e => e.invoice_no || 'Other'))]
    const inMthsR   = [...new Set(cashInEntries.filter(e => !e.is_opening).map(e => e.period_month).filter(Boolean))].sort()
    const pivInR    = {}
    inTypesR.forEach(t => { pivInR[t] = {} })
    cashInEntries.forEach(e => { if (!e.is_opening) pivInR[e.invoice_no||'Other'][e.period_month] = e })
    const outTypesR = [...new Set(cashOutSummary.map(e => e.payment_type || 'Other'))]
    const outMthsR  = [...new Set(cashOutSummary.filter(e => !e.is_opening).map(e => e.period_month).filter(Boolean))].sort()
    const pivOutR   = {}
    outTypesR.forEach(t => { pivOutR[t] = {} })
    cashOutSummary.forEach(e => {
      if (e.is_opening) return
      const t = e.payment_type || 'Other'
      pivOutR[t][e.period_month] = (pivOutR[t][e.period_month] || 0) + (parseFloat(e.amount) || 0)
    })
    const allMthsR = [...new Set([...inMthsR, ...outMthsR])].sort()
    const fN = n => Math.abs(n).toLocaleString('en-US', {maximumFractionDigits:0})
    const sign = n => n < 0 ? '\u2212' : '+'

    // Build Cash In table (months as rows)
    const inOpeningByType = {}
    inTypesR.forEach(t => { inOpeningByType[t] = 0 })
    cashInEntries.forEach(e => { if (e.is_opening) inOpeningByType[e.invoice_no||'Other'] = (inOpeningByType[e.invoice_no||'Other']||0) + (parseFloat(e.collected_amount)||0) })
    const inOpeningTotal = Object.values(inOpeningByType).reduce((s,v)=>s+v,0)
    const inOpeningRow = inOpeningTotal !== 0 ? (() => {
      const cells = inTypesR.map(t => {
        const v = inOpeningByType[t] || 0
        return `<td style="text-align:right;color:${v>0?'#1A7A4A':'#BBB'};font-weight:${v>0?600:400}">${v>0?fN(v):'—'}</td>`
      }).join('')
      return `<tr style="background:#EAF5EE"><td style="background:#CDE8D8;font-weight:700;padding:4px 7px">Opening</td>${cells}<td style="color:#1A7A4A;font-weight:700;background:#CDE8D8;text-align:right">${fN(inOpeningTotal)}</td></tr>`
    })() : ''
    let inRows = inMthsR.map((m, mi) => {
      const tot = inTypesR.reduce((s,t) => s + (parseFloat(pivInR[t][m]?.collected_amount)||0), 0)
      const bg = mi % 2 === 0 ? '#FAFAF8' : '#FFFFFF'
      const cells = inTypesR.map(t => {
        const v = parseFloat(pivInR[t][m]?.collected_amount) || 0
        return `<td style="text-align:right;color:${v>0?'#1A7A4A':'#BBB'};font-weight:${v>0?600:400}">${v>0?fN(v):'—'}</td>`
      }).join('')
      return `<tr style="background:${bg}"><td style="background:#F5F1EA;font-weight:600;padding:4px 7px">${new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'numeric'})}</td>${cells}<td style="color:#1A7A4A;font-weight:700;background:#E8F5EE;text-align:right">${tot>0?fN(tot):'—'}</td></tr>`
    }).join('')
    const inTotalCells = inTypesR.map(t => {
      const s = inMthsR.reduce((a,m) => a+(parseFloat(pivInR[t][m]?.collected_amount)||0), 0) + (inOpeningByType[t]||0)
      return `<td style="text-align:right;color:#1A7A4A;font-weight:700;background:#1C1A18">${s>0?fN(s):'—'}</td>`
    }).join('')
    const inTable = `<table style="width:100%;border-collapse:collapse;font-size:8.5px">
      <thead><tr style="background:#1A7A4A;-webkit-print-color-adjust:exact;print-color-adjust:exact">
        <th style="padding:5px 7px;text-align:left;color:white;min-width:80px">Month</th>
        ${inTypesR.map(t => `<th style="padding:5px 7px;text-align:right;color:white">${t}</th>`).join('')}
        <th style="padding:5px 7px;text-align:right;color:white">Total</th>
      </tr></thead>
      <tbody>${inOpeningRow}${inRows}</tbody>
      <tfoot><tr style="background:#1C1A18;-webkit-print-color-adjust:exact;print-color-adjust:exact">
        <td style="padding:5px 7px;color:white;font-weight:700">TOTAL</td>
        ${inTotalCells}
        <td style="text-align:right;color:#1A7A4A;font-weight:700;background:#1C1A18">${fN(totalIn2)}</td>
      </tr></tfoot>
    </table>`

    // Build Cash Out table (months as rows)
    const outOpeningByType = {}
    outTypesR.forEach(t => { outOpeningByType[t] = 0 })
    cashOutSummary.forEach(e => { if (e.is_opening) outOpeningByType[e.payment_type||'Other'] = (outOpeningByType[e.payment_type||'Other']||0) + (parseFloat(e.amount)||0) })
    const outOpeningTotal = Object.values(outOpeningByType).reduce((s,v)=>s+v,0)
    const outOpeningRow = outOpeningTotal !== 0 ? (() => {
      const cells = outTypesR.map(t => {
        const v = outOpeningByType[t] || 0
        const color = v < 0 ? '#c0392b' : v > 0 ? '#1A7A4A' : '#BBB'
        return `<td style="text-align:right;color:${color};font-weight:${v!==0?600:400}">${v!==0?(sign(v)+fN(v)):'—'}</td>`
      }).join('')
      return `<tr style="background:#FDF0F0"><td style="background:#F0D5D5;font-weight:700;padding:4px 7px">Opening</td>${cells}<td style="color:#c0392b;font-weight:700;background:#F0D5D5;text-align:right">${sign(outOpeningTotal)}${fN(outOpeningTotal)}</td></tr>`
    })() : ''
    let outRows = outMthsR.map((m, mi) => {
      const tot = outTypesR.reduce((s,t) => s + (pivOutR[t][m]||0), 0)
      const bg = mi % 2 === 0 ? '#FAFAF8' : '#FFFFFF'
      const cells = outTypesR.map(t => {
        const v = pivOutR[t][m] || 0
        const color = v < 0 ? '#c0392b' : v > 0 ? '#1A7A4A' : '#BBB'
        return `<td style="text-align:right;color:${color};font-weight:${v!==0?600:400}">${v!==0?(sign(v)+fN(v)):'—'}</td>`
      }).join('')
      const totColor = tot < 0 ? '#c0392b' : '#1A7A4A'
      const totBg = tot < 0 ? '#FFF0F0' : '#E8F5EE'
      return `<tr style="background:${bg}"><td style="background:#F5F1EA;font-weight:600;padding:4px 7px">${new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'numeric'})}</td>${cells}<td style="color:${totColor};font-weight:700;background:${totBg};text-align:right">${tot!==0?(sign(tot)+fN(Math.abs(tot))):'—'}</td></tr>`
    }).join('')
    const outTotalCells = outTypesR.map(t => {
      const s = outMthsR.reduce((a,m) => a+(pivOutR[t][m]||0), 0) + (outOpeningByType[t]||0)
      return `<td style="text-align:right;color:${s<0?'#ff8080':'#1A7A4A'};font-weight:700;background:#1C1A18">${s!==0?(sign(s)+fN(Math.abs(s))):'—'}</td>`
    }).join('')
    const outTable = `<table style="width:100%;border-collapse:collapse;font-size:8.5px">
      <thead><tr style="background:#A32D2D;-webkit-print-color-adjust:exact;print-color-adjust:exact">
        <th style="padding:5px 7px;text-align:left;color:white;min-width:80px">Month</th>
        ${outTypesR.map(t => `<th style="padding:5px 7px;text-align:right;color:white">${t}</th>`).join('')}
        <th style="padding:5px 7px;text-align:right;color:white">Total</th>
      </tr></thead>
      <tbody>${outOpeningRow}${outRows}</tbody>
      <tfoot><tr style="background:#1C1A18;-webkit-print-color-adjust:exact;print-color-adjust:exact">
        <td style="padding:5px 7px;color:white;font-weight:700">TOTAL</td>
        ${outTotalCells}
        <td style="text-align:right;color:${(totalOut2)<0?'#ff8080':'#1A7A4A'};font-weight:700;background:#1C1A18">${totalOut2!==0?(sign(totalOut2)+fN(Math.abs(totalOut2))):'—'}</td>
      </tr></tfoot>
    </table>`

    // Build SVG S-Curve
    let ci=0, co=0
    const cData = allMthsR.map(m => {
      const i = cashInEntries.filter(e=>!e.is_opening && e.period_month===m).reduce((s,e)=>s+(parseFloat(e.collected_amount)||0),0)
      const o = cashOutSummary.filter(e=>!e.is_opening && e.period_month===m).reduce((s,e)=>s+(parseFloat(e.amount)||0),0)
      ci+=i; co+=o
      return { label: new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'}), i, o:Math.abs(Math.min(o,0)), ci, co:Math.abs(co), bal:ci+co }
    })
    const W=860, H=200, PL=65, PR=65, PT=20, PB=30, IW=W-PL-PR, IH=H-PT-PB
    const maxL = Math.max(...cData.map(d=>Math.max(d.i,d.o)), 1)
    const maxR = Math.max(...cData.map(d=>Math.max(d.ci,d.co,Math.abs(d.bal))), 1)
    const xp  = idx => PL + (idx/(Math.max(cData.length-1,1)))*IW
    const ylp = v => PT + IH*(1-Math.min(v/maxL,1))
    const yrp = v => PT + IH*(1-Math.min(Math.abs(v)/maxR,1))
    const bw  = Math.max(3, IW/cData.length*0.3)
    const grid = [0,.25,.5,.75,1].map(f =>
      `<line x1="${PL}" y1="${PT+IH*(1-f)}" x2="${PL+IW}" y2="${PT+IH*(1-f)}" stroke="#DDD" stroke-dasharray="3,3"/>` +
      `<text x="${PL-4}" y="${PT+IH*(1-f)+3}" text-anchor="end" font-size="7" fill="#888">${(maxL*f/1e6).toFixed(1)}M</text>` +
      `<text x="${PL+IW+4}" y="${PT+IH*(1-f)+3}" font-size="7" fill="#1A7A4A">${(maxR*f/1e6).toFixed(0)}M</text>`
    ).join('')
    const bars = cData.map((d,idx) =>
      `<rect x="${PL+idx*(IW/cData.length)}" y="${ylp(d.i)}" width="${bw}" height="${Math.max(1,IH-(ylp(d.i)-PT))}" fill="#1A7A4A"/>` +
      `<rect x="${PL+idx*(IW/cData.length)+bw+1}" y="${ylp(d.o)}" width="${bw}" height="${Math.max(1,IH-(ylp(d.o)-PT))}" fill="#A32D2D"/>`
    ).join('')
    const mkL = (key,clr,dash) =>
      `<path d="${cData.map((d,i)=>(i===0?'M':'L')+xp(i)+','+yrp(d[key])).join(' ')}" fill="none" stroke="${clr}" stroke-width="2" ${dash?'stroke-dasharray="'+dash+'"':''}/>`
    const lbls = cData.map((d,i) =>
      `<text x="${PL+i*(IW/cData.length)+bw}" y="${PT+IH+12}" text-anchor="middle" font-size="6.5" fill="#555">${d.label}</text>`
    ).join('')
    const svgCurve = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" style="width:100%;font-family:sans-serif">
      <rect width="${W}" height="${H}" fill="white"/>
      ${grid}
      <line x1="${PL}" y1="${PT}" x2="${PL}" y2="${PT+IH}" stroke="#BBB"/>
      <line x1="${PL}" y1="${PT+IH}" x2="${PL+IW}" y2="${PT+IH}" stroke="#BBB"/>
      ${bars}
      ${mkL('ci','#1A7A4A','')}
      ${mkL('co','#A32D2D','5,3')}
      ${mkL('bal','#B06D00','')}
      ${lbls}
      <rect x="${PL}" y="2" width="8" height="6" fill="#1A7A4A"/>
      <text x="${PL+10}" y="8" font-size="7">Cash In</text>
      <rect x="${PL+55}" y="2" width="8" height="6" fill="#A32D2D"/>
      <text x="${PL+65}" y="8" font-size="7">Cash Out</text>
      <line x1="${PL+115}" y1="5" x2="${PL+125}" y2="5" stroke="#1A7A4A" stroke-width="2"/>
      <text x="${PL+127}" y="8" font-size="7">Cum.In</text>
      <line x1="${PL+163}" y1="5" x2="${PL+173}" y2="5" stroke="#A32D2D" stroke-width="2" stroke-dasharray="3,2"/>
      <text x="${PL+175}" y="8" font-size="7">Cum.Out</text>
      <line x1="${PL+218}" y1="5" x2="${PL+228}" y2="5" stroke="#B06D00" stroke-width="2.5"/>
      <text x="${PL+230}" y="8" font-size="7">Balance</text>
    </svg>`

    // Notes
    const noteEntries = Object.entries(financeNotes).filter(([,v])=>v)
    const notesHtml = noteEntries.length > 0 ? `
      <div style="background:white;border-radius:8px;border:1px solid #DDD9D0;padding:12px;margin-bottom:10px">
        <h2 style="font-size:11px;font-weight:700;margin-bottom:8px">Analysis Notes</h2>
        <table style="width:100%;border-collapse:collapse;font-size:8.5px">
          ${noteEntries.map(([k,v],i) => `<tr style="background:${i%2===0?'#FAFAF8':'white'}">
            <td style="padding:4px 7px;font-weight:600;color:#6B655C;width:150px">${k.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}</td>
            <td style="padding:4px 7px" dir="auto">${v}</td>
          </tr>`).join('')}
        </table>
      </div>` : ''

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Cash Flow Report</title><style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Segoe UI',Arial,sans-serif;font-size:10px;color:#1C1A18;background:#F5F1EA;padding:14px}
      h1{font-size:16px;margin-bottom:3px}
      .meta{font-size:9px;color:#6B655C;margin-bottom:10px}
      .summary{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:10px}
      .scard{padding:10px 14px;border-radius:8px;color:white;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .scard p{font-size:9px;opacity:.75;margin-bottom:2px}
      .scard b{font-size:14px}
      .section{background:white;border-radius:8px;border:1px solid #DDD9D0;padding:12px;margin-bottom:10px}
      h2{font-size:11px;font-weight:700;margin-bottom:7px}
      .no-print{text-align:right;margin-top:10px}
      @media print{
        body{background:white!important;padding:6mm}
        .no-print{display:none}
        .section{page-break-inside:avoid}
        @page{size:A3 portrait;margin:7mm}
      }
    </style></head><body>
      <h1>Cash Flow Report</h1>
      <div class="meta">Generated: ${new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})} | ${allMthsR.length} months</div>
      <div class="summary">
        <div class="scard" style="background:#1A7A4A"><p>Total Cash In</p><b>SAR ${fN(totalIn2)}</b></div>
        <div class="scard" style="background:#A32D2D"><p>Total Cash Out</p><b>${sign(totalOut2)} SAR ${fN(Math.abs(totalOut2))}</b></div>
        <div class="scard" style="background:${bal2>=0?'#1C1A18':'#B06D00'}"><p>Balance</p><b>${bal2<0?'\u2212 ':''} SAR ${fN(Math.abs(bal2))}</b></div>
      </div>
      ${notesHtml}
      <div class="section"><h2>S-Curve \u2014 Cash In / Out / Balance</h2>${svgCurve}</div>
      <div class="section"><h2>Cash In by Month</h2>${inTable}</div>
      <div class="section"><h2>Cash Out by Month</h2>${outTable}</div>
      <div class="no-print">
        <button onclick="window.print()" style="padding:7px 18px;background:#1C1A18;color:white;border:none;border-radius:6px;cursor:pointer;font-size:11px">\uD83D\uDDA8 Print / Save PDF</button>
      </div>
    </body></html>`

    const win = window.open('', '_blank')
    win.document.write(html)
    win.document.close()
  }

  if (!projectId) return (
    <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F1017', marginBottom: 6 }}>Finance Transactions</h1>
      <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 16 }}>Select a project to continue</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => { setProjectId(p.id); setProjectName(p.name) }}
            style={{ textAlign: 'left', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#1A7A4A'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(26,122,74,.1)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none' }}>
            <p style={{ fontWeight: 700, color: '#0F1017', fontSize: 13 }}>{p.name}</p>
            <p style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{p.organizations?.name}</p>
          </button>
        ))}
      </div>
    </div>
  )

  // ── Design tokens ──
  const btnBase = { display:'inline-flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:6, fontSize:12, fontWeight:600, border:'1px solid transparent', cursor:'pointer', fontFamily:'inherit', transition:'all .12s', whiteSpace:'nowrap' }
  const btn = (bg='#0F1017',fg='#fff',border='none') => ({ ...btnBase, background:bg, color:fg, border:`1px solid ${border||'transparent'}` })
  const btnGhost = (color='#6B7280') => ({ ...btnBase, background:'transparent', color, border:`1px solid #E5E7EB` })

  return (
    <div style={{ fontFamily: 'inherit', background: '#F3F4F6', minHeight: '100vh' }}>

      {/* ── Page Header ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '18px 24px 14px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexShrink: 0 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#0F1017', letterSpacing: '-.02em' }}>Finance Transactions</h1>
          <p style={{ fontSize: 11.5, color: '#6B7280', marginTop: 3 }}>
            {projectName}
            <button onClick={() => setProjectId(null)}
              style={{ marginLeft: 8, fontSize: 10, color: '#9CA3AF', background: 'none', border: '1px solid #E5E7EB', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontFamily: 'inherit' }}>
              × Change
            </button>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab === 'cashinout' && (<>
            <button onClick={() => downloadTemplate('in')} style={btnGhost('#1A7A4A')}>↓ Template</button>
            <button onClick={() => { setUploadTarget('in'); setShowUpload(!showUpload) }} style={btnGhost('#1A7A4A')}>↑ Upload Cash In</button>
            <button onClick={openNewIn} style={btn('#1A7A4A')}>+ Cash In</button>
          </>)}
          {activeTab === 'cashout' && (<>
            <button onClick={() => downloadTemplate('out')} style={btnGhost()}>↓ Template</button>
            <button onClick={() => { setUploadTarget('out'); setShowUpload(!showUpload) }} style={btnGhost()}>↑ Upload</button>
            <button onClick={exportCashOut} style={btnGhost()}>↓ Export</button>
            <button onClick={openNewOut} style={btn()}>+ Cash Out</button>
          </>)}
        </div>
      </div>
      <div style={{ padding: '20px 24px' }}>

      {/* Stats — only on Cash In / Out tab */}
      {activeTab === 'cashinout' && (() => {
        const totalIn  = cashInEntries.reduce((s,e) => s+(parseFloat(e.collected_amount)||0), 0)
        const totalOut = cashOutSummary.reduce((s,e) => s+(parseFloat(e.amount)||0), 0)
        const balance  = totalIn + totalOut

        // Analysis calculations
        const outMonthsUniq = [...new Set(cashOutSummary.map(e=>e.period_month).filter(Boolean))]
        const burnRate = outMonthsUniq.length > 0 ? Math.abs(totalOut) / outMonthsUniq.length : 0
        const runway   = burnRate > 0 ? balance / burnRate : 0

        // Top cost category (excluding positive/income rows)
        const byType = {}
        cashOutSummary.forEach(e => {
          const t = e.payment_type || 'Other'
          byType[t] = (byType[t] || 0) + (parseFloat(e.amount) || 0)
        })
        const topCats = Object.entries(byType)
          .filter(([,v]) => v < 0)
          .sort((a,b) => a[1]-b[1])
          .slice(0,1)
        const topCat  = topCats[0]
        const topPct  = topCat && totalOut < 0 ? (topCat[1]/totalOut*100).toFixed(0) : 0

        // Payment gaps — months with outflow but no cashin
        const inMonthsSet = new Set(cashInEntries.map(e=>e.period_month).filter(Boolean))
        const gapMonths   = outMonthsUniq.filter(m => !inMonthsSet.has(m))

        // Collection efficiency
        const efficiency  = Math.abs(totalOut) > 0 ? (totalIn / Math.abs(totalOut) * 100) : 0

        return (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 }}>
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '18px 20px', borderTop: '3px solid #1A7A4A' }}>
              <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: '#6B7280', marginBottom: 6 }}>Total Cash In</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: '#1A7A4A', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>SAR {fmt(totalIn)}</p>
            </div>
            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '18px 20px', borderTop: '3px solid #DC2626' }}>
              <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: '#6B7280', marginBottom: 6 }}>Total Cash Out</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: '#DC2626', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>− SAR {fmt(Math.abs(Math.min(totalOut, 0)))}</p>
            </div>
            <div style={{ background: '#fff', border: `1px solid #E5E7EB`, borderRadius: 10, padding: '18px 20px', borderTop: `3px solid ${balance >= 0 ? '#1A7A4A' : '#D97706'}` }}>
              <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: '#6B7280', marginBottom: 6 }}>Net Position</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: balance >= 0 ? '#1A7A4A' : '#D97706', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{balance < 0 ? '− ' : ''}SAR {fmt(Math.abs(balance))}</p>
              <p style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4 }}>Cash In + Cash Out</p>
            </div>
          </div>

          {/* Analysis Cards */}
          {(cashInEntries.length > 0 || cashOutSummary.length > 0) && (() => {
            const NoteWidget = ({ noteKey, label }) => {
              const existing = financeNotes[noteKey]
              return (
                <div className="mt-2 border-t border-[#DDD9D0] pt-2">
                  {existing ? (
                    <div className="flex items-start gap-1 group/note cursor-pointer"
                      onClick={() => { setActiveNoteKey(noteKey); setActiveNoteLabel(label); setNoteText(existing); setShowNoteModal(true) }}>
                      <p className="text-xs text-[#6B655C] flex-1 leading-relaxed line-clamp-2" dir="auto">{existing}</p>
                      <span className="opacity-0 group-hover/note:opacity-100 text-[#9A948C] text-xs flex-shrink-0">✏</span>
                    </div>
                  ) : (
                    <button onClick={() => { setActiveNoteKey(noteKey); setActiveNoteLabel(label); setNoteText(''); setShowNoteModal(true) }}
                      className="text-xs text-[#9A948C] hover:text-[#1A7A4A] transition-colors">+ Add note</button>
                  )}
                </div>
              )
            }
            return (
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:20}}>
              <div style={{background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:10,padding:"14px 16px",display:"flex",flexDirection:"column"}}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-base">🔥</span>
                  <p style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".1em",color:"#4a6585",margin:0}}>Burn Rate</p>
                </div>
                <p className="text-sm font-bold text-[#1C1A18] font-mono">{fmt(burnRate)}</p>
                <p className="text-xs text-[#9A948C] mt-0.5">SAR / month avg</p>
                <div className="mt-auto"><NoteWidget noteKey="burn_rate" label="Burn Rate"/></div>
              </div>

              <div style={{background:"#fff",border:`1px solid ${runway<3?"rgba(229,72,77,.3)":runway<6?"rgba(217,165,33,.3)":"rgba(10,37,64,.1)"}`,borderRadius:10,padding:"14px 16px",display:"flex",flexDirection:"column"}}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-base">⏳</span>
                  <p style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".1em",color:"#4a6585",margin:0}}>Runway</p>
                </div>
                <p className={`text-sm font-bold font-mono ${runway < 3 ? 'text-red-600' : runway < 6 ? 'text-amber-600' : 'text-[#1C1A18]'}`}>
                  {runway > 0 ? runway.toFixed(1) : '∞'} mo
                </p>
                <p className="text-xs text-[#9A948C] mt-0.5">at current burn</p>
                <div className="mt-auto"><NoteWidget noteKey="runway" label="Cash Runway"/></div>
              </div>

              <div style={{background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:10,padding:"14px 16px",display:"flex",flexDirection:"column"}}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-base">🏆</span>
                  <p style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".1em",color:"#4a6585",margin:0}}>Top Cost</p>
                </div>
                {topCat ? <>
                  <p className="text-xs font-bold text-[#1C1A18] truncate">{topCat[0]}</p>
                  <p className="text-xs text-[#9A948C] mt-0.5">{topPct}% of total out</p>
                </> : <p className="text-xs text-[#9A948C]">—</p>}
                <div className="mt-auto"><NoteWidget noteKey="top_cost" label="Top Cost"/></div>
              </div>

              <div style={{background:"#fff",border:`1px solid ${gapMonths.length>3?"rgba(217,165,33,.3)":"rgba(10,37,64,.1)"}`,borderRadius:10,padding:"14px 16px",display:"flex",flexDirection:"column"}}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-base">⚠️</span>
                  <p style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".1em",color:"#4a6585",margin:0}}>Pay Gaps</p>
                </div>
                <p className={`text-sm font-bold font-mono ${gapMonths.length > 3 ? 'text-amber-600' : 'text-[#1C1A18]'}`}>
                  {gapMonths.length} months
                </p>
                <p className="text-xs text-[#9A948C] mt-0.5">no cash in</p>
                <div className="mt-auto"><NoteWidget noteKey="pay_gaps" label="Payment Gaps"/></div>
              </div>

              <div style={{background:"#fff",border:`1px solid ${efficiency<70?"rgba(229,72,77,.3)":efficiency<90?"rgba(217,165,33,.3)":"rgba(30,158,106,.25)"}`,borderRadius:10,padding:"14px 16px",display:"flex",flexDirection:"column"}}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-base">📈</span>
                  <p style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".1em",color:"#4a6585",margin:0}}>Collection</p>
                </div>
                <p className={`text-sm font-bold font-mono ${efficiency < 70 ? 'text-red-600' : efficiency < 90 ? 'text-amber-600' : 'text-[#1A7A4A]'}`}>
                  {efficiency.toFixed(1)}%
                </p>
                <p className="text-xs text-[#9A948C] mt-0.5">in vs out</p>
                <div className="mt-auto"><NoteWidget noteKey="collection" label="Collection Efficiency"/></div>
              </div>
            </div>
            )
          })()}
          </>
        )
      })()}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: 20, gap: 0, overflowX: 'auto' }}>
        {[
          { key: 'cashinout',        label: 'Cash In / Out' },
          { key: 'cashin_new',       label: 'Liquidity Report' },
          { key: 'cf_packages',      label: 'Payment Entries' },
          { key: 'po_lines',         label: 'PO Lines' },
          { key: 'cash_out_forecast',label: 'Cash Out Forecast' },
          { key: 'financial_fees',   label: 'Financial Fees' },
          { key: 'cf_expectation',   label: 'Cash Flow Expectation' },
        ].map(t => (
          <button key={t.key} onClick={() => { setActiveTab(t.key); setShowUpload(false) }}
            style={{ padding:'10px 18px', fontSize:12.5, fontWeight:activeTab===t.key?700:500, color:activeTab===t.key?'#0F1017':'#6B7280', background:'transparent', border:'none', borderBottom:`2px solid ${activeTab===t.key?'#1B3A6B':'transparent'}`, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Upload Zone */}
      {showUpload && (
        <div style={{background:"#fff",border:"1px solid rgba(47,128,237,.25)",borderRadius:12,padding:20,marginBottom:16}}>
          <h3 style={{fontSize:13,fontWeight:700,color:"#0a2540",marginBottom:8}}>
            رفع {uploadTarget === 'out' ? 'Cash Out' : 'Cash In'} من Excel
          </h3>
          <p style={{fontSize:11,color:"#4a6585",marginBottom:12}}>
            Format: Row 1 = Date headers (months), Row 2+ = payment types — يقرأ sheet "{uploadTarget === 'in' ? 'cash in' : 'cash out'}" تلقائي
          </p>
          <div className="flex items-center gap-3 mb-3">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload}
              className="text-sm text-[#6B655C] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1C1A18] file:text-white file:text-sm" />
          </div>
          {uploadError && <p className="text-red-500 text-sm mb-2">{uploadError}</p>}
          {uploadRows.length > 0 && (
            <div>
              <p className="text-sm text-[#1A7A4A] mb-2">تم قراءة <strong>{uploadRows.length}</strong> سجل</p>
              <div className="overflow-x-auto mb-3 max-h-32">
                <table className="text-xs w-full bg-[#EDE7DA] rounded border border-indigo-200">
                  <thead className="bg-[#DDD9D0]">
                    <tr>{Object.keys(uploadRows[0]).map(k => <th key={k} className="px-2 py-1 text-left font-medium text-[#1A7A4A]">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {uploadRows.slice(0, 3).map((row, i) => (
                      <tr key={i} className="border-t border-indigo-100">
                        {Object.values(row).map((v, j) => <td key={j} className="px-2 py-1 text-[#6B655C]">{String(v).slice(0, 12)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3 p-3 bg-[#EDE7DA] rounded-lg border border-[#DDD9D0]">
                <span className="text-xs font-medium text-[#6B655C]">الداتا الموجودة:</span>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="radio" name="uploadMode" value="replace" checked={uploadMode==='replace'}
                    onChange={() => setUploadMode('replace')} className="text-[#A32D2D]"/>
                  <span className={uploadMode==='replace'?'font-bold text-[#A32D2D]':'text-[#6B655C]'}>استبدال كل الداتا</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="radio" name="uploadMode" value="append" checked={uploadMode==='append'}
                    onChange={() => setUploadMode('append')} className="text-[#1A7A4A]"/>
                  <span className={uploadMode==='append'?'font-bold text-[#1A7A4A]':'text-[#6B655C]'}>إضافة فوق الموجود</span>
                </label>
              </div>
              <button onClick={confirmUpload} disabled={uploading}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${uploading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#1C1A18] text-white hover:bg-[#2E2C29]'}`}>
                {uploading ? '⏳ جاري الرفع...' : `✓ تأكيد الرفع (${uploadRows.length} سجل)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {activeTab !== 'cashflow' && (
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            style={{border:"1px solid rgba(10,37,64,.15)",borderRadius:7,padding:"6px 10px",fontSize:12,color:"#0a2540",background:"#fff",fontFamily:"inherit"}}>
            <option value="all">All Months</option>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
            style={{border:"1px solid rgba(10,37,64,.15)",borderRadius:7,padding:"6px 10px",fontSize:12,color:"#0a2540",background:"#fff",fontFamily:"inherit"}}>
            <option value="all">All Years</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {activeTab === 'cashout' && (
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{border:"1px solid rgba(10,37,64,.15)",borderRadius:7,padding:"6px 10px",fontSize:12,color:"#0a2540",background:"#fff",fontFamily:"inherit"}}>
              <option value="all">All Types</option>
              {getAllPaymentTypes().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          )}
        </div>
      )}

      {/* ===================== CASH OUT TAB ===================== */}
      {activeTab === 'cashout' && (
        filteredOut.length === 0 ? (
          <div style={{textAlign:"center",padding:"60px 20px",background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:12}}>
            <div style={{fontSize:40,marginBottom:12}}>💸</div>
            <p style={{color:"#4a6585",fontSize:13}}>لا يوجد مدفوعات. اضغط "+ Cash Out" أو ارفع من Excel.</p>
          </div>
        ) : (
          <div style={{background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:12,overflow:"hidden",boxShadow:"0 2px 8px rgba(10,37,64,.06)"}}>
            <table className="w-full text-sm">
              <thead style={{background:"#0a2540"}}>
                <tr>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Date</th>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Type</th>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Reference</th>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Payee</th>
                  <th style={{textAlign:"left",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Cost Code</th>
                  <th style={{textAlign:"right",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Exc. VAT</th>
                  <th style={{textAlign:"right",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>VAT</th>
                  <th style={{textAlign:"right",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.85)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap",background:"rgba(229,72,77,.15)"}}>Inc. VAT</th>
                  <th style={{textAlign:"center",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Method</th>
                  <th style={{textAlign:"center",padding:"10px 12px",fontSize:10,fontWeight:600,color:"rgba(244,246,249,.65)",letterSpacing:".06em",fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap"}}>Status</th>
                  <th style={{padding:"10px 12px"}}></th>
                </tr>
              </thead>
              <tbody>
                {filteredOut.map(e => (
                  <tr key={e.id} style={{borderBottom:'1px solid rgba(10,37,64,.05)',cursor:'default'}}>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#4a6585",fontFamily:"'IBM Plex Mono',monospace"}}>{e.date}</td>
                    <td className="px-3 py-3">
                      <span style={{padding:'2px 8px',borderRadius:20,fontSize:10,fontWeight:600,background:e.payment_type==='subcontractor_supplier'?'rgba(47,128,237,.1)':e.payment_type==='others'?'rgba(217,165,33,.12)':'rgba(30,158,106,.1)',color:e.payment_type==='subcontractor_supplier'?'#2f80ed':e.payment_type==='others'?'#b07900':'#1e9e6a',textTransform:'capitalize'}}>
                        {paymentTypeLabel(e.payment_type)}
                      </span>
                    </td>
                    <td style={{padding:"10px 12px",fontSize:11,color:"#4a6585",fontFamily:"'IBM Plex Mono',monospace"}}>{e.purchase_orders?.po_number || e.subcontracts?.contract_no || e.payroll_period || e.petty_ref || '—'}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#0a2540",maxWidth:140,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.payee}</td>
                    <td style={{padding:"10px 12px",fontSize:11,color:"#4a6585",fontFamily:"'IBM Plex Mono',monospace"}}>{e.cost_codes?.code || '—'}</td>
                    <td style={{padding:"10px 12px",fontSize:12,textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",color:"#0a2540"}}>{fmt(e.amount_exc_vat)}</td>
                    <td style={{padding:"10px 12px",fontSize:12,textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",color:"#4a6585"}}>{fmt(e.vat_amount)}</td>
                    <td style={{padding:"10px 12px",fontSize:12,textAlign:"right",fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:"#e5484d",background:"rgba(229,72,77,.04)"}}>{fmt(e.amount_inc_vat)}</td>
                    <td style={{padding:"10px 12px",fontSize:11,textAlign:"center",color:"#4a6585"}}>{e.payment_method?.replace('_', ' ')}</td>
                    <td className="px-3 py-3 text-center">
                      <span style={{padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:600,background:e.status==='posted'?'rgba(30,158,106,.1)':'rgba(10,37,64,.07)',color:e.status==='posted'?'#1e9e6a':'#4a6585'}}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {e.status === 'draft' && (
                          <button onClick={() => postTransaction(e.id)} className="text-green-600 text-xs px-2 py-1 rounded border border-green-200 hover:bg-green-50">Post</button>
                        )}
                        <button onClick={() => openEditOut(e)} className="text-[#9A948C] hover:text-[#6B655C]">✏</button>
                        <DocButton entityId={e.id} entityType="finance" projectId={e.project_id}/>
                        <button onClick={() => deleteOut(e.id)} className="text-[#9A948C] hover:text-red-500">🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                <tr>
                  <td colSpan={7} className="px-3 py-3 font-medium text-[#6B655C]">
                    Total Posted ({filteredOut.filter(e => e.status === 'posted').length})
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-red-600 bg-red-50">
                    {fmt(filteredOut.filter(e => e.status === 'posted').reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0))}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {/* ===================== CASH IN / OUT TAB ===================== */}
      {activeTab === 'cashinout' && (() => {
        // ── Cash In pivot ──
        const inTypes  = [...new Set(cashInEntries.map(e => e.invoice_no || 'Other'))]
        const inMonths = [...new Set(cashInEntries.filter(e => !e.is_opening).map(e => e.period_month).filter(Boolean))].sort()
        const pivotIn  = {}
        const pivotInOpening = {} // pivotInOpening[type] -> summed amount
        inTypes.forEach(t => { pivotIn[t] = {}; pivotInOpening[t] = 0 })
        cashInEntries.forEach(e => {
          const t = e.invoice_no || 'Other'
          if (e.is_opening) {
            pivotInOpening[t] = (pivotInOpening[t] || 0) + (parseFloat(e.collected_amount) || 0)
          } else {
            pivotIn[t][e.period_month] = e
          }
        })
        const inColTotals  = inMonths.map(m => inTypes.reduce((s,t) => s+(parseFloat(pivotIn[t][m]?.collected_amount)||0), 0))
        const inOpeningTotal = inTypes.reduce((s,t) => s+(pivotInOpening[t]||0), 0)
        const inGrandTotal = inColTotals.reduce((s,v) => s+v, 0) + inOpeningTotal

        // ── Cash Out pivot (read-only, derived from cf_payment_entries — grouped by payment_type) ──
        const outTypes  = getAllPaymentTypes().map(t => t.value).filter(v => cfEntries.some(e => (e.payment_type||'subcontractor_supplier') === v))
        const outMonths = [...new Set(cfEntries.filter(e => !e.is_opening).map(e => e.date?.slice(0,7)).filter(Boolean))].sort()
        const allMonths = [...new Set([...inMonths, ...outMonths])].sort()
        const pivotOut  = {}       // pivotOut[type][period_month] -> amount (negative, cash out convention)
        const pivotOutOpening = {} // pivotOutOpening[type] -> amount
        outTypes.forEach(t => { pivotOut[t] = {}; pivotOutOpening[t] = 0 })
        cfEntries.forEach(e => {
          const t = e.payment_type || 'subcontractor_supplier'
          if (!pivotOut[t]) return
          const amt = -(parseFloat(e.amount_inc_vat) || 0) // cash out convention: negative
          if (e.is_opening) {
            pivotOutOpening[t] += amt
          } else {
            const m = e.date?.slice(0,7)
            if (!m) return
            if (!pivotOut[t][m]) pivotOut[t][m] = 0
            pivotOut[t][m] += amt
          }
        })
        const outColTotals  = outMonths.map(m => outTypes.reduce((s,t) => s+(pivotOut[t][m]||0), 0))
        const outOpeningTotal = outTypes.reduce((s,t) => s+(pivotOutOpening[t]||0), 0)
        const outGrandTotal = outColTotals.reduce((s,v) => s+v, 0) + outOpeningTotal

        // ── Combined S-Curve data ──
        let cumIn = 0, cumOut = 0
        const curveData = allMonths.map(m => {
          const mIn  = cashInEntries.filter(e => !e.is_opening && e.period_month === m).reduce((s,e) => s+(parseFloat(e.collected_amount)||0), 0)
          const mOut = cfEntries.filter(e => !e.is_opening && e.date?.slice(0,7) === m).reduce((s,e) => s-(parseFloat(e.amount_inc_vat)||0), 0)
          cumIn  += mIn
          cumOut += mOut
          return {
            label: new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'}),
            'Cash In': mIn,
            'Cash Out': Math.abs(Math.min(mOut,0)),
            'Cum. In': cumIn,
            'Cum. Out': Math.abs(cumOut),
            'Balance': cumIn + cumOut,
          }
        })

        return (
          <div className="space-y-6">

            {/* ── Cash In Pivot ── */}
            <div>
              <h3 style={{fontSize:12,fontWeight:700,color:"#0a2540",marginBottom:10,display:"flex",alignItems:"center",gap:8,textTransform:"uppercase",letterSpacing:".08em"}}><span style={{width:8,height:8,borderRadius:"50%",background:"#1e9e6a",display:"inline-block"}}/>Cash In</h3>
              {cashInEntries.length === 0 ? (
                <div style={{textAlign:"center",padding:"32px",background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:12,fontSize:12,color:"#4a6585"}}>لا يوجد بيانات — ارفع Cash In</div>
              ) : (
                <div style={{overflowX:"auto",borderRadius:12,border:"1px solid rgba(10,37,64,.1)",boxShadow:"0 2px 8px rgba(10,37,64,.06)"}}>
                  <table className="text-xs min-w-max">
                    <thead>
                      <tr style={{background:"#0a2540"}}>
                        <th className="sticky left-0 z-10 bg-[#1A7A4A] text-left px-4 py-3 text-white min-w-[220px]">Payment Type</th>
                        <th className="px-3 py-3 text-slate-100 text-right whitespace-nowrap min-w-[130px] bg-blue-900/40">Opening</th>
                        {inMonths.map(m => <th key={m} className="px-3 py-3 text-slate-100 text-right whitespace-nowrap min-w-[140px]">{new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}</th>)}
                        <th className="px-4 py-3 text-right text-white font-bold whitespace-nowrap">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inTypes.map((t,ti) => {
                        const rowTotal = inMonths.reduce((s,m) => s+(parseFloat(pivotIn[t][m]?.collected_amount)||0), 0) + (pivotInOpening[t]||0)
                        return (
                          <tr key={t} className={ti%2===0?'bg-white':'bg-[#FDFCFA]'}>
                            <td className="sticky left-0 z-10 px-4 py-2.5 font-semibold text-[#1C1A18] border-r border-[#DDD9D0] min-w-[220px]" style={{background:ti%2===0?'white':'#FDFCFA'}}>{t}</td>
                            <td className="px-2 py-2 text-right min-w-[130px] whitespace-nowrap bg-blue-50/50">
                              {pivotInOpening[t] ? (
                                <span className="font-bold text-[#1A7A4A] whitespace-nowrap">{pivotInOpening[t].toLocaleString('en-US',{maximumFractionDigits:0})}</span>
                              ) : '—'}
                            </td>
                            {inMonths.map(m => {
                              const e = pivotIn[t][m]
                              const v = parseFloat(e?.collected_amount)||0
                              return (
                                <td key={m} className="px-2 py-2 text-right group relative min-w-[130px] whitespace-nowrap">
                                  {e ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="font-bold text-[#1A7A4A] whitespace-nowrap">{v.toLocaleString('en-US',{maximumFractionDigits:0})}</span>
                                      <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 transition-opacity">
                                        <button onClick={() => openEditIn(e)} className="text-[#9A948C] hover:text-[#1A7A4A] text-xs px-1" title="Edit">✏</button>
                                        <button onClick={() => deleteIn(e.id)} className="text-[#9A948C] hover:text-red-500 text-xs px-1" title="Delete">🗑</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button onClick={() => { setInForm({date:m, invoice_no:t, collected_amount:'', claimed_amount:'', certified_amount:'', notes:'', is_opening:false}); setEditIn(null); setShowInModal(true) }}
                                      className="opacity-0 group-hover:opacity-100 text-[#9A948C] hover:text-[#1A7A4A] text-xs transition-opacity" title="Add">＋</button>
                                  )}
                                </td>
                              )
                            })}
                            <td className="px-4 py-2 text-right font-bold text-[#1A7A4A] bg-[#E8F5EE] min-w-[130px] whitespace-nowrap">{rowTotal>0?rowTotal.toLocaleString('en-US',{maximumFractionDigits:0}):'—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:"#0a2540"}}>
                        <td style={{position:"sticky",left:0,zIndex:10,background:"#0a2540",padding:"10px 16px",fontWeight:700,color:"rgba(244,246,249,.9)",minWidth:180,fontFamily:"'IBM Plex Mono',monospace",fontSize:12}}>Total</td>
                        <td className="px-3 py-3 text-right font-bold text-[#1A7A4A] whitespace-nowrap bg-blue-950/40">{inOpeningTotal>0?inOpeningTotal.toLocaleString('en-US',{maximumFractionDigits:0}):'—'}</td>
                        {inColTotals.map((t,i) => <td key={i} className="px-3 py-3 text-right font-bold text-[#1A7A4A] whitespace-nowrap">{t>0?t.toLocaleString('en-US',{maximumFractionDigits:0}):'—'}</td>)}
                        <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">{inGrandTotal.toLocaleString('en-US',{maximumFractionDigits:0})}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* ── Cash Out Pivot ── */}
            <div>
              <h3 style={{fontSize:12,fontWeight:700,color:"#0a2540",marginBottom:2,display:"flex",alignItems:"center",gap:8,textTransform:"uppercase",letterSpacing:".08em"}}><span style={{width:8,height:8,borderRadius:"50%",background:"#e5484d",display:"inline-block"}}/>Cash Out</h3>
              <p style={{fontSize:11,color:"#9CA3AF",marginBottom:8}}>Auto-calculated from Payment Entries — read only.</p>
              {outTypes.length === 0 ? (
                <div style={{textAlign:"center",padding:"32px",background:"#fff",border:"1px solid rgba(10,37,64,.1)",borderRadius:12,fontSize:12,color:"#4a6585"}}>لا يوجد بيانات — سجّل مدفوعات في تاب Payment Entries</div>
              ) : (
                <div style={{overflowX:"auto",borderRadius:12,border:"1px solid rgba(10,37,64,.1)",boxShadow:"0 2px 8px rgba(10,37,64,.06)"}}>
                  <table className="text-xs min-w-max">
                    <thead>
                      <tr style={{background:"#0a2540"}}>
                        <th className="sticky left-0 z-10 bg-[#A32D2D] text-left px-4 py-3 text-white min-w-[220px]">Payment Type</th>
                        <th className="px-3 py-3 text-red-100 text-right whitespace-nowrap min-w-[130px] bg-blue-900/40">Opening</th>
                        {outMonths.map(m => <th key={m} className="px-3 py-3 text-red-100 text-right whitespace-nowrap min-w-[140px]">{new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}</th>)}
                        <th className="px-4 py-3 text-right text-white font-bold whitespace-nowrap">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outTypes.map((t,ti) => {
                        const rowTotal = outMonths.reduce((s,m) => s+(pivotOut[t][m]||0), 0) + (pivotOutOpening[t]||0)
                        const isPositive = rowTotal > 0
                        return (
                          <tr key={t} className={ti%2===0?'bg-white':'bg-[#FDFCFA]'}>
                            <td className="sticky left-0 z-10 px-4 py-2.5 font-semibold text-[#1C1A18] border-r border-[#DDD9D0] min-w-[220px]" style={{background:ti%2===0?'white':'#FDFCFA'}}>{paymentTypeLabel(t)}</td>
                            <td className="px-2 py-2 text-right min-w-[130px] whitespace-nowrap bg-blue-50/50">
                              {pivotOutOpening[t] !== 0 ? (
                                <span className={`font-bold ${pivotOutOpening[t] < 0 ? 'text-red-600' : 'text-[#1A7A4A]'}`}>
                                  {pivotOutOpening[t] < 0 ? '−' : '+'}{Math.abs(pivotOutOpening[t]).toLocaleString('en-US',{maximumFractionDigits:0})}
                                </span>
                              ) : '—'}
                            </td>
                            {outMonths.map(m => {
                              const v = pivotOut[t][m] || 0
                              return (
                                <td key={m} className="px-2 py-2 text-right min-w-[130px] whitespace-nowrap">
                                  {v !== 0 ? (
                                    <span className={`font-bold ${v < 0 ? 'text-red-600' : 'text-[#1A7A4A]'}`}>
                                      {v < 0 ? '−' : '+'}{Math.abs(v).toLocaleString('en-US',{maximumFractionDigits:0})}
                                    </span>
                                  ) : '—'}
                                </td>
                              )
                            })}
                            <td className={`px-4 py-2 text-right font-bold min-w-[130px] ${isPositive ? 'text-[#1A7A4A] bg-[#E8F5EE]' : 'text-red-600 bg-red-50'}`}>
                              {rowTotal !== 0 ? `${rowTotal<0?'−':'+'}${Math.abs(rowTotal).toLocaleString('en-US',{maximumFractionDigits:0})}` : '—'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1C1A18]">
                        <td className="sticky left-0 z-10 bg-[#1C1A18] px-4 py-3 font-bold text-white min-w-[220px]">Total</td>
                        <td className={`px-3 py-3 text-right font-bold bg-blue-950/40 ${outOpeningTotal<0?'text-red-400':'text-[#1A7A4A]'}`}>{outOpeningTotal!==0?`${outOpeningTotal<0?'−':'+'}${Math.abs(outOpeningTotal).toLocaleString('en-US',{maximumFractionDigits:0})}`:'—'}</td>
                        {outColTotals.map((t,i) => <td key={i} className={`px-3 py-3 text-right font-bold ${t<0?'text-red-400':'text-[#1A7A4A]'}`}>{t!==0?`${t<0?'−':'+'}${Math.abs(t).toLocaleString('en-US',{maximumFractionDigits:0})}` :'—'}</td>)}
                        <td className={`px-4 py-3 text-right font-bold ${outGrandTotal<0?'text-red-400':'text-[#1A7A4A]'}`}>{outGrandTotal!==0?`${outGrandTotal<0?'−':'+'}${Math.abs(outGrandTotal).toLocaleString('en-US',{maximumFractionDigits:0})}`:'—'}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* ── Combined S-Curve ── */}
            {curveData.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#DDD9D0] p-8 text-center">
                <p className="text-sm text-[#6B655C] font-medium">مفيش رسم بياني لسه</p>
                <p className="text-xs text-[#9A948C] mt-1">الـ Curve بيتبني من الدفعات اللي ليها تاريخ فعلي (Normal) — دفعات Opening بس مش كفاية لرسمه</p>
              </div>
            ) : (() => {
              const filteredCurve = curveData.filter(d => {
                // find month key from label by matching back to allMonths
                const idx = curveData.indexOf(d)
                const m   = allMonths[idx]
                if (curveMonthFrom && m < curveMonthFrom) return false
                if (curveMonthTo   && m > curveMonthTo)   return false
                return true
              })
              // Recalc cumulative on filtered data
              let ci=0, co=0
              const plotData = filteredCurve.map(d => {
                ci += d['Cash In'] || 0
                co += d['Cash Out'] || 0
                return { ...d, 'Cum. In': ci, 'Cum. Out': co, Balance: ci - co }
              })
              return (
              <div className="bg-white rounded-xl border border-[#DDD9D0] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="text-sm font-bold text-[#1C1A18]">Combined S-Curve</h3>
                  {/* Month range filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#6B655C]">From</span>
                    <select value={curveMonthFrom} onChange={e => setCurveMonthFrom(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none">
                      <option value="">All</option>
                      {allMonths.map(m => <option key={m} value={m}>{new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}</option>)}
                    </select>
                    <span className="text-xs text-[#6B655C]">To</span>
                    <select value={curveMonthTo} onChange={e => setCurveMonthTo(e.target.value)}
                      className="text-xs border border-[#DDD9D0] rounded px-2 py-1 focus:outline-none">
                      <option value="">All</option>
                      {allMonths.map(m => <option key={m} value={m}>{new Date(m+'T00:00:00').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}</option>)}
                    </select>
                    {(curveMonthFrom || curveMonthTo) && (
                      <button onClick={() => { setCurveMonthFrom(''); setCurveMonthTo('') }}
                        className="text-xs text-[#9A948C] hover:text-red-500 px-1">✕ Reset</button>
                    )}
                  </div>
                  {/* Series filters */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {[
                      { key: 'cashIn',  label: 'Bar: Cash In',  color: '#1A7A4A' },
                      { key: 'cashOut', label: 'Bar: Cash Out', color: '#A32D2D' },
                      { key: 'cumIn',   label: 'Cum. In',       color: '#1A7A4A' },
                      { key: 'cumOut',  label: 'Cum. Out',      color: '#A32D2D' },
                      { key: 'balance', label: 'Balance',       color: '#B06D00' },
                    ].map(f => (
                      <label key={f.key} className="flex items-center gap-1.5 cursor-pointer text-xs text-[#6B655C] hover:text-[#1C1A18]">
                        <input type="checkbox" checked={curveFilters[f.key]}
                          onChange={() => setCurveFilters(p => ({...p, [f.key]: !p[f.key]}))}
                          className="w-3 h-3 rounded"/>
                        <span className="w-3 h-0.5 inline-block rounded" style={{background: f.color}}/>
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={plotData} margin={{top:5,right:30,left:20,bottom:5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DA"/>
                    <XAxis dataKey="label" tick={{fontSize:10,fill:'#6B655C'}}/>
                    <YAxis yAxisId="l" tick={{fontSize:10,fill:'#6B655C'}} tickFormatter={v=>v>=1e6?`${(v/1e6).toFixed(0)}M`:v>=1e3?`${(v/1e3).toFixed(0)}K`:v}/>
                    <YAxis yAxisId="r" orientation="right" tick={{fontSize:10,fill:'#6B655C'}} tickFormatter={v=>v>=1e6?`${(v/1e6).toFixed(0)}M`:v>=1e3?`${(v/1e3).toFixed(0)}K`:v}/>
                    <Tooltip formatter={(v,n)=>[v.toLocaleString('en-US',{maximumFractionDigits:0}),n]}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    {curveFilters.cashIn  && <Bar  yAxisId="l" dataKey="Cash In"  fill="#1A7A4A" opacity={0.7} radius={[3,3,0,0]}/>}
                    {curveFilters.cashOut && <Bar  yAxisId="l" dataKey="Cash Out" fill="#A32D2D" opacity={0.7} radius={[3,3,0,0]}/>}
                    {curveFilters.cumIn   && <Line yAxisId="r" type="monotone" dataKey="Cum. In"  stroke="#1A7A4A" strokeWidth={2} dot={false}/>}
                    {curveFilters.cumOut  && <Line yAxisId="r" type="monotone" dataKey="Cum. Out" stroke="#A32D2D" strokeWidth={2} dot={false} strokeDasharray="5 3"/>}
                    {curveFilters.balance && <Line yAxisId="r" type="monotone" dataKey="Balance"  stroke="#B06D00" strokeWidth={2.5} dot={{fill:'#B06D00',r:3}}/>}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              )
            })()}

            {/* ── Export Report ── */}
            {(cashInEntries.length > 0 || cashOutSummary.length > 0) && (
              <div className="flex justify-end">
                <button onClick={generateCashFlowReport}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1C1A18] text-white text-sm rounded-lg hover:bg-[#2E2C29]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path d="M7 17H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2M9 21h6M12 17v4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Export Report
                </button>
              </div>
            )}

          </div>
        )
      })()}

      {/* ===================== CASH FLOW TAB ===================== */}
      {activeTab === 'cashflow' && (
        cashFlowRows.length === 0 ? (
          <div className="text-center py-16 bg-[#EDE7DA] rounded-xl border border-[#DDD9D0]">
            <div className="text-5xl mb-3">📊</div>
            <p className="text-[#6B655C]">لا يوجد بيانات كافية لعرض Cash Flow.</p>
          </div>
        ) : (
          <div className="bg-[#EDE7DA] rounded-xl border border-[#DDD9D0] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6B655C]">Month</th>
                  <th className="text-right px-4 py-3 font-medium text-green-600">Cash In</th>
                  <th className="text-right px-4 py-3 font-medium text-red-600">Cash Out</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6B655C]">Net</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6B655C]">Cumulative</th>
                  <th className="px-4 py-3 font-medium text-[#6B655C]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E4DC]">
                {cashFlowRows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#F5F1EA]">
                    <td className="px-4 py-3 font-medium text-[#2E2C29]">
                      {MONTHS[row.month - 1]} {row.year}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">
                      {row.inAmt > 0 ? fmt(row.inAmt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">
                      {row.outAmt > 0 ? fmt(row.outAmt) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${row.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmtSigned(row.net)}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${row.cumulative >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {fmtSigned(row.cumulative)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.net >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {row.net >= 0 ? '✅ فائض' : '🔴 عجز'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F5F1EA] border-t-2 border-[#DDD9D0]">
                <tr>
                  <td className="px-4 py-3 font-medium text-[#6B655C]">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {fmt(cashFlowRows.reduce((s, r) => s + r.inAmt, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {fmt(cashFlowRows.reduce((s, r) => s + r.outAmt, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold">
                    {fmtSigned(cashFlowRows.reduce((s, r) => s + r.net, 0))}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {/* ===================== CF PACKAGES TAB ===================== */}
      {activeTab === 'cf_packages' && (
        <div className="space-y-5">

          {/* Header actions */}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setCfShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1C1A18] rounded-lg hover:bg-[#2E2C29]">
              + New Payment
            </button>
            <div
              onDragOver={e => { e.preventDefault(); setCfDragOver(true) }}
              onDragLeave={() => setCfDragOver(false)}
              onDrop={e => { e.preventDefault(); setCfDragOver(false); handleCFDictUpload(e.dataTransfer.files[0]) }}
              onClick={() => document.getElementById('cf-dict-input').click()}
              className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-lg cursor-pointer transition-colors ${cfDragOver ? 'border-[#1A7A4A] bg-[#EDE7DA] text-[#1A7A4A]' : 'border-[#DDD9D0] text-[#6B655C] hover:bg-[#F5F1EA]'}`}>
              📂 Upload Dictionary
            </div>
            <input id="cf-dict-input" type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={e => handleCFDictUpload(e.target.files[0])} />

            <div className="flex items-center gap-1 border border-[#DDD9D0] rounded-lg overflow-hidden">
              {[
                { key: 'nopo',  label: '⚠ No PO' },
                { key: 'petty', label: '💰 Petty Cash' },
                { key: 'po',    label: '🏢 PO' },
              ].map((m, i) => (
                <div key={m.key} className={`flex items-center ${i > 0 ? 'border-l border-[#DDD9D0]' : ''}`}>
                  <button onClick={() => downloadBpTemplate(m.key)}
                    title="Download Template"
                    className="px-2 py-2 text-xs text-[#6B655C] hover:bg-[#F5F1EA]">⬇</button>
                  <button onClick={() => openBpUpload(m.key)}
                    className="px-3 py-2 text-xs font-medium text-[#6B655C] hover:bg-[#F5F1EA] whitespace-nowrap">
                    Upload {m.label}
                  </button>
                </div>
              ))}
            </div>

            <button onClick={() => setCfCollapsedPkgs(new Set([...new Set(cfEntries.map(e => e.cf_package || 'Unclassified')), ...cfPackages.map(p=>p.cf_package)]))}
              className="px-3 py-2 text-xs border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
              ▶ Collapse All
            </button>
            <button onClick={() => setCfCollapsedPkgs(new Set())}
              className="px-3 py-2 text-xs border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
              ▼ Expand All
            </button>

            <button onClick={() => setShowPaymentTypesMgr(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
              ⚙ Payment Types
            </button>

            <button onClick={() => setShowCategoriesMgr(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
              ⚙ Categories
            </button>

            {cfPackages.length > 0 && (
              <button onClick={() => setCfShowDict(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
                📋 View Dictionary ({cfPackages.length})
              </button>
            )}
            <span className="text-xs text-[#9A948C] ml-auto">{cfPackages.length} sub-codes loaded · {cfEntries.length} payments</span>
          </div>

          {/* Dictionary upload preview */}
          {cfUploadPreview && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-amber-200">
                <p className="text-sm font-semibold text-amber-800">Preview — {cfUploadPreview.length} sub-codes</p>
                <div className="flex gap-2">
                  <button onClick={() => setCfUploadPreview(null)} className="px-3 py-1.5 text-xs border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">Cancel</button>
                  <button onClick={confirmCFDictUpload} disabled={cfUploading}
                    className="px-4 py-1.5 text-xs font-medium text-white bg-[#1C1A18] rounded-lg hover:bg-[#2E2C29] disabled:opacity-50">
                    {cfUploading ? 'Importing...' : `Add / Update ${cfUploadPreview.length} rows`}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto max-h-48">
                <table className="w-full text-xs">
                  <thead className="bg-amber-100">
                    <tr>{['CF Package','Sub Code','Description'].map(h => <th key={h} className="text-left px-4 py-2 font-semibold text-amber-800">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {cfUploadPreview.slice(0, 20).map((r, i) => (
                      <tr key={i} className="hover:bg-amber-50">
                        <td className="px-4 py-2 text-[#1A7A4A] font-medium">{r.cf_package}</td>
                        <td className="px-4 py-2 font-mono text-[#2E2C29]">{r.sub_code}</td>
                        <td className="px-4 py-2 text-[#6B655C]">{r.sub_code_description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Total Cash Out KPI Cards */}
          {cfEntries.length > 0 && (() => {
            const { totalAll, totalDirect, totalIndirect } = cfKpiTotals
            const fmtK = n => 'SAR ' + Number(n).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
            return (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'TOTAL CASH OUT', val: totalAll,      color: '#1C1A18', bg: '#EDE7DA' },
                  { label: 'DIRECT',          val: totalDirect,   color: '#1A7A4A', bg: '#F0FAF5' },
                  { label: 'INDIRECT',        val: totalIndirect, color: '#B06D00', bg: '#FDF8F0' },
                ].map(k => (
                  <div key={k.label} className="rounded-xl border px-5 py-4" style={{ background: k.bg, borderColor: '#DDD9D0' }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#9A948C' }}>{k.label}</p>
                    <p className="text-lg font-bold font-mono" style={{ color: k.color }}>{fmtK(k.val)}</p>
                  </div>
                ))}
              </div>
            )
          })()}

          {/* CF Packages Filters */}
          {(cfEntries.length > 0 || cfPackages.length > 0) && (
            <div className="flex flex-wrap gap-3 items-center bg-[#F5F1EA] border border-[#DDD9D0] rounded-xl px-4 py-3">
              <select value={cfFilterPkg} onChange={e => setCfFilterPkg(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29] min-w-[160px]">
                <option value="all">All Packages</option>
                {[...new Set(cfPackages.map(p => p.cf_package).filter(Boolean))].sort().map(pkg => (
                  <option key={pkg} value={pkg}>{pkg}</option>
                ))}
              </select>
              <input value={cfFilterSubCode} onChange={e => setCfFilterSubCode(e.target.value)}
                placeholder="Sub Code..." className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29] w-36" />
              <select value={cfFilterType} onChange={e => setCfFilterType(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29]">
                <option value="all">All Types</option>
                <option value="direct">Direct</option>
                <option value="indirect">Indirect</option>
              </select>
              <select value={cfFilterCategory} onChange={e => setCfFilterCategory(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29]">
                <option value="all">All Categories</option>
                {getAllCategories().map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select value={cfFilterPaymentType} onChange={e => setCfFilterPaymentType(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29] min-w-[160px]">
                <option value="all">All Payment Types</option>
                {getAllPaymentTypes().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <input value={cfFilterSupplier} onChange={e => setCfFilterSupplier(e.target.value)}
                placeholder="Supplier..." className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29] w-36" />
              <input type="date" value={cfFilterDateFrom} onChange={e => setCfFilterDateFrom(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29]" />
              <span className="text-xs text-[#9A948C]">→</span>
              <input type="date" value={cfFilterDateTo} onChange={e => setCfFilterDateTo(e.target.value)}
                className="border border-[#DDD9D0] rounded-lg px-3 py-1.5 text-sm bg-white text-[#2E2C29]" />
              {(cfFilterPkg !== 'all' || cfFilterSubCode || cfFilterType !== 'all' || cfFilterCategory !== 'all' || cfFilterPaymentType !== 'all' || cfFilterSupplier || cfFilterDateFrom || cfFilterDateTo) && (
                <button onClick={() => { setCfFilterPkg('all'); setCfFilterSubCode(''); setCfFilterType('all'); setCfFilterCategory('all'); setCfFilterPaymentType('all'); setCfFilterSupplier(''); setCfFilterDateFrom(''); setCfFilterDateTo('') }}
                  className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:bg-red-50">
                  ✕ Clear
                </button>
              )}
            </div>
          )}

          {/* Payments table grouped by CF Package */}
          {cfEntries.length === 0 && cfPackages.length === 0 ? (
            <div className="text-center py-16 bg-[#EDE7DA] rounded-xl border border-[#DDD9D0]">
              <div className="text-5xl mb-3">📦</div>
              <p className="text-[#6B655C] font-medium">ابدأ برفع الـ Dictionary أولاً</p>
              <p className="text-xs text-[#9A948C] mt-1">ارفع ملف Excel بـ columns: Cash flow packages / Sub code / sub code Description</p>
            </div>
          ) : (
            <div className="bg-[#EDE7DA] rounded-2xl border border-[#DDD9D0] overflow-hidden">
              {cfSelected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 bg-[#1A7A4A] text-white">
                  <span className="text-sm font-semibold">{cfSelected.size} selected</span>
                  <button onClick={()=>setCfBulkPicker("category")} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold">Change Category</button>
                  <button onClick={()=>setCfBulkPicker("payment_type")} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold">Change Payment Type</button>
                  <button onClick={()=>setCfBulkDateEdit({ date: '', is_opening: null })} className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold">Change Date / Opening</button>
                  <button onClick={bulkDeleteCFEntries} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-xs font-semibold">🗑 Delete</button>
                  <button onClick={()=>setCfSelected(new Set())} className="ml-auto px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs">Clear</button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0]">
                    <tr>
                      <th className="px-3 py-3 w-8">
                        <input type="checkbox" onChange={e => {
                          const allIds = cfEntries.map(x => x.id)
                          setCfSelected(e.target.checked ? new Set(allIds) : new Set())
                        }} className="w-4 h-4 rounded cursor-pointer"/>
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">CF Package</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Payment Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Sub Code</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Cost Code</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-purple-700 uppercase">Resource Code</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-purple-700 uppercase">Resource Description</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">PO Number</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#1D4ED8] uppercase">IPC</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Supplier</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#1A7A4A] uppercase">Amount Paid (SAR)</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Opening</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E8E4DC]">
                    {(() => {
                      const grouped = cfGroupedEntries
                      return Object.entries(grouped).map(([pkg, entries]) => {
                        const pkgTotal = entries.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
                        const isCollapsed = cfCollapsedPkgs.has(pkg)
                        return [
                          <tr key={`pkg-${pkg}`} className="bg-[#1C1A18] cursor-pointer" onClick={() => {
                            setCfCollapsedPkgs(prev => {
                              const s = new Set(prev)
                              s.has(pkg) ? s.delete(pkg) : s.add(pkg)
                              return s
                            })
                          }}>
                            <td colSpan={7} className="px-4 py-2.5">
                              <span className="text-xs font-bold text-white uppercase tracking-wide">
                                {isCollapsed ? '▶' : '▼'} 📦 {pkg}
                              </span>
                            </td>
                            <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                              {(() => {
                                const pkgDict = cfPackages.find(p => p.cf_package === pkg)
                                const bType = entries[0]?.budget_type || pkgDict?.budget_type || 'direct'
                                return (
                                  <button
                                    onClick={async () => {
                                      const newType = bType === 'direct' ? 'indirect' : 'direct'
                                      await supabase.from('cf_package_dictionary').update({ budget_type: newType }).eq('project_id', projectId).eq('cf_package', pkg)
                                      await supabase.from('cf_payment_entries').update({ budget_type: newType }).eq('project_id', projectId).eq('cf_package', pkg)
                                      await fetchCFPackages()
                                      await fetchCFEntries()
                                    }}
                                    style={{ background: bType==='direct'?'#1A7A4A':'#B06D00', color:'white', border:'none', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700, cursor:'pointer' }}
                                    title="Click to toggle Direct / Indirect">
                                    {bType === 'direct' ? 'Direct' : 'Indirect'}
                                  </button>
                                )
                              })()}
                            </td>
                            <td colSpan={6}/>
                            <td className="px-4 py-2.5 text-right text-sm font-bold text-[#1A7A4A]">
                              {pkgTotal > 0 ? Number(pkgTotal).toLocaleString('en-US', {minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}
                            </td>
                            <td/>
                          </tr>,
                          ...(isCollapsed ? [] : entries.map((e, i) => (
                            <tr key={e.id || i} className="hover:bg-[#F5F1EA]">
                              <td className="px-3 py-3">
                                <input type="checkbox" checked={cfSelected.has(e.id)} onChange={ev => {
                                  const s = new Set(cfSelected)
                                  ev.target.checked ? s.add(e.id) : s.delete(e.id)
                                  setCfSelected(s)
                                }} className="w-4 h-4 rounded cursor-pointer"/>
                              </td>
                              <td className="px-4 py-3 text-xs text-[#9A948C] pl-8">{e.cf_package}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                                  style={{ background: (e.budget_type||'direct')==='direct'?'#E8F5EE':'#FFF3E0', color: (e.budget_type||'direct')==='direct'?'#1A7A4A':'#B06D00' }}>
                                  {(e.budget_type||'direct')==='direct'?'Direct':'Indirect'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button onClick={() => setCfPicker({ entry: e, field: 'category' })}
                                  className="px-2 py-0.5 rounded-full text-xs font-semibold cursor-pointer border"
                                  style={{ background: (e.category||'invoice')==='invoice'?'#EFF6FF':'#FFF7ED', color: (e.category||'invoice')==='invoice'?'#1D4ED8':'#C2410C', borderColor: (e.category||'invoice')==='invoice'?'#BFDBFE':'#FED7AA' }}>
                                  {categoryLabel(e.category||'invoice')} ▾
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <button onClick={() => setCfPicker({ entry: e, field: 'payment_type' })}
                                  className="px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer border border-[#DDD9D0] capitalize"
                                  style={{ background: 'rgba(107,101,92,.08)', color: '#6B655C' }}>
                                  {paymentTypeLabel(e.payment_type)} ▾
                                </button>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A] font-semibold">{e.sub_code}</td>
                              <td className="px-4 py-3 font-mono text-xs text-[#6B655C]">{allSubCodes.find(sc=>sc.code===e.sub_code)?.costCode || '—'}</td>
                              <td className="px-4 py-3 font-mono text-xs text-purple-700">{allSubCodes.find(sc=>sc.code===e.sub_code)?.resourceCode || '—'}</td>
                              <td className="px-4 py-3 text-xs text-purple-700">{allSubCodes.find(sc=>sc.code===e.sub_code)?.resourceCodeDescription || '—'}</td>
                              <td className="px-4 py-3 text-[#6B655C] text-xs max-w-xs truncate">{e.sub_code_desc}</td>
                              <td className="px-4 py-3 font-mono text-xs text-[#1C1A18] font-semibold">{e.purchase_orders?.po_number || '—'}</td>
                              <td className="px-4 py-3 font-mono text-xs text-[#1D4ED8] font-semibold">{e._ipc_number != null ? `IPC-${String(e._ipc_number).padStart(4,'0')}` : '—'}</td>
                              <td className="px-4 py-3 text-[#2E2C29] text-xs">{e.payee || e.purchase_orders?.vendor || '—'}</td>
                              <td className="px-4 py-3 text-xs text-[#6B655C]">{e.date || '—'}</td>
                              <td className="px-4 py-3 text-right font-bold text-[#1A7A4A]">
                                {Number(e.amount_inc_vat||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {e.is_opening && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">Opening</span>}
                                  <button onClick={() => openEditCFEntry(e)}
                                    className="text-[#9A948C] hover:text-[#1A7A4A] transition-colors" title="Edit">
                                    ✏
                                  </button>
                                  {(e.payee||'').startsWith('Petty Cash — ') ? (
                                    <button onClick={() => deletePCVoucherEntries(e.payee.replace('Petty Cash — ', ''))}
                                      className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-colors font-medium whitespace-nowrap"
                                      title="Delete all lines of this voucher">
                                      🗑 Voucher
                                    </button>
                                  ) : (
                                  <button onClick={() => deleteCFEntry(e.id)}
                                    className="text-[#9A948C] hover:text-red-500 transition-colors" title="Delete">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                                      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))),
                          !isCollapsed && entries.length === 0 && (
                            <tr key={`empty-${pkg}`} className="bg-[#FDFCFA]">
                              <td colSpan={9} className="px-8 py-2 text-xs text-[#9A948C] italic">
                                {cfPackages.filter(p => p.cf_package === pkg).map(p => (
                                  <span key={p.sub_code} className="mr-4 font-mono text-[#C4BFB8]">{p.sub_code} — {p.sub_code_description}</span>
                                ))}
                              </td>
                            </tr>
                          )
                        ]
                      })
                    })()}
                  </tbody>
                  <tfoot className="bg-[#1C1A18] border-t-2 border-[#2E2C29]">
                    <tr>
                      <td colSpan={9} className="px-4 py-3 text-sm font-bold text-white uppercase">Total Cash Out</td>
                      <td className="px-4 py-3 text-right text-base font-bold text-[#1A7A4A]">
                        {Number(cfEntries.reduce((s,e)=>s+(parseFloat(e.amount_inc_vat)||0),0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                      </td>
                      <td/>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================== CF DICTIONARY VIEWER MODAL ===================== */}
      {cfShowDict && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <div>
                <h2 className="text-base font-bold text-[#1C1A18]">📋 CF Package Dictionary</h2>
                <p className="text-xs text-[#9A948C] mt-0.5">{cfPackages.length} sub-codes · {[...new Set(cfPackages.map(p=>p.cf_package))].length} packages</p>
              </div>
              <button onClick={() => setCfShowDict(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-5 pt-4">
              {[{id:'view',label:'📋 View Dictionary'},{id:'add',label:'➕ Add Manual Package'}].map(t => (
                <button key={t.id} onClick={() => setDictTab(t.id)}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${dictTab===t.id?'bg-[#1C1A18] text-white':'text-[#6B655C] hover:bg-[#F5F1EA] border border-[#DDD9D0]'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* View Tab */}
            {dictTab === 'view' && (
              <div className="overflow-y-auto flex-1 p-5 space-y-3">
                <input type="text" placeholder="بحث بالـ sub code أو الوصف أو الباكدج..."
                  value={editDictForm._search || ''}
                  onChange={e => setEditDictForm(p => ({...p, _search: e.target.value}))}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white"/>
                {(() => {
                  const q = (editDictForm._search || '').toLowerCase()
                  const grouped = {}
                  cfPackages
                    .filter(p => !q ||
                      (p.sub_code||'').toLowerCase().includes(q) ||
                      (p.sub_code_description||'').toLowerCase().includes(q) ||
                      (p.cf_package||'').toLowerCase().includes(q))
                    .forEach(p => {
                      if (!grouped[p.cf_package]) grouped[p.cf_package] = []
                      grouped[p.cf_package].push(p)
                    })
                  return Object.entries(grouped).map(([pkg, items]) => (
                    <div key={pkg} className="bg-white rounded-xl border border-[#DDD9D0] overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-[#1C1A18]">
                        <span className="text-xs font-bold text-white uppercase tracking-wide">📦 {pkg}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">{items.length} sub-code{items.length !== 1 ? 's' : ''}</span>
                          <button onClick={() => setEditDictRow(`new__${pkg}`)}
                            className="text-xs px-2 py-0.5 rounded bg-white/10 text-white hover:bg-[#1A7A4A] transition-colors font-medium">
                            + Add Sub Code
                          </button>
                        </div>
                      </div>
                      <table className="w-full text-xs">
                        <thead className="bg-[#F5F1EA] border-b border-[#DDD9D0]">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold text-[#6B655C]">Sub Code</th>
                            <th className="text-left px-4 py-2 font-semibold text-[#6B655C]">Description</th>
                            <th className="text-left px-4 py-2 font-semibold text-[#6B655C]">Type</th>
                            <th className="w-16 px-2 py-2"/>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F0EDE8]">
                          {items.map(item => (
                            editDictRow === item.id ? (
                              /* ── Edit Row ── */
                              <tr key={item.id} className="bg-[#F5F1EA]">
                                <td className="px-2 py-2">
                                  <input value={editDictForm.sub_code}
                                    onChange={e => setEditDictForm(p => ({...p, sub_code: e.target.value}))}
                                    className="w-full border border-[#1A7A4A] rounded px-2 py-1 text-xs font-mono font-bold focus:outline-none"/>
                                </td>
                                <td className="px-2 py-2">
                                  <input value={editDictForm.sub_code_description}
                                    onChange={e => setEditDictForm(p => ({...p, sub_code_description: e.target.value}))}
                                    className="w-full border border-[#DDD9D0] rounded px-2 py-1 text-xs focus:outline-none"/>
                                </td>
                                <td className="px-2 py-2">
                                  <select value={editDictForm.budget_type}
                                    onChange={e => setEditDictForm(p => ({...p, budget_type: e.target.value}))}
                                    className="border border-[#DDD9D0] rounded px-1 py-1 text-xs focus:outline-none">
                                    <option value="direct">Direct</option>
                                    <option value="indirect">Indirect</option>
                                  </select>
                                </td>
                                <td className="px-2 py-2">
                                  <div className="flex gap-1">
                                    <button onClick={async () => {
                                      await supabase.from('cf_package_dictionary').update({
                                        sub_code: editDictForm.sub_code,
                                        sub_code_description: editDictForm.sub_code_description,
                                        budget_type: editDictForm.budget_type,
                                      }).eq('id', item.id)
                                      const { data } = await supabase.from('cf_package_dictionary').select('*').eq('project_id', projectId).order('cf_package')
                                      setCfPackages(data || [])
                                      setEditDictRow(null)
                                    }} className="text-xs px-2 py-0.5 bg-[#1A7A4A] text-white rounded hover:bg-[#158a42]">✓</button>
                                    <button onClick={() => setEditDictRow(null)}
                                      className="text-xs px-2 py-0.5 bg-[#EDE7DA] text-[#6B655C] rounded hover:bg-[#DDD9D0]">✕</button>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              /* ── Normal Row ── */
                              <tr key={item.sub_code} className="hover:bg-[#FDFCFA] group">
                                <td className="px-4 py-2 font-mono font-bold text-[#1A7A4A]">{item.sub_code}</td>
                                <td className="px-4 py-2 text-[#6B655C]">{item.sub_code_description}</td>
                                <td className="px-4 py-2">
                                  <span className="text-xs px-1.5 py-0.5 rounded-full"
                                    style={{background: item.budget_type==='direct'?'#E8F5EE':'#FFF3E0', color: item.budget_type==='direct'?'#1A7A4A':'#B06D00'}}>
                                    {item.budget_type==='direct'?'Direct':'Indirect'}
                                  </span>
                                </td>
                                <td className="px-2 py-2">
                                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => {
                                      setEditDictRow(item.id)
                                      setEditDictForm({ sub_code: item.sub_code, sub_code_description: item.sub_code_description || '', budget_type: item.budget_type || 'direct' })
                                    }} className="text-xs px-2 py-0.5 bg-[#EDE7DA] text-[#6B655C] rounded hover:bg-[#1A7A4A] hover:text-white">✏</button>
                                    <button onClick={async () => {
                                      if (!window.confirm('حذف هذا الـ sub code؟')) return
                                      await supabase.from('cf_package_dictionary').delete().eq('id', item.id)
                                      const { data } = await supabase.from('cf_package_dictionary').select('*').eq('project_id', projectId).order('cf_package')
                                      setCfPackages(data || [])
                                    }} className="text-xs px-2 py-0.5 bg-[#EDE7DA] text-[#A32D2D] rounded hover:bg-red-500 hover:text-white">🗑</button>
                                  </div>
                                </td>
                              </tr>
                            )
                          ))}
                          {/* ── New Sub Code Row ── */}
                          {editDictRow === `new__${pkg}` && (
                            <tr className="bg-[#E8F5EE]">
                              <td className="px-2 py-2">
                                <input autoFocus value={editDictForm.sub_code || ''}
                                  onChange={e => {
                                    const val = e.target.value
                                    const found = allSubCodes.find(sc => sc.code === val)
                                    setEditDictForm(p => ({
                                      ...p,
                                      sub_code: val,
                                      sub_code_description: found ? found.description : p.sub_code_description
                                    }))
                                  }}
                                  list="dict-new-subcodes"
                                  placeholder="Sub Code"
                                  className="w-full border border-[#1A7A4A] rounded px-2 py-1 text-xs font-mono font-bold focus:outline-none"/>
                                <datalist id="dict-new-subcodes">
                                  {allSubCodes.map(sc => (
                                    <option key={sc.code} value={sc.code} label={sc.description || sc.code}/>
                                  ))}
                                </datalist>
                              </td>
                              <td className="px-2 py-2">
                                <input value={editDictForm.sub_code_description || ''}
                                  onChange={e => setEditDictForm(p => ({...p, sub_code_description: e.target.value}))}
                                  placeholder="Description"
                                  className="w-full border border-[#DDD9D0] rounded px-2 py-1 text-xs focus:outline-none"/>
                              </td>
                              <td className="px-2 py-2">
                                <select value={editDictForm.budget_type || 'direct'}
                                  onChange={e => setEditDictForm(p => ({...p, budget_type: e.target.value}))}
                                  className="border border-[#DDD9D0] rounded px-1 py-1 text-xs focus:outline-none">
                                  <option value="direct">Direct</option>
                                  <option value="indirect">Indirect</option>
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <div className="flex gap-1">
                                  <button onClick={async () => {
                                    if (!editDictForm.sub_code?.trim()) return
                                    const duplicate = cfPackages.find(p => p.sub_code?.trim().toLowerCase() === editDictForm.sub_code.trim().toLowerCase())
                                    if (duplicate) {
                                      alert(`Sub code "${editDictForm.sub_code}" موجود بالفعل في package "${duplicate.cf_package}"`)
                                      return
                                    }
                                    await supabase.from('cf_package_dictionary').insert({
                                      project_id: projectId,
                                      cf_package: pkg,
                                      sub_code: editDictForm.sub_code.trim(),
                                      sub_code_description: editDictForm.sub_code_description || '',
                                      budget_type: editDictForm.budget_type || 'direct',
                                    })
                                    const { data } = await supabase.from('cf_package_dictionary').select('*').eq('project_id', projectId).order('cf_package')
                                    setCfPackages(data || [])
                                    setEditDictRow(null)
                                    setEditDictForm({ sub_code: '', sub_code_description: '', budget_type: 'direct' })
                                  }} className="text-xs px-2 py-0.5 bg-[#1A7A4A] text-white rounded hover:bg-[#158a42]">✓</button>
                                  <button onClick={() => { setEditDictRow(null); setEditDictForm({ sub_code: '', sub_code_description: '', budget_type: 'direct' }) }}
                                    className="text-xs px-2 py-0.5 bg-[#EDE7DA] text-[#6B655C] rounded hover:bg-[#DDD9D0]">✕</button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ))
                })()}
              </div>
            )}

            {/* Add Manual Tab */}
            {dictTab === 'add' && (
              <div className="flex-1 p-5">
                <div className="bg-white rounded-xl border border-[#DDD9D0] p-5 space-y-4">
                  <p className="text-xs text-[#9A948C]">أضف package وsub code يدوياً في الـ Dictionary</p>
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Package Name *</label>
                    <input value={manualPkg.cf_package} onChange={e => setManualPkg(p=>({...p, cf_package: e.target.value}))}
                      list="dict-packages"
                      placeholder="اكتب اسم package موجود أو جديد"
                      className="mt-1 w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white"/>
                    <datalist id="dict-packages">
                      {[...new Set(cfPackages.map(p => p.cf_package).filter(Boolean))].map(pkg => (
                        <option key={pkg} value={pkg}/>
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Sub Code *</label>
                    <input value={manualPkg.sub_code} onChange={e => {
                      const val = e.target.value
                      const found = allSubCodes.find(sc => sc.code === val)
                      setManualPkg(p => ({
                        ...p,
                        sub_code: val,
                        sub_code_description: found?.description || p.sub_code_description
                      }))
                    }}
                      list="manual-subcodes"
                      placeholder="مثال: CONC-01"
                      className="mt-1 w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white font-mono"/>
                    <datalist id="manual-subcodes">
                      {allSubCodes.map(sc => (
                        <option key={sc.code} value={sc.code}>{sc.code}{sc.description ? ` — ${sc.description}` : ''}</option>
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Description</label>
                    <input value={manualPkg.sub_code_description} onChange={e => setManualPkg(p=>({...p, sub_code_description: e.target.value}))}
                      placeholder="وصف الـ sub code"
                      className="mt-1 w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#6B655C] uppercase">Budget Type *</label>
                    <div className="flex gap-2 mt-1">
                      {['direct','indirect'].map(t => (
                        <button key={t} type="button"
                          onClick={() => setManualPkg(p => ({...p, budget_type: t}))}
                          className="flex-1 py-2 rounded-lg text-sm font-medium border transition-colors"
                          style={{
                            background: manualPkg.budget_type === t ? (t==='direct'?'#1A7A4A':'#B06D00') : 'white',
                            color: manualPkg.budget_type === t ? 'white' : '#6B655C',
                            borderColor: manualPkg.budget_type === t ? 'transparent' : '#DDD9D0',
                          }}>
                          {t.charAt(0).toUpperCase()+t.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={async () => {
                    if (!manualPkg.cf_package.trim() || !manualPkg.sub_code.trim()) return
                    const duplicate = cfPackages.find(p => p.sub_code?.trim().toLowerCase() === manualPkg.sub_code.trim().toLowerCase())
                    if (duplicate) {
                      alert(`Sub code "${manualPkg.sub_code}" موجود بالفعل في package "${duplicate.cf_package}"`)
                      return
                    }
                    const { error } = await supabase.from('cf_package_dictionary').insert({
                      project_id: projectId,
                      cf_package: manualPkg.cf_package.trim(),
                      sub_code: manualPkg.sub_code.trim(),
                      sub_code_description: manualPkg.sub_code_description.trim() || null,
                      budget_type: manualPkg.budget_type || 'direct',
                    })
                    if (error) return
                    const { data } = await supabase.from('cf_package_dictionary').select('*').eq('project_id', projectId).order('cf_package')
                    setCfPackages(data || [])
                    setManualPkg({ cf_package: manualPkg.cf_package, sub_code: '', sub_code_description: '', budget_type: 'direct' })
                  }}
                    className="w-full py-2.5 bg-[#1C1A18] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">
                    + Add to Dictionary
                  </button>
                </div>
              </div>
            )}

            <div className="p-5 border-t border-[#DDD9D0]">
              <button onClick={() => setCfShowDict(false)}
                className="w-full py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CF ENTRY EDIT MODAL ===================== */}
      {cfEditEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-[#1C1A18]">✏ Edit CF Payment</h2>
              <button onClick={() => setCfEditEntry(null)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-[#F5F1EA] rounded-lg px-4 py-3 border border-[#DDD9D0]">
                <p className="text-xs text-[#9A948C]">CF Package</p>
                <p className="text-sm font-bold text-[#1C1A18]">📦 {cfEditEntry.cf_package || '—'}</p>
                <p className="text-xs text-[#6B655C] mt-0.5 font-mono">{cfEditEntry.sub_code} — {cfEditEntry.sub_code_desc}</p>
                <p className="text-xs text-[#9A948C] mt-1">Payee: {cfEditEntry.payee || cfEditEntry.purchase_orders?.vendor || '—'}</p>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Date *</label>
                <input type="date" value={cfEditForm.date}
                  onChange={e => setCfEditForm(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-2">Opening Balance Status</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCfEditForm(p => ({ ...p, is_opening: true }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${cfEditForm.is_opening ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-[#DDD9D0] text-[#6B655C]'}`}>
                    Opening
                  </button>
                  <button type="button" onClick={() => setCfEditForm(p => ({ ...p, is_opening: false }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${!cfEditForm.is_opening ? 'border-[#1A7A4A] bg-[#E8F5EE] text-[#1A7A4A]' : 'border-[#DDD9D0] text-[#6B655C]'}`}>
                    Normal
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1A7A4A] mb-1">Amount Inc. VAT (SAR) *</label>
                <input type="number" value={cfEditForm.amount_inc_vat}
                  onChange={e => setCfEditForm(p => ({ ...p, amount_inc_vat: e.target.value }))}
                  className="w-full border border-[#1A7A4A]/40 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-[#1A7A4A] bg-white" />
              </div>
              {cfEditIpcOptions.length > 0 && (
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">IPC (اختياري) — لربط هذه الدفعة بمستخلص محدد</label>
                  <select value={cfEditForm.ipc_id||''} onChange={e => setCfEditForm(p => ({ ...p, ipc_id: e.target.value }))}
                    className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm">
                    <option value="">— بدون ربط بمستخلص محدد —</option>
                    {cfEditIpcOptions.map(ipc => (
                      <option key={ipc.id} value={ipc.id}>
                        IPC-{String(ipc.ipc_number).padStart(4,'0')} — Net: {Number(ipc.net_payable||0).toLocaleString('en-US',{maximumFractionDigits:0})} SAR ({ipc.approval_stage||'draft'})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                <input value={cfEditForm.notes}
                  onChange={e => setCfEditForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#DDD9D0]">
              <button onClick={() => setCfEditEntry(null)}
                className="flex-1 py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Cancel</button>
              <button onClick={saveEditCFEntry}
                className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">
                Update Payment
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ===================== PO LINES TAB ===================== */}
      {activeTab === 'po_lines' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <input value={poLineSearch} onChange={e => setPoLineSearch(e.target.value)}
                placeholder="ابحث بـ PO number أو sub code أو description..."
                className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] pl-8"/>
              <span className="absolute left-2.5 top-2.5 text-[#9A948C] text-xs">🔍</span>
            </div>
            <button onClick={fetchPOLines} className="px-3 py-2 text-sm border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">↻ Refresh</button>
            <span className="text-xs text-[#9A948C] ml-auto">{poLines.length} line items</span>
          </div>
          {poLinesLoading ? (
            <div className="text-center py-16 text-[#9A948C] text-sm">Loading PO lines...</div>
          ) : (
            <div className="bg-[#EDE7DA] rounded-2xl border border-[#DDD9D0] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#EDE7DA] border-b border-[#DDD9D0]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">PO Number</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">Supplier</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B655C] uppercase">PO Amount</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#1A7A4A] uppercase">Paid</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#9A948C] uppercase">Remaining</th>
                      <th className="px-4 py-3"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0EDE8]">
                    {poLines.filter(l => {
                      if (!poLineSearch.trim()) return true
                      const q = poLineSearch.toLowerCase()
                      return l.po_number?.toLowerCase().includes(q) || l.vendor?.toLowerCase().includes(q)
                    }).map(l => {
                      const poAmt = l.po_amount || 0
                      const paid  = l.paid || 0
                      const remaining = poAmt - paid
                      const pct = poAmt > 0 ? Math.min(100, (paid / poAmt) * 100) : 0
                      return (
                        <tr key={l.id} className={`hover:bg-[#F5F1EA] transition-colors ${paid >= poAmt && poAmt > 0 ? 'bg-green-50/30' : ''}`}>
                          <td className="px-4 py-3 font-mono text-xs font-bold text-[#1C1A18]">{l.po_number}</td>
                          <td className="px-4 py-3 text-xs text-[#6B655C]">{l.vendor}</td>
                          <td className="px-4 py-3 text-xs text-right font-mono text-[#2E2C29]">
                            {Number(poAmt).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-xs font-mono font-bold ${paid > 0 ? 'text-[#1A7A4A]' : 'text-[#9A948C]'}`}>
                              {Number(paid).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-xs font-mono font-semibold ${remaining <= 0 && poAmt > 0 ? 'text-[#1A7A4A]' : remaining < poAmt * 0.2 ? 'text-amber-600' : 'text-[#9A948C]'}`}>
                              {remaining <= 0 && poAmt > 0 ? '✓ Full' : Number(remaining).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                            {poAmt > 0 && <div className="w-16 h-1 bg-[#DDD9D0] rounded-full mt-1 ml-auto overflow-hidden"><div className="h-full rounded-full bg-[#1A7A4A]" style={{ width: `${pct}%` }}/></div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {paid >= poAmt && poAmt > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1A7A4A]/10 text-[#1A7A4A] font-medium">Paid</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="bg-[#1C1A18] border-t-2 border-[#2E2C29]">
                    <tr>
                      <td colSpan={2} className="px-4 py-3 text-xs font-bold text-slate-400 uppercase">Total</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-white font-mono">{Number(poLines.reduce((s,l)=>s+(l.po_amount||0),0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-[#2FA866] font-mono">{Number(poLines.reduce((s,l)=>s+(l.paid||0),0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-slate-400 font-mono">{Number(poLines.reduce((s,l)=>s+(l.po_amount||0)-(l.paid||0),0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td/>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===================== CF DICTIONARY VIEWER MODAL ===================== */}
      {/* ===================== CF ENTRY EDIT MODAL ===================== */}
      {/* ── Category / Payment Type Picker ── */}
      {cfPicker && (() => {
        const isCat = cfPicker.field === 'category'
        const options = isCat
          ? getAllCategories().map(c => ({ value: c.value, label: c.label, color: '#1D4ED8', bg: '#EFF6FF' }))
          : getAllPaymentTypes().map(t => ({ value: t.value, label: t.label, color: '#2E2C29', bg: '#F9FAFB' }))
        const current = isCat ? (cfPicker.entry.category||'invoice') : (cfPicker.entry.payment_type||'subcontractor_supplier')
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCfPicker(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-72 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-[#E5E7EB]">
                <p className="font-bold text-[#0F1017] text-sm">{isCat ? 'Select Category' : 'Select Payment Type'}</p>
                <p className="text-xs text-[#6B7280] mt-0.5 truncate">{cfPicker.entry.payee || cfPicker.entry.cf_package || '—'}</p>
              </div>
              <div className="p-3 flex flex-col gap-2">
                {options.map(opt => (
                  <button key={opt.value} onClick={async () => {
                    await supabase.from('cf_payment_entries').update({ [cfPicker.field]: opt.value }).eq('id', cfPicker.entry.id)
                    setCfPicker(null)
                    await fetchCFEntries()
                  }} className="flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left"
                    style={{ borderColor: current===opt.value ? opt.color : '#E5E7EB', background: current===opt.value ? opt.bg : '#fff', color: current===opt.value ? opt.color : '#2E2C29' }}>
                    <span className="font-semibold text-sm">{opt.label}</span>
                    {current===opt.value && <span className="text-lg">✓</span>}
                  </button>
                ))}
              </div>
              <div className="px-5 pb-4">
                <button onClick={() => setCfPicker(null)} className="w-full py-2 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280] hover:bg-[#F9FAFB]">Cancel</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ── Bulk Category / Payment Type Picker ── */}
      {cfBulkPicker && (() => {
        const isCat = cfBulkPicker === 'category'
        const options = isCat
          ? getAllCategories().map(c => ({ value: c.value, label: c.label, color: '#1D4ED8', bg: '#EFF6FF' }))
          : getAllPaymentTypes().map(t => ({ value: t.value, label: t.label, color: '#2E2C29', bg: '#F9FAFB' }))
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCfBulkPicker(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-[#E5E7EB]">
                <p className="font-bold text-[#0F1017] text-sm">{isCat ? 'Set Category' : 'Set Payment Type'}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">{cfSelected.size} entries selected</p>
              </div>
              <div className="p-3 flex flex-col gap-2">
                {options.map(opt => (
                  <button key={opt.value} onClick={async () => {
                    const ids = [...cfSelected]
                    const BATCH = 100
                    for (let i = 0; i < ids.length; i += BATCH) {
                      const chunk = ids.slice(i, i + BATCH)
                      await supabase.from('cf_payment_entries').update({ [cfBulkPicker]: opt.value }).in('id', chunk)
                    }
                    setCfBulkPicker(null)
                    setCfSelected(new Set())
                    await fetchCFEntries()
                  }} className="flex items-center px-4 py-3 rounded-xl border-2 border-[#E5E7EB] hover:border-current transition-all text-left gap-3"
                    style={{ color: opt.color }}>
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: opt.color }}/>
                    <span className="font-semibold text-sm">{opt.label}</span>
                  </button>
                ))}
              </div>
              <div className="px-5 pb-4">
                <button onClick={() => setCfBulkPicker(null)} className="w-full py-2 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280] hover:bg-[#F9FAFB]">Cancel</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* ===================== BULK DATE / OPENING EDIT MODAL ===================== */}
      {cfBulkDateEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCfBulkDateEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-96 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#E5E7EB]">
              <p className="font-bold text-[#0F1017] text-sm">Change Date / Opening</p>
              <p className="text-xs text-[#6B7280] mt-0.5">{cfSelected.size} entries selected — leave a field empty to keep it unchanged</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">New Date</label>
                <input type="date" value={cfBulkDateEdit.date} onChange={e => setCfBulkDateEdit(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-2">Opening Balance Status</label>
                <div className="flex gap-2">
                  <button onClick={() => setCfBulkDateEdit(p => ({ ...p, is_opening: true }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${cfBulkDateEdit.is_opening === true ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-[#E5E7EB] text-[#6B7280]'}`}>
                    Opening
                  </button>
                  <button onClick={() => setCfBulkDateEdit(p => ({ ...p, is_opening: false }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${cfBulkDateEdit.is_opening === false ? 'border-[#1A7A4A] bg-[#E8F5EE] text-[#1A7A4A]' : 'border-[#E5E7EB] text-[#6B7280]'}`}>
                    Normal
                  </button>
                  <button onClick={() => setCfBulkDateEdit(p => ({ ...p, is_opening: null }))}
                    className={`px-3 py-2 rounded-lg text-xs border-2 ${cfBulkDateEdit.is_opening === null ? 'border-[#9A948C] bg-[#F5F1EA] text-[#6B655C]' : 'border-[#E5E7EB] text-[#9A948C]'}`}>
                    No change
                  </button>
                </div>
              </div>
            </div>
            <div className="p-5 pt-0 flex gap-3">
              <button onClick={() => setCfBulkDateEdit(null)} className="flex-1 py-2 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280] hover:bg-[#F9FAFB]">Cancel</button>
              <button onClick={async () => {
                  const patch = {}
                  if (cfBulkDateEdit.date) patch.date = cfBulkDateEdit.date
                  if (cfBulkDateEdit.is_opening !== null) patch.is_opening = cfBulkDateEdit.is_opening
                  if (Object.keys(patch).length === 0) { setCfBulkDateEdit(null); return }
                  const ids = [...cfSelected]
                  const BATCH = 100
                  for (let i = 0; i < ids.length; i += BATCH) {
                    const chunk = ids.slice(i, i + BATCH)
                    await supabase.from('cf_payment_entries').update(patch).in('id', chunk)
                  }
                  setCfBulkDateEdit(null)
                  setCfSelected(new Set())
                  await fetchCFEntries()
                }}
                className="flex-1 py-2 bg-[#1C1A18] text-white rounded-xl text-sm font-medium hover:bg-[#2E2C29]">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== BULK PAYMENT UPLOAD MODAL ===================== */}
      {bpMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-[#1C1A18]">
                {bpMode === 'nopo' ? '⚠ Bulk Upload — No PO' : bpMode === 'petty' ? '💰 Bulk Upload — Petty Cash' : '🏢 Bulk Upload — PO'}
              </h2>
              <button onClick={() => { setBpMode(null); setBpRows([]); setBpUnmatched([]); setBpError('') }}
                className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              {bpRows.length === 0 && (
                <div className="border-2 border-dashed border-[#DDD9D0] rounded-xl p-8 text-center">
                  <p className="text-sm text-[#6B655C] mb-3">ارفع ملف Excel بنفس تنسيق الـ Template</p>
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => downloadBpTemplate(bpMode)}
                      className="px-4 py-2 text-xs border border-[#DDD9D0] rounded-lg text-[#6B655C] hover:bg-[#F5F1EA]">
                      ⬇ Download Template
                    </button>
                    <label className="px-4 py-2 text-xs font-medium text-white bg-[#1C1A18] rounded-lg hover:bg-[#2E2C29] cursor-pointer">
                      📂 Choose File
                      <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                        onChange={e => handleBpFile(e.target.files[0])} />
                    </label>
                  </div>
                </div>
              )}

              {bpError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-xs text-red-700">{bpError}</div>
              )}

              {bpUnmatched.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
                  <p className="px-4 py-2 text-xs font-semibold text-amber-800 border-b border-amber-200">
                    ⚠ صفوف محتاجة مطابقة يدوية ({bpUnmatched.length}) — لازم تتحل قبل الرفع
                  </p>
                  <div className="max-h-56 overflow-y-auto divide-y divide-amber-100">
                    {bpUnmatched.map(row => (
                      <div key={row._row} className="flex items-center gap-3 px-4 py-2">
                        <span className="text-xs text-[#6B655C] flex-1">
                          Row {row._row}: {bpMode === 'po' ? `PO "${row.po_number}" / Line "${row.po_line_desc}"` : bpMode === 'petty' ? `Voucher "${row.voucher_no}"` : ''}
                          {row.sub_code && ` / Sub Code "${row.sub_code}"`}
                        </span>
                        {row._reason?.includes('po') && (
                          <select className="border border-amber-300 rounded-lg px-2 py-1 text-xs bg-white"
                            onChange={e => resolveBpUnmatchedRow(row, e.target.value)} defaultValue="">
                            <option value="" disabled>اختر PO</option>
                            {availablePOs.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.vendor}</option>)}
                          </select>
                        )}
                        {row._reason === 'line' && row._matched_po_id && (
                          <select className="border border-amber-300 rounded-lg px-2 py-1 text-xs bg-white max-w-[280px]"
                            onChange={e => resolveBpUnmatchedRow(row, e.target.value)} defaultValue="">
                            <option value="" disabled>اختر بند الـ PO</option>
                            {(poLinesMap[row._matched_po_id] || []).map(l => (
                              <option key={l.id} value={l.id}>{l.ref_code ? `${l.ref_code} — ` : ''}{l.description?.slice(0,50)} ({Number(l.amount_inc_vat||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})})</option>
                            ))}
                          </select>
                        )}
                        {row._reason?.includes('voucher') && (
                          <select className="border border-amber-300 rounded-lg px-2 py-1 text-xs bg-white"
                            onChange={e => resolveBpUnmatchedRow(row, e.target.value)} defaultValue="">
                            <option value="" disabled>اختر Voucher</option>
                            {pcVouchers.map(v => <option key={v.voucher_no} value={v.voucher_no}>{v.voucher_no}</option>)}
                          </select>
                        )}
                        {row._reason?.includes('subcode') && (
                          <select className="border border-amber-300 rounded-lg px-2 py-1 text-xs bg-white"
                            onChange={e => resolveBpUnmatchedRow(row, e.target.value)} defaultValue="">
                            <option value="" disabled>اختر Sub Code</option>
                            {allSubCodes.map(sc => <option key={sc.code} value={sc.code}>{sc.code} — {sc.description}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {bpRows.length > 0 && (
                <div className="border border-[#DDD9D0] rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2 bg-[#F5F1EA] border-b border-[#DDD9D0]">
                    <span className="text-xs font-semibold text-[#6B655C]">Preview — {bpRows.length} rows</span>
                    <span className="text-xs font-bold text-[#1C1A18]">
                      SAR {bpRows.reduce((s,r)=>s+r.amount_inc_vat,0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F5F1EA] sticky top-0">
                        <tr>
                          {(bpMode === 'nopo' ? ['Date','Payee','Sub Code','Amount','Opening','Category','Payment Type','Notes']
                            : bpMode === 'petty' ? ['Date','Voucher','Sub Code','Amount','Opening','Category','Payment Type']
                            : ['Date','PO','Sub Code (auto)','Amount','Opening','Category','Payment Type','Notes']).map(h => (
                            <th key={h} className="text-left px-3 py-2 font-semibold text-[#6B655C]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0EDE8]">
                        {bpRows.map((r,i) => (
                          <tr key={i} className={!bpUnmatched.some(u=>u._row===r._row) ? '' : 'bg-amber-50'}>
                            <td className="px-3 py-1.5">{r.date}</td>
                            <td className="px-3 py-1.5 font-mono">{bpMode==='nopo' ? r.payee : bpMode==='petty' ? (r._matched_voucher || r.voucher_no) : (availablePOs.find(p=>p.id===r._matched_po_id)?.po_number || r.po_number)}</td>
                            <td className="px-3 py-1.5 font-mono">{r._matched_sub_code || (bpMode==='po' ? '—' : r.sub_code) || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-bold">{r.amount_inc_vat.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                            <td className="px-3 py-1.5">{r.is_opening ? <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">Opening</span> : '—'}</td>
                            <td className="px-3 py-1.5 text-[#6B655C]">{categoryLabel(r.category)}</td>
                            <td className="px-3 py-1.5 text-[#6B655C]">{paymentTypeLabel(r.payment_type)}</td>
                            {bpMode !== 'petty' && <td className="px-3 py-1.5 text-[#6B655C]">{r.notes}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            {bpRows.length > 0 && (
              <div className="flex gap-3 p-5 border-t border-[#DDD9D0]">
                <button onClick={() => { setBpRows([]); setBpUnmatched([]); setBpError('') }}
                  className="flex-1 py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Cancel</button>
                <button onClick={confirmBpImport} disabled={bpUploading || bpUnmatched.length > 0}
                  className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg hover:bg-[#2E2C29] text-sm font-medium disabled:opacity-50">
                  {bpUploading ? 'جاري الرفع...' : `Import ${bpRows.length} rows`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== BULK UPLOAD — CONFIRM WARNINGS MODAL ===================== */}
      {bpConfirmData && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-red-700">⚠ Confirm Before Import</h2>
              <button onClick={() => setBpConfirmData(null)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {bpConfirmData.dupes.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-2">
                    {bpConfirmData.dupes.length} rows look like duplicates (same date, amount, and reference already exist):
                  </p>
                  <div className="border border-amber-200 rounded-xl overflow-hidden max-h-40 overflow-y-auto">
                    {bpConfirmData.dupes.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs bg-amber-50 border-b border-amber-100 last:border-0">
                        <span>Row {r._row} — {r.date}</span>
                        <span className="font-bold">{r.amount_inc_vat.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {bpConfirmData.warnings.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-red-700 mb-2">
                    {bpConfirmData.warnings.length} items exceed the allowed ceiling:
                  </p>
                  <div className="border border-red-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-red-700">Item</th>
                          <th className="text-right px-3 py-2 font-semibold text-red-700">Over By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {bpConfirmData.warnings.map((w, i) => (
                          <tr key={i} className="bg-white">
                            <td className="px-3 py-2 text-[#1C1A18]">{w.label}</td>
                            <td className="px-3 py-2 text-right font-bold text-red-700">+{w.overBy.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-red-100 sticky bottom-0">
                          <td className="px-3 py-2 font-bold text-red-800">Total Over</td>
                          <td className="px-3 py-2 text-right font-bold text-red-800">
                            +{bpConfirmData.warnings.reduce((s,w)=>s+w.overBy,0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-5 border-t border-[#DDD9D0]">
              <button onClick={() => setBpConfirmData(null)}
                className="flex-1 py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Cancel</button>
              <button onClick={() => { const rows = bpConfirmData.validRows; setBpConfirmData(null); runBpImport(rows) }}
                disabled={bpUploading}
                className="flex-1 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm font-medium disabled:opacity-50">
                {bpUploading ? 'Importing...' : 'Import Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== PAYMENT TYPES MANAGER MODAL ===================== */}
      {showPaymentTypesMgr && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-[#1C1A18]">⚙ Manage Payment Types</h2>
              <button onClick={() => setShowPaymentTypesMgr(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <p className="text-xs font-semibold text-[#9A948C] uppercase mb-2">Built-in (always available)</p>
                <div className="space-y-1.5">
                  {['subcontractor_supplier','payroll','others'].map(v => (
                    <div key={v} className="px-3 py-2 rounded-lg bg-[#F5F1EA] text-sm text-[#6B655C]">
                      {paymentTypeLabel(v)}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#9A948C] uppercase mb-2">Custom Types</p>
                {customPaymentTypes.length === 0 && (
                  <p className="text-xs text-[#9A948C] italic">No custom payment types added yet.</p>
                )}
                <div className="space-y-1.5">
                  {customPaymentTypes.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F5F1EA]">
                      <span className="text-sm text-[#1C1A18]">{t.label}</span>
                      <button onClick={() => deletePaymentType(t.id)} className="text-[#9A948C] hover:text-red-500">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[#DDD9D0] flex gap-2">
              <input value={newPaymentTypeLabel} onChange={e => setNewPaymentTypeLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addPaymentType() }}
                placeholder="New payment type name..."
                className="flex-1 border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none" />
              <button onClick={addPaymentType}
                className="px-4 py-2 bg-[#1C1A18] text-white rounded-lg hover:bg-[#2E2C29] text-sm font-medium">
                + Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CATEGORIES MANAGER MODAL ===================== */}
      {showCategoriesMgr && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-[#1C1A18]">⚙ Manage Categories</h2>
              <button onClick={() => setShowCategoriesMgr(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              <div>
                <p className="text-xs font-semibold text-[#9A948C] uppercase mb-2">Built-in (always available)</p>
                <div className="space-y-1.5">
                  {['invoice','advanced_payment'].map(v => (
                    <div key={v} className="px-3 py-2 rounded-lg bg-[#F5F1EA] text-sm text-[#6B655C]">
                      {categoryLabel(v)}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#9A948C] uppercase mb-2">Custom Categories</p>
                {customCategories.length === 0 && (
                  <p className="text-xs text-[#9A948C] italic">No custom categories added yet.</p>
                )}
                <div className="space-y-1.5">
                  {customCategories.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F5F1EA]">
                      <span className="text-sm text-[#1C1A18]">{t.label}</span>
                      <button onClick={() => deleteCategory(t.id)} className="text-[#9A948C] hover:text-red-500">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[#DDD9D0] flex gap-2">
              <input value={newCategoryLabel} onChange={e => setNewCategoryLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCategory() }}
                placeholder="New category name..."
                className="flex-1 border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none" />
              <button onClick={addCategory}
                className="px-4 py-2 bg-[#1C1A18] text-white rounded-lg hover:bg-[#2E2C29] text-sm font-medium">
                + Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CF PACKAGES MODAL ===================== */}
      {cfShowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h2 className="text-base font-bold text-[#1C1A18]">📦 New CF Payment</h2>
              <button onClick={() => { setCfShowModal(false); setCfPoLines([]); setCfPoSearch(''); setCfLineAmounts({}); setCfScAmounts({}); setCfExpandedSc({}); setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({}); setPcVouchers([]); setPcSearch('') }} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Date *</label>
                  <input type="date" value={cfForm.date} onChange={e => setCfForm(p => ({ ...p, date: e.target.value }))}
                    className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
                </div>
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Opening Balance Status</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setCfForm(p => ({ ...p, is_opening: true }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border-2 ${cfForm.is_opening ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-[#DDD9D0] text-[#6B655C]'}`}>
                      Opening
                    </button>
                    <button type="button" onClick={() => setCfForm(p => ({ ...p, is_opening: false }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border-2 ${!cfForm.is_opening ? 'border-[#1A7A4A] bg-[#E8F5EE] text-[#1A7A4A]' : 'border-[#DDD9D0] text-[#6B655C]'}`}>
                      Normal
                    </button>
                  </div>
                </div>
              </div>

              {/* Category + Payment Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Category</label>
                  <select value={cfForm.category||'invoice'} onChange={e => setCfForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] bg-white">
                    {getAllCategories().map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Payment Type</label>
                  <select value={cfForm.payment_type||'subcontractor_supplier'} onChange={e => setCfForm(p => ({ ...p, payment_type: e.target.value }))}
                    className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] bg-white">
                    {getAllPaymentTypes().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Payment Mode Selector */}
              <div className="flex gap-2">
                {[
                  { key: 'po', label: '🏢 Supplier / PO', active: !cfForm.no_po && !cfForm.petty_cash },
                  { key: 'nopo', label: '⚠ No PO', active: cfForm.no_po && !cfForm.petty_cash },
                  { key: 'petty', label: '💰 Petty Cash', active: cfForm.petty_cash },
                ].map(m => (
                  <button key={m.key} type="button"
                    onClick={() => {
                      if (m.key === 'petty') {
                        setCfForm(p => ({ ...p, no_po: false, petty_cash: true, po_id: '', sub_code: '', cf_package: '', sub_code_desc: '' }))
                        fetchPCVouchers()
                      } else if (m.key === 'nopo') {
                        setCfForm(p => ({ ...p, no_po: true, petty_cash: false, po_id: '', sub_code: '', cf_package: '', sub_code_desc: '' }))
                        setPcVouchers([]); setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({})
                      } else {
                        setCfForm(p => ({ ...p, no_po: false, petty_cash: false, po_id: '', sub_code: '', cf_package: '', sub_code_desc: '' }))
                        setPcVouchers([]); setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({})
                      }
                    }}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-colors ${
                      m.active
                        ? 'bg-[#1C1A18] text-white border-[#1C1A18]'
                        : 'bg-white text-[#6B655C] border-[#DDD9D0] hover:border-[#1C1A18]'
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>

              {/* ── Petty Cash Mode ── */}
              {cfForm.petty_cash && (
                <div className="space-y-3">
                  {pcLoading ? (
                    <p className="text-xs text-[#9A948C] text-center py-4">جاري التحميل...</p>
                  ) : !selectedPcVoucher ? (
                    /* Step 1: Voucher list */
                    <div className="border border-[#DDD9D0] rounded-xl overflow-hidden max-h-64 overflow-y-auto">
                      {pcVouchers.length === 0 && (
                        <p className="text-xs text-[#9A948C] text-center py-6">لا توجد vouchers لهذا المشروع</p>
                      )}
                      {pcVouchers.map(v => (
                        <button key={v.voucher_no} type="button"
                          onClick={() => { setSelectedPcVoucher(v.voucher_no); fetchPCLines(v.voucher_no) }}
                          className="w-full flex items-center justify-between px-4 py-3 border-b border-[#F0EDE8] last:border-0 hover:bg-[#F5F1EA] text-left transition-colors">
                          <div>
                            <span className="font-mono text-sm font-bold text-[#1A7A4A]">{v.voucher_no}</span>
                            <span className="text-xs text-[#9A948C] ml-3">{v.date || ''}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#1C1A18] font-mono">
                              {v.total_inc.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </p>
                            <p className="text-xs text-[#9A948C]">inc VAT</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* Step 2: Sub code lines of selected voucher */
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({}) }}
                          className="text-xs text-[#9A948C] hover:text-[#1C1A18]">← Back</button>
                        <span className="text-xs font-bold text-[#1A7A4A] font-mono">{selectedPcVoucher}</span>
                      </div>
                      <div className="border border-[#DDD9D0] rounded-xl overflow-hidden">
                        {pcVoucherLines.map(l => {
                          const paidBefore = getPaidForPCVoucher(selectedPcVoucher, l.sub_code === '—' ? null : l.sub_code)
                          const c = checkCeiling(paidBefore, pcLineAmounts[l.sub_code], l.inc)
                          return (
                          <div key={l.sub_code} className="border-b border-[#F0EDE8] last:border-0">
                            <div className="flex items-center gap-3 px-4 py-3">
                              <div className="flex-1 min-w-0">
                                <span className="font-mono text-sm font-bold text-[#1A7A4A]">{l.sub_code}</span>
                                <p className="text-xs text-[#6B655C] truncate mt-0.5">{l.desc}</p>
                              </div>
                              <button type="button"
                                onClick={() => setPcLineAmounts(p => ({...p, [l.sub_code]: l.inc.toFixed(2)}))}
                                className="text-xs px-2 py-0.5 rounded-full bg-[#1A7A4A]/10 text-[#1A7A4A] hover:bg-[#1A7A4A] hover:text-white transition-colors font-medium whitespace-nowrap">
                                {l.inc.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                              </button>
                              <input type="number" placeholder="0.00"
                                value={pcLineAmounts[l.sub_code] || ''}
                                onChange={e => setPcLineAmounts(p => ({...p, [l.sub_code]: e.target.value}))}
                                className="w-36 border border-[#DDD9D0] rounded-lg px-2 py-1.5 text-xs text-right font-bold focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none bg-white"/>
                            </div>
                            {c && (
                              <div className={`mx-4 mb-2 -mt-1 px-3 py-1.5 rounded-lg text-xs font-medium ${c.over ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-[#F5F1EA] text-[#9A948C]'}`}>
                                {c.over
                                  ? `تخطي عن قيمة الـ voucher بمقدار ${c.overBy.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR`
                                  : `مدفوع سابقاً: ${c.paidBefore.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} — متبقي: ${(c.ceiling - c.paidBefore).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                              </div>
                            )}
                          </div>
                          )
                        })}
                        {Object.values(pcLineAmounts).some(v => parseFloat(v) > 0) && (
                          <div className="flex items-center justify-between px-4 py-2.5 bg-[#1C1A18]">
                            <span className="text-xs font-bold text-slate-400 uppercase">Total</span>
                            <span className="text-sm font-bold text-white">
                              SAR {Object.values(pcLineAmounts).reduce((s,v)=>s+(parseFloat(v)||0),0)
                                .toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-[#6B655C]">CF Package (optional)</label>
                    <input value={cfForm.cf_package} onChange={e => setCfForm(p => ({...p, cf_package: e.target.value}))}
                      list="cf-packages-list-pc" placeholder="اختر أو اكتب"
                      className="mt-1 w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-white"/>
                    <datalist id="cf-packages-list-pc">
                      {[...new Set(cfPackages.map(p => p.cf_package).filter(Boolean))].map(pkg => (
                        <option key={pkg} value={pkg}/>
                      ))}
                    </datalist>
                  </div>
                </div>
              )}

              {/* ── PO / No-PO Mode ── */}
              {!cfForm.petty_cash && (
              <div className="relative">
                {cfForm.no_po ? (
                  <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-xs text-amber-700 font-medium mb-1">
                      <span>⚠</span>
                      <span>دفع بدون PO — سيتم تسجيله على مستوى الباكدج</span>
                    </div>
                    <div>
                      <label className="text-xs text-[#6B655C]">Payee / Supplier Name</label>
                      <input value={cfForm.payee} onChange={e => setCfForm(p => ({...p, payee: e.target.value}))}
                        placeholder="اسم المورد أو الجهة"
                        className="mt-1 w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B06D00] bg-white"/>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-[#6B655C]">Package</label>
                        <input value={cfForm.cf_package} onChange={e => setCfForm(p => ({...p, cf_package: e.target.value}))}
                          list="cf-packages-list"
                          placeholder="اختر أو اكتب"
                          className="mt-1 w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#B06D00] bg-white"/>
                        <datalist id="cf-packages-list">
                          {[...new Set(cfPackages.map(p => p.cf_package).filter(Boolean))].map(pkg => (
                            <option key={pkg} value={pkg}/>
                          ))}
                        </datalist>
                      </div>
                      <div>
                        <label className="text-xs text-[#6B655C]">Sub Code (optional)</label>
                        <select value={cfForm.sub_code} onChange={e => {
                            const val = e.target.value
                            const match = allSubCodes.find(sc => sc.code === val)
                            setCfForm(p => ({...p, sub_code: val, sub_code_desc: match?.description || ''}))
                          }}
                          className="mt-1 w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none bg-white">
                          <option value="">— Select Sub Code —</option>
                          {allSubCodes.map(sc => (
                            <option key={sc.code} value={sc.code}>{sc.code}{sc.description ? ` — ${sc.description}` : ''}</option>
                          ))}
                        </select>
                        {/* Cost Code + Resource Code are derived automatically from the selected Sub Code */}
                        {cfForm.sub_code && (() => {
                          const sel = allSubCodes.find(sc => sc.code === cfForm.sub_code)
                          if (!sel) return null
                          return (
                            <div className="mt-1.5 space-y-0.5">
                              {sel.costCode && (
                                <p className="text-xs text-[#6B655C]">
                                  Cost Code: <span className="font-mono font-semibold text-[#1A7A4A]">{sel.costCode}</span>{sel.costCodeDescription ? ` — ${sel.costCodeDescription}` : ''}
                                </p>
                              )}
                              {sel.resourceCode && (
                                <p className="text-xs text-purple-700">
                                  Resource Code: <span className="font-mono font-semibold">{sel.resourceCode}</span>{sel.resourceCodeDescription ? ` — ${sel.resourceCodeDescription}` : ''}
                                </p>
                              )}
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#6B655C]">ملاحظة — Still no PO?</label>
                      <textarea value={cfForm.notes} onChange={e => setCfForm(p => ({...p, notes: e.target.value}))}
                        rows={2} placeholder="مثال: PO under preparation — payment advance against contract"
                        className="mt-1 w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none resize-none bg-white"/>
                    </div>
                  </div>
                ) : (
                  <>
                <input
                  type="text"
                  placeholder="ابحث عن supplier أو PO number..."
                  value={cfPoSearch}
                  onChange={e => {
                    setCfPoSearch(e.target.value)
                    if (cfForm.po_id) setCfForm(p => ({ ...p, po_id: '', sub_code: '', cf_package: '', sub_code_desc: '' }))
                    setCfPoLines([])
                  }}
                  onFocus={e => { if (cfForm.po_id) setCfPoSearch('') }}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]"
                />
                {cfPoSearch && !cfForm.po_id && (
                  <div className="absolute z-20 w-full bg-white border border-[#DDD9D0] rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                    {availablePOs
                      .filter(po =>
                        po.vendor?.toLowerCase().includes(cfPoSearch.toLowerCase()) ||
                        po.po_number?.toLowerCase().includes(cfPoSearch.toLowerCase())
                      )
                      .slice(0, 25)
                      .map(po => (
                        <button key={po.id} type="button"
                          onClick={() => {
                            handleCFPoSelect(po.id)
                            setCfPoSearch('')
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F1EA] border-b border-[#F0EDE8] last:border-0">
                          <span className="font-mono text-[#1A7A4A] text-xs font-bold">{po.po_number}</span>
                          <span className="text-[#2E2C29] ml-2">{po.vendor}</span>
                        </button>
                      ))}
                    {availablePOs.filter(po =>
                      po.vendor?.toLowerCase().includes(cfPoSearch.toLowerCase()) ||
                      po.po_number?.toLowerCase().includes(cfPoSearch.toLowerCase())
                    ).length === 0 && (
                      <p className="px-4 py-3 text-xs text-[#9A948C]">لا توجد نتائج</p>
                    )}
                  </div>
                )}
                {cfForm.po_id && (
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-[#1A7A4A] font-medium">
                      ✓ {availablePOs.find(p => p.id === cfForm.po_id)?.po_number} — {availablePOs.find(p => p.id === cfForm.po_id)?.vendor}
                    </p>
                    <button type="button" onClick={() => { setCfForm(p => ({ ...p, po_id: '', sub_code: '', cf_package: '', sub_code_desc: '', ipc_id: '' })); setCfPoLines([]); setCfIpcOptions([]); setCfPoSearch('') }}
                      className="text-xs text-[#9A948C] hover:text-red-500">× تغيير</button>
                  </div>
                )}
                {cfForm.po_id && cfIpcOptions.length > 0 && (
                  <div className="mt-2">
                    <label className="text-xs font-semibold text-[#6B655C] uppercase tracking-wide">IPC (اختياري)</label>
                    <select value={cfForm.ipc_id||''} onChange={e => setCfForm(p => ({ ...p, ipc_id: e.target.value }))}
                      className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm mt-1">
                      <option value="">— بدون ربط بمستخلص محدد —</option>
                      {cfIpcOptions.map(ipc => (
                        <option key={ipc.id} value={ipc.id}>
                          IPC-{String(ipc.ipc_number).padStart(4,'0')} — Net: {Number(ipc.net_payable||0).toLocaleString('en-US',{maximumFractionDigits:0})} SAR ({ipc.approval_stage||'draft'})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                  </>
                )}
              </div>
              )}

              {!cfForm.petty_cash && !cfForm.no_po && cfForm.po_id && cfPoLines.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-[#6B655C] uppercase tracking-wide">بنود الـ PO — حط المبلغ على كل sub code</label>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#9A948C]">{[...new Set(cfPoLines.map(l=>l.sub_code||'—'))].length} sub codes</span>
                      <button type="button"
                        onClick={() => {
                          const newAmts = { ...cfLineAmounts }
                          cfPoLines.forEach(l => { if (parseFloat(l.amount_inc_vat) > 0) newAmts[l.id] = parseFloat(l.amount_inc_vat).toFixed(2) })
                          setCfLineAmounts(newAmts)
                        }}
                        className="text-xs px-3 py-1 rounded-full bg-[#1C1A18] text-white hover:bg-[#2E2C29] transition-colors font-medium">
                        Use All Full
                      </button>
                    </div>
                  </div>
                  <div className="border border-[#DDD9D0] rounded-xl overflow-hidden">
                    {[...new Set(cfPoLines.map(l => l.sub_code || '—'))].map(sc => {
                      const pkg = cfPackages.find(p => p.sub_code === sc)
                      const scLines = cfPoLines.filter(l => (l.sub_code || '—') === sc)
                      const scTotal = scLines.reduce((s, l) => s + (parseFloat(l.amount_inc_vat) || 0), 0)
                      const scAmountActive = parseFloat(cfScAmounts[sc]) > 0
                      const lineAmountsActive = scLines.some(l => parseFloat(cfLineAmounts[l.id]) > 0)
                      const hasAmt = scAmountActive || lineAmountsActive
                      const [expanded, setExpanded] = [cfExpandedSc?.[sc], (v) => setCfExpandedSc(prev => ({...prev, [sc]: v}))]
                      return (
                        <div key={sc} className={`border-b border-[#F0EDE8] last:border-0 ${hasAmt ? 'bg-[#1A7A4A]/5' : ''}`}>
                          <div className="flex items-center gap-3 px-4 py-3">
                            <button type="button" onClick={() => setExpanded(!expanded)}
                              className="text-[#9A948C] hover:text-[#1A7A4A] text-xs flex-shrink-0">
                              {expanded ? '▼' : '▶'}
                            </button>
                            <div className="flex-1 min-w-0">
                              <span className="font-mono text-sm font-bold text-[#1A7A4A]">{sc}</span>
                              <p className="text-xs text-[#9A948C] mt-0.5">{pkg?.sub_code_description || ''}</p>
                              {scTotal > 0 && (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-[#6B655C]">Total:</span>
                                  <span className="text-xs font-mono font-bold text-[#1C1A18]">
                                    {Number(scTotal).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                                  </span>
                                  <button type="button" disabled={lineAmountsActive}
                                    onClick={() => setCfScAmounts(prev => ({ ...prev, [sc]: scTotal.toFixed(2) }))}
                                    className="text-xs px-2 py-0.5 rounded-full bg-[#1A7A4A]/10 text-[#1A7A4A] hover:bg-[#1A7A4A] hover:text-white transition-colors font-medium disabled:opacity-40 disabled:pointer-events-none">
                                    Use Full
                                  </button>
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              placeholder="Total on sub code"
                              disabled={lineAmountsActive}
                              value={cfScAmounts[sc] || ''}
                              onChange={e => setCfScAmounts(prev => ({ ...prev, [sc]: e.target.value }))}
                              className={`w-40 border rounded-lg px-3 py-2 text-sm text-right font-bold focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none disabled:bg-[#F0EDE8] disabled:cursor-not-allowed ${
                                scAmountActive ? 'border-[#1A7A4A] bg-white text-[#1A7A4A]' : 'border-[#DDD9D0] bg-white'
                              }`}
                            />
                          </div>
                          {lineAmountsActive && (
                            <p className="mx-4 mb-2 -mt-1 text-xs text-[#9A948C]">فيه مبالغ متحطة على بنود جوه — الخانة دي مقفولة. امسح البنود الأول لو عايز تدفع إجمالي على الـ sub code.</p>
                          )}
                          {(() => {
                            const paidBefore = getPaidForPOLine(cfForm.po_id, sc === '—' ? null : sc)
                            const liveTotal = scAmountActive
                              ? parseFloat(cfScAmounts[sc]) || 0
                              : scLines.reduce((s, l) => s + (parseFloat(cfLineAmounts[l.id]) || 0), 0)
                            const c = checkCeiling(paidBefore, liveTotal, scTotal)
                            if (!c) return null
                            return (
                              <div className={`mx-4 mb-2 -mt-1 px-3 py-1.5 rounded-lg text-xs font-medium ${c.over ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-[#F5F1EA] text-[#9A948C]'}`}>
                                {c.over
                                  ? `تخطي عن قيمة الـ sub code بمقدار ${c.overBy.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR`
                                  : `مدفوع سابقاً: ${c.paidBefore.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} — متبقي: ${(c.ceiling - c.paidBefore).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                              </div>
                            )
                          })()}
                          {expanded && (
                            <div className="bg-[#F5F1EA] border-t border-[#E8E4DC]">
                              {scLines.map(l => (
                                <div key={l.id} className="flex items-center gap-3 px-8 py-2 border-b border-[#EDE7DA] last:border-0">
                                  <p className="flex-1 text-xs text-[#6B655C] line-clamp-1">{l.description}</p>
                                  {parseFloat(l.amount_inc_vat) > 0 && (
                                    <button type="button" disabled={scAmountActive}
                                      onClick={() => setCfLineAmounts(prev => ({ ...prev, [l.id]: parseFloat(l.amount_inc_vat).toFixed(2) }))}
                                      className="text-xs px-2 py-0.5 rounded-full bg-[#1A7A4A]/10 text-[#1A7A4A] hover:bg-[#1A7A4A] hover:text-white transition-colors font-medium whitespace-nowrap disabled:opacity-40 disabled:pointer-events-none">
                                      {Number(l.amount_inc_vat).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                                    </button>
                                  )}
                                  <input
                                    type="number"
                                    placeholder="0.00"
                                    disabled={scAmountActive}
                                    value={cfLineAmounts[l.id] || ''}
                                    onChange={e => setCfLineAmounts(prev => ({ ...prev, [l.id]: e.target.value }))}
                                    className="w-36 border border-[#DDD9D0] rounded-lg px-2 py-1.5 text-xs text-right font-medium focus:ring-2 focus:ring-[#1A7A4A] focus:outline-none bg-white disabled:bg-[#F0EDE8] disabled:cursor-not-allowed"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {(Object.values(cfLineAmounts).some(v => parseFloat(v) > 0) || Object.values(cfScAmounts).some(v => parseFloat(v) > 0)) && (
                      <div className="flex items-center justify-between px-4 py-2.5 bg-[#1C1A18]">
                        <span className="text-xs font-bold text-slate-400 uppercase">Total</span>
                        <span className="text-sm font-bold text-white">
                          SAR {Number(
                            Object.values(cfLineAmounts).reduce((s,v)=>s+(parseFloat(v)||0),0) +
                            Object.values(cfScAmounts).reduce((s,v)=>s+(parseFloat(v)||0),0)
                          ).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!cfForm.petty_cash && cfForm.po_id && cfPoLines.length === 0 && (
                <p className="text-xs text-[#9A948C]">لا يوجد بنود لهذا الـ PO</p>
              )}

              {cfForm.cf_package && !cfForm.petty_cash && (
                <div className="bg-[#1A7A4A]/10 border border-[#1A7A4A]/30 rounded-lg px-4 py-3">
                  <p className="text-xs text-[#6B655C]">CF Package detected:</p>
                  <p className="text-sm font-bold text-[#1A7A4A] mt-0.5">📦 {cfForm.cf_package}</p>
                  <p className="text-xs text-[#9A948C] mt-0.5">{cfForm.sub_code_desc}</p>
                </div>
              )}

              {!cfForm.petty_cash && (cfForm.no_po || (!Object.values(cfLineAmounts).some(v => parseFloat(v) > 0) && !Object.values(cfScAmounts).some(v => parseFloat(v) > 0))) && (
                <div>
                  <label className="block text-xs font-medium text-[#1A7A4A] mb-1">Amount Paid Inc. VAT (SAR) *</label>
                  <input type="number" value={cfForm.amount_inc_vat} onChange={e => setCfForm(p => ({ ...p, amount_inc_vat: e.target.value }))}
                    placeholder="0.00"
                    className="w-full border border-[#1A7A4A]/40 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-[#1A7A4A] bg-[#EDE7DA]" />
                  {!cfForm.no_po && cfForm.po_id && (() => {
                    const po = availablePOs.find(p => p.id === cfForm.po_id)
                    const paidBefore = getPaidForPOTotal(cfForm.po_id)
                    const c = checkCeiling(paidBefore, cfForm.amount_inc_vat, parseFloat(po?.total_inc_vat))
                    if (!c) return null
                    return (
                      <div className={`mt-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${c.over ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-[#F5F1EA] text-[#9A948C]'}`}>
                        {c.over
                          ? `تخطي عن قيمة الـ PO الكلية بمقدار ${c.overBy.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} SAR`
                          : `مدفوع سابقاً: ${c.paidBefore.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})} — متبقي: ${(c.ceiling - c.paidBefore).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                      </div>
                    )
                  })()}
                </div>
              )}

              {!cfForm.no_po && !cfForm.petty_cash && (
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                <input value={cfForm.notes} onChange={e => setCfForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              )}
            </div>
            <div className="flex gap-3 p-5 border-t border-[#DDD9D0]">
              <button onClick={() => { setCfShowModal(false); setCfPoLines([]); setCfPoSearch(''); setCfLineAmounts({}); setCfScAmounts({}); setCfExpandedSc({}); setSelectedPcVoucher(null); setPcVoucherLines([]); setPcLineAmounts({}); setPcVouchers([]); setPcSearch('') }}
                className="flex-1 py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Cancel</button>
              <button onClick={saveCFEntry}
                className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CASH OUT MODAL ===================== */}
      {showOutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
              <h2 className="text-lg font-bold">💸 {editOut ? 'Edit' : 'New'} Cash Out</h2>
              <button onClick={() => setShowOutModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Date *</label>
                <input type="date" value={outForm.date} onChange={e => setOutForm(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Payment Type</label>
                <select value={outForm.payment_type} onChange={e => setOutForm(p => ({ ...p, payment_type: e.target.value, po_id: '', contract_id: '' }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                  <option value="supplier">Supplier</option>
                  <option value="subcontractor">Subcontractor</option>
                  <option value="payroll">Payroll</option>
                  <option value="petty_cash">Petty Cash</option>
                </select>
              </div>

              {/* Reference based on type */}
              {outForm.payment_type === 'supplier' && (
                <div className="col-span-2">
                  <label className="block text-xs text-[#6B655C] mb-1">PO Reference</label>
                  <select value={outForm.po_id} onChange={e => handlePOSelect(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">-- Select PO --</option>
                    {availablePOs.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.vendor}</option>)}
                  </select>
                </div>
              )}
              {outForm.payment_type === 'subcontractor' && (
                <div className="col-span-2">
                  <label className="block text-xs text-[#6B655C] mb-1">Contract Reference</label>
                  <select value={outForm.contract_id} onChange={e => handleContractSelect(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">-- Select Contract --</option>
                    {availableContracts.map(c => <option key={c.id} value={c.id}>{c.contract_no} — {c.contractor_name}</option>)}
                  </select>
                </div>
              )}
              {outForm.payment_type === 'payroll' && (
                <div className="col-span-2">
                  <label className="block text-xs text-[#6B655C] mb-1">Payroll Period</label>
                  <input value={outForm.payroll_period} onChange={e => setOutForm(p => ({ ...p, payroll_period: e.target.value }))}
                    placeholder="Jan 2026" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
                </div>
              )}
              {outForm.payment_type === 'petty_cash' && (
                <div className="col-span-2">
                  <label className="block text-xs text-[#6B655C] mb-1">Petty Cash Reference</label>
                  <input value={outForm.petty_ref} onChange={e => setOutForm(p => ({ ...p, petty_ref: e.target.value }))}
                    placeholder="رقم الفاتورة" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
                </div>
              )}

              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Payee</label>
                <input value={outForm.payee} onChange={e => setOutForm(p => ({ ...p, payee: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Cost Code</label>
                <select value={outForm.cost_code_id} onChange={e => setOutForm(p => ({ ...p, cost_code_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                  <option value="">-- Select --</option>
                  {costCodes.map(c => <option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Amount Exc. VAT *</label>
                <input type="number" value={outForm.amount_exc_vat} onChange={e => handleOutAmountCalc('amount_exc_vat', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">VAT %</label>
                <select value={outForm.vat_pct} onChange={e => handleOutAmountCalc('vat_pct', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                  {VAT_OPTIONS.map(v => <option key={v} value={v}>{v}%</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">VAT Amount (auto)</label>
                <input type="number" value={outForm.vat_amount} readOnly
                  className="w-full border border-[#DDD9D0] rounded-lg px-3 py-2 text-sm bg-[#F5F1EA]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1 font-medium text-[#1A7A4A]">Amount Inc. VAT = Cash Out *</label>
                <input type="number" value={outForm.amount_inc_vat} onChange={e => setOutForm(p => ({ ...p, amount_inc_vat: e.target.value }))}
                  className="w-full border border-[#1A7A4A]/50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A] font-bold bg-[#EDE7DA]" />
              </div>

              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Payment Method</label>
                <select value={outForm.payment_method} onChange={e => setOutForm(p => ({ ...p, payment_method: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Status</label>
                <select value={outForm.status} onChange={e => setOutForm(p => ({ ...p, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]">
                  <option value="draft">Draft</option>
                  <option value="posted">Posted</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                <input value={outForm.notes} onChange={e => setOutForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
              <button onClick={() => setShowOutModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F5F1EA] text-sm">Cancel</button>
              <button onClick={saveOut} className="flex-1 py-2 bg-[#1C1A18] text-white rounded-lg hover:bg-[#2E2C29] text-sm font-medium">
                {editOut ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CASH IN MODAL ===================== */}
      {showInModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
              <h2 className="text-lg font-bold">💰 {editIn ? 'Edit' : 'New'} Cash In</h2>
              <button onClick={() => setShowInModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Period Month *</label>
                <input type="date" value={inForm.date} onChange={e => setInForm(p => ({ ...p, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-2">Opening Balance Status</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setInForm(p => ({ ...p, is_opening: true }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${inForm.is_opening ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-[#6B655C]'}`}>
                    Opening
                  </button>
                  <button type="button" onClick={() => setInForm(p => ({ ...p, is_opening: false }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 ${!inForm.is_opening ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-[#6B655C]'}`}>
                    Normal
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Invoice / Certificate No</label>
                <input value={inForm.invoice_no || ''} onChange={e => setInForm(p => ({ ...p, invoice_no: e.target.value }))}
                  placeholder="INV-001 أو رقم المستخلص"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Claimed (SAR)</label>
                  <input type="number" value={inForm.claimed_amount || ''} onChange={e => setInForm(p => ({ ...p, claimed_amount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1">Certified (SAR)</label>
                  <input type="number" value={inForm.certified_amount || ''} onChange={e => setInForm(p => ({ ...p, certified_amount: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs text-[#6B655C] mb-1 font-medium text-green-700">Collected (SAR) *</label>
                  <input type="number" value={inForm.collected_amount || ''} onChange={e => setInForm(p => ({ ...p, collected_amount: e.target.value }))}
                    className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 font-bold bg-green-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                <input value={inForm.notes || ''} onChange={e => setInForm(p => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500" />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
              <button onClick={() => setShowInModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-[#F5F1EA] text-sm">Cancel</button>
              <button onClick={saveIn} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                {editIn ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#DDD9D0]">
              <h3 className="font-bold text-[#1C1A18]">📝 {activeNoteLabel}</h3>
              <button onClick={() => setShowNoteModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
            </div>
            <div className="p-5">
              <textarea autoFocus value={noteText} onChange={e => setNoteText(e.target.value)}
                rows={5} dir="auto" placeholder="اكتب ملاحظتك أو تحليلك هنا..."
                className="w-full border border-[#DDD9D0] rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A7A4A] bg-[#F5F1EA]"/>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#DDD9D0]">
              {financeNotes[activeNoteKey] && (
                <button onClick={() => { saveFinanceNote(activeNoteKey, ''); setShowNoteModal(false) }}
                  className="px-4 py-2 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50">🗑 Delete</button>
              )}
              <button onClick={() => setShowNoteModal(false)}
                className="flex-1 py-2 border border-[#DDD9D0] rounded-lg text-sm hover:bg-[#F5F1EA]">Cancel</button>
              <button onClick={() => { saveFinanceNote(activeNoteKey, noteText); setShowNoteModal(false) }}
                className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg text-sm font-medium hover:bg-[#158a42]">Save Note</button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CASH OUT — NEW (ERP VIEW) TAB ===================== */}
      {/* SSOT = cf_payment_entries ONLY. purchase_orders/subcontracts are enrichment-only
          (PO Number, PO VAT %, PO Status, Contract Number) — never used for totals/counts.
          Hierarchy: MONTH → PAYMENT TYPE → PAYMENT ENTRIES, per the approved spec. */}

      {/* Cash Out Summary Modal */}
      {/* ===================== CASH OUT FORECAST TAB ===================== */}
      {activeTab === 'cash_out_forecast' && (
        <CashOutForecast projectId={projectId} />
      )}

      {/* ===================== FINANCIAL FEES TAB ===================== */}
      {activeTab === 'financial_fees' && (
        <FinancialFees projectId={projectId} />
      )}

      {/* ===================== CASH FLOW EXPECTATION TAB ===================== */}
      {activeTab === 'cf_expectation' && (
        <CashFlowExpectation projectId={projectId} />
      )}

      {/* ===================== CASH IN — NEW (parallel/temporary tab) ===================== */}
      {activeTab === 'cashin_new' && (() => {
        // ── Derive per-month rows: Scheduling (Invoice/Certified) + Finance (Collected/Advance) ──
        // ── Master month source: Scheduling (cashflow_settings.project_start_month/end_month) ──
        // Cash In must NEVER derive its month list from cash_in_planning/cash_in_entries rows —
        // a month with zero transactions must still appear. Scheduling defines the project
        // timeline; Cash In only reads it and merges financial data into those months.
        function buildMonthsRange(start, end) {
          const out = []
          const s = new Date(start); s.setDate(1)
          const e = new Date(end); e.setDate(1)
          while (s <= e) {
            out.push(s.toISOString().slice(0,7))
            s.setMonth(s.getMonth() + 1)
          }
          return out
        }
        const scheduledMonths = (cin2Settings?.project_start_month && cin2Settings?.project_end_month)
          ? buildMonthsRange(cin2Settings.project_start_month, cin2Settings.project_end_month)
          : []

        // certificateNumber ordering (1-based, chronological) is derived strictly from
        // Scheduling's certified months — independent of the timeline expansion below, so
        // adding future Expected/Actual months never shifts certificate numbering.
        const certifiedMonthsInOrder = (scheduledMonths.length > 0 ? scheduledMonths : [...new Set(cin2Planning.map(p => (p.period_month||'').slice(0,7)))].sort())
          .filter(ym => {
            const plan = cin2Planning.find(p => (p.period_month || '').slice(0,7) === ym)
            return (parseFloat(plan?.certified_amount) || 0) > 0
          })
        const certificateNumberByMonth = {}
        certifiedMonthsInOrder.forEach((ym, idx) => { certificateNumberByMonth[ym] = idx + 1 })

        // Legacy cashflow_settings.retention_pct/adv_recovery_pct are intentionally NOT
        // read here anymore — the new rule engine (project_cashin_rules / cin2Rules) is
        // now the sole source for Advance/Retention suggestions. cashflow_settings remains
        // untouched for CashFlowExpectation/legacy use elsewhere.
        const contractValue = parseFloat(cin2Project?.contract_value) || 0

        // ── Expected Collection distribution (FORECAST ONLY, no cash_in_entries writes) ──
        // For each certified month, distribute its NET Certified amount (the Certificate
        // itself is already the net expected cash figure — Retention/Advance Recovery/Other
        // Deductions are NOT recalculated against it here) across one or more target months
        // per the configured Expected Collection Shift/Lag rule, then aggregate by target
        // month. If two certifications both forecast collection into the same target month,
        // their amounts are summed there — correct, intended behavior (each certified month
        // contributes exactly once).
        const expectedByTargetMonth = {}
        certifiedMonthsInOrder.forEach(ym => {
          const plan = cin2Planning.find(p => (p.period_month || '').slice(0,7) === ym)
          const certifiedAmt = parseFloat(plan?.certified_amount) || 0
          const schedule = calcExpectedCollectionSchedule(cin2Rules, cin2ECScheduleRules, certifiedAmt, ym)
          schedule.forEach(({ targetMonth, amount }) => {
            expectedByTargetMonth[targetMonth] = (expectedByTargetMonth[targetMonth] || 0) + amount
          })
        })

        // ── Other Income / Other Deduction — INDEPENDENT cash movements. ──
        // These are Actual-side items only (Section 6/13 of spec: no Shift/Lag, no
        // Expected/Actual split — the user simply assigns them to a month, and they land
        // in the Actual Cash In total for that month). Never fed back into
        // expectedByTargetMonth above, and never passed through calcExpectedCollectionSchedule().
        const otherIncomeByMonth = {}
        const otherDeductionByMonth = {}
        cin2OtherMovements.forEach(r => {
          const m = (r.period_month || '').slice(0, 7)
          const bucket = r.type === 'deduction' ? otherDeductionByMonth : otherIncomeByMonth
          bucket[m] = (bucket[m] || 0) + (parseFloat(r.amount) || 0)
        })

        // ── Timeline = union of Scheduling months, Expected Collection target months, and ──
        // actual transaction months (Collection/Advance/cert_finance_detail). Never truncated
        // at Scheduling.project_end_month.
        const monthsSet = new Set([
          ...scheduledMonths,
          ...Object.keys(expectedByTargetMonth),
          ...cin2Planning.map(p => (p.period_month || '').slice(0,7)),
          ...cin2Entries.map(e => (e.period_month || '').slice(0,7)),
        ])
        monthsSet.delete('')
        const monthsList = [...monthsSet].sort()

        const allocatedByCollection = {}
        cin2Allocations.filter(a => a.status === 'active').forEach(a => {
          allocatedByCollection[a.collection_id] = (allocatedByCollection[a.collection_id] || 0) + (parseFloat(a.allocated_amount) || 0)
        })

        // has_advance/has_retention now come exclusively from the configured rule set —
        // never inferred merely from the presence of a transaction row (Section 8/11).
        const hasAnyAdvance = !!cin2Rules?.has_advance

        // Cumulative Advance Received: computed once, up front, across ALL months in order —
        // required by the rule engine's "remaining balance" check (Section 8/9), independent
        // of any single month's iteration.
        const totalAdvanceReceivedAllTime = cin2Entries
          .filter(e => e.transaction_type === 'advance_received')
          .reduce((s,e) => s + (parseFloat(e.collected_amount)||0), 0)

        let runningOutstanding = 0
        let runningAdvanceBalance = 0
        let cumulativeAdvanceRecoveredSoFar = 0
        let cumulativeRetentionSoFar = 0
        const monthRows = monthsList.map(ym => {
          const plan = cin2Planning.find(p => (p.period_month || '').slice(0,7) === ym)
          const invoice = parseFloat(plan?.client_invoice) || 0
          // Certified comes from Scheduling (cash_in_planning.certified_amount) — read-only here.
          const certified = parseFloat(plan?.certified_amount) || 0
          const certificateNumber = certificateNumberByMonth[ym] || 0

          const monthEntries = cin2Entries.filter(e => (e.period_month || '').slice(0,7) === ym)
          const collections = monthEntries.filter(e => e.transaction_type === 'collection')
          const advances = monthEntries.filter(e => e.transaction_type === 'advance_received')
          const certificationRow = monthEntries.find(e => e.transaction_type === 'cert_finance_detail')
          // Actual Collection: entered independently by Finance — never forced to equal
          // Certified or Expected Collection (business rule, Section 3/10 of the spec).
          const collected = collections.reduce((s,e) => s + (parseFloat(e.collected_amount)||0), 0)
          const collectionDates = collections.map(c => c.collection_date).filter(Boolean)
          const advanceReceived = advances.reduce((s,e) => s + (parseFloat(e.collected_amount)||0), 0)

          // Suggested values from the configured rule engine — shown only as a starting
          // point, never treated as an actual financial transaction (Section 7).
          const retentionSuggested = calcSuggestedRetention(cin2Rules, certified, certificateNumber, ym, cumulativeRetentionSoFar, contractValue)
          const advanceRecoverySuggested = calcSuggestedAdvanceRecovery(cin2Rules, certified, certificateNumber, ym, totalAdvanceReceivedAllTime, cumulativeAdvanceRecoveredSoFar)
          // Actual values (Finance-owned, cash_in_entries.retention_held_amount /
          // .advance_recovered_amount / .other_deduction_amount on the month's certification row).
          const hasCertDetail = !!certificationRow
          const retentionActual = hasCertDetail ? (parseFloat(certificationRow.retention_held_amount) || 0) : null
          const advanceRecoveryActual = hasCertDetail ? (parseFloat(certificationRow.advance_recovered_amount) || 0) : null
          const otherDeduction = hasCertDetail ? (parseFloat(certificationRow.other_deduction_amount) || 0) : 0
          const otherDeductionDescription = hasCertDetail ? (certificationRow.other_deduction_description || '') : ''
          // The value used in all financial math: actual once recorded by Finance,
          // suggested only as a fallback display before Finance records it.
          const retentionUsed = retentionActual !== null ? retentionActual : retentionSuggested
          const advanceRecoveryUsed = advanceRecoveryActual !== null ? advanceRecoveryActual : advanceRecoverySuggested
          // Cumulative trackers advance using the CONFIRMED actual once available, falling
          // back to the suggestion only while unconfirmed — keeps later months' start/stop/cap
          // evaluation consistent with what Finance has actually confirmed so far.
          cumulativeRetentionSoFar += retentionUsed
          cumulativeAdvanceRecoveredSoFar += advanceRecoveryUsed

          // Expected Collection is FORECAST ONLY — the sum of every certification's
          // distributed amount that targets THIS month (per the configured Expected
          // Collection rule), not necessarily this month's own certification. A
          // certification in an earlier month may forecast its collection here (fixed_lag/
          // split), and this month's own certification may forecast elsewhere. This never
          // creates or modifies cash_in_entries — it is purely a display aggregation of
          // expectedByTargetMonth, computed once above and shared by both views.
          const expectedCashIn = expectedByTargetMonth[ym] || 0
          // Other Income / Other Deduction — independent Actual-side cash movements
          // (Section 4/6 of spec: no Shift/Lag, assigned directly to a month by the user).
          const otherIncome = otherIncomeByMonth[ym] || 0
          const otherDeduction2 = otherDeductionByMonth[ym] || 0
          // Actual Cash In = Actual Collection + Advance Payment + Other Income − Other Deduction
          const actualCashIn = collected + advanceReceived + otherIncome - otherDeduction2
          const cashInVariance = actualCashIn - expectedCashIn
          const achievementPct = expectedCashIn !== 0 ? (actualCashIn / expectedCashIn) * 100 : null
          const cashInVariancePct = expectedCashIn !== 0 ? (cashInVariance / expectedCashIn) * 100 : null
          // Net Certified retains its prior meaning (Certified − Retention) for the
          // Outstanding/Receivable rollforward, independent of Advance Recovery/Deductions
          // (Advance has its own separate balance — Section 6/12 of the spec).
          const netCertified = certified - retentionUsed

          runningOutstanding += netCertified - collected
          runningAdvanceBalance += advanceReceived - advanceRecoveryUsed

          return {
            ym, invoice, certified, netCertified, certificateNumber,
            retentionSuggested, retentionActual, retentionUsed,
            advanceRecoverySuggested, advanceRecoveryActual, advanceRecoveryUsed,
            otherDeduction, otherDeductionDescription, hasCertDetail,
            expectedCashIn, collected, collectionDates,
            otherIncome, otherDeduction2, actualCashIn, cashInVariance, cashInVariancePct, achievementPct,
            advanceReceived,
            outstanding: runningOutstanding, advanceBalance: runningAdvanceBalance,
            collections, advances,
          }
        })

        const totalInvoice = monthRows.reduce((s,r) => s+r.invoice, 0)
        const totalCertified = monthRows.reduce((s,r) => s+r.certified, 0)
        const totalExpectedCashIn = monthRows.reduce((s,r) => s+r.expectedCashIn, 0)
        const totalCollected = monthRows.reduce((s,r) => s+r.collected, 0)
        const totalOtherIncome = monthRows.reduce((s,r) => s+r.otherIncome, 0)
        const totalOtherDeduction = monthRows.reduce((s,r) => s+r.otherDeduction2, 0)
        const totalActualCashIn = monthRows.reduce((s,r) => s+r.actualCashIn, 0)
        const totalCashInVariance = totalActualCashIn - totalExpectedCashIn
        const totalAdvanceReceived = monthRows.reduce((s,r) => s+r.advanceReceived, 0)
        const totalAdvanceRecovery = monthRows.reduce((s,r) => s+r.advanceRecoveryUsed, 0)
        const closingOutstanding = monthRows.length ? monthRows[monthRows.length-1].outstanding : 0
        const closingAdvanceBalance = monthRows.length ? monthRows[monthRows.length-1].advanceBalance : 0

        const thStyle = { padding: '8px 10px', fontSize: 10, fontWeight: 700, color: '#fff', textAlign: 'right', whiteSpace: 'nowrap' }
        const thStyleL = { ...thStyle, textAlign: 'left' }
        const tdStyle = { padding: '7px 10px', fontSize: 11, textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #E5E7EB' }
        const tdStyleL = { ...tdStyle, textAlign: 'left', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }

        return (
          <div style={{ padding: '20px 24px' }}>
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 11, color: '#92400E' }}>
              ⚠ Parallel development tab. The existing "Cash In / Out" tab remains the active production workflow until this page is reviewed and approved.
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 800, color: '#0A2540' }}>Cash In — New</h2>
                <p style={{ fontSize: 11, color: '#6B7280' }}>Project 1 • Finance Entries</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={cin2OpenRules} style={btnGhost('#6B7280')}>⚙ Cash In Rules</button>
                <button onClick={() => setCin2ShowUpload(!cin2ShowUpload)} style={btnGhost('#1A7A4A')}>↑ Upload Collections</button>
                <button onClick={cin2DownloadTemplate} style={btnGhost('#1A7A4A')}>↓ Template</button>
                <button onClick={cin2OpenCollection} style={btn('#1A7A4A')}>+ Collection</button>
                <button onClick={cin2OpenAdvance} style={btnGhost('#1A7A4A')}>+ Advance</button>
              </div>
            </div>

            {/* Three presentations under one unified Liquidity Report tab: Cash In ERP,
                Cash Out ERP (own fetch/state, cout2*), and the combined Schedule View
                built from monthRows + cout2Entries. Switching only changes rendering. */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #E5E7EB' }}>
              <button onClick={() => setCin2ActiveView('erp')}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer',
                  color: cin2ActiveView === 'erp' ? '#0A2540' : '#9CA3AF',
                  borderBottom: cin2ActiveView === 'erp' ? '2px solid #0A2540' : '2px solid transparent', marginBottom: -2 }}>
                ERP View — Cash In
              </button>
              <button onClick={() => setCin2ActiveView('erp_out')}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer',
                  color: cin2ActiveView === 'erp_out' ? '#0A2540' : '#9CA3AF',
                  borderBottom: cin2ActiveView === 'erp_out' ? '2px solid #0A2540' : '2px solid transparent', marginBottom: -2 }}>
                ERP View — Cash Out
              </button>
              <button onClick={() => setCin2ActiveView('schedule')}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer',
                  color: cin2ActiveView === 'schedule' ? '#0A2540' : '#9CA3AF',
                  borderBottom: cin2ActiveView === 'schedule' ? '2px solid #0A2540' : '2px solid transparent', marginBottom: -2 }}>
                Cash Schedule View
              </button>
            </div>

            {cin2ActiveView === 'erp' && (<>
            {cin2ShowUpload && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <p style={{ fontSize: 12, marginBottom: 8 }}>Excel columns: <code>Month, Transaction Type (collection/advance_received), Collected Amount, Reference</code></p>
                <input type="file" accept=".xlsx,.xls" onChange={e => e.target.files[0] && cin2HandleUploadFile(e.target.files[0])} style={{ fontSize: 12, marginBottom: 8 }} />
                {cin2UploadError && <p style={{ color: '#DC2626', fontSize: 12 }}>{cin2UploadError}</p>}
                {cin2UploadRows.length > 0 && (
                  <>
                    <p style={{ fontSize: 12, marginBottom: 8 }}>{cin2UploadRows.length} rows ready to import.</p>
                    <button onClick={cin2ConfirmUpload} disabled={cin2Uploading} style={btn('#1A7A4A')}>{cin2Uploading ? 'Uploading…' : 'Confirm Upload'}</button>
                  </>
                )}
              </div>
            )}

            <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyleL, background: '#0A2540' }} rowSpan={2}>Month</th>
                    <th style={{ ...thStyle, background: '#0369A1' }} colSpan={2}>EXPECTED</th>
                    <th style={{ ...thStyle, background: '#1A7A4A' }} colSpan={hasAnyAdvance ? 4 : 3}>ACTUAL</th>
                    <th style={{ ...thStyle, background: '#7C3AED' }} colSpan={4}>VARIANCE / PERFORMANCE</th>
                  </tr>
                  <tr>
                    <th style={{ ...thStyle, background: '#0369A1' }}>Client Invoice</th>
                    <th style={{ ...thStyle, background: '#0369A1' }}>Certified / Expected Cash In</th>
                    <th style={{ ...thStyle, background: '#1A7A4A' }}>Actual Collection</th>
                    {hasAnyAdvance && <th style={{ ...thStyle, background: '#1A7A4A' }}>Advance Payment</th>}
                    <th style={{ ...thStyle, background: '#1A7A4A' }}>Other Income</th>
                    <th style={{ ...thStyle, background: '#1A7A4A' }}>Other Deduction</th>
                    <th style={{ ...thStyle, background: '#7C3AED' }}>Expected Cash In</th>
                    <th style={{ ...thStyle, background: '#7C3AED' }}>Actual Cash In</th>
                    <th style={{ ...thStyle, background: '#7C3AED' }}>Variance</th>
                    <th style={{ ...thStyle, background: '#7C3AED' }}>Achievement %</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.length === 0 ? (
                    <tr><td colSpan={hasAnyAdvance ? 10 : 9} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>No data yet — start by adding a Collection or Advance, or enter Certified/Invoice in Scheduling</td></tr>
                  ) : monthRows.map((r,ri) => (
                    <Fragment key={r.ym}>
                      <tr className={ri%2===0?'bg-white':'bg-[#FAFAFA]'} onClick={() => setCin2OpenMonth(cin2OpenMonth === r.ym ? null : r.ym)}>
                        <td style={tdStyleL}>{cin2OpenMonth === r.ym ? '▾' : '▸'} {r.ym}</td>
                        <td style={tdStyle}>{fmt(r.invoice)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }} title="The Certificate amount already represents the NET expected cash figure">{fmt(r.certified)}</td>
                        <td style={{ ...tdStyle, color: '#1A7A4A', fontWeight: 700 }}>{fmt(r.collected)}</td>
                        {hasAnyAdvance && <td style={tdStyle}>{r.advanceReceived ? fmt(r.advanceReceived) : '—'}</td>}
                        <td style={{ ...tdStyle, color: '#0E7490' }}>{r.otherIncome ? fmt(r.otherIncome) : '—'}</td>
                        <td style={{ ...tdStyle, color: '#B45309' }}>{r.otherDeduction2 ? fmt(r.otherDeduction2) : '—'}</td>
                        <td style={{ ...tdStyle, color: '#6B7280' }} title="Certificate Cash In after existing Collection Shift/Lag">{fmt(r.expectedCashIn)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{fmt(r.actualCashIn)}</td>
                        <td style={{ ...tdStyle, color: r.cashInVariance < 0 ? '#DC2626' : r.cashInVariance > 0 ? '#1A7A4A' : '#9CA3AF' }}>
                          {r.actualCashIn > 0 || r.expectedCashIn > 0 ? fmtSigned(r.cashInVariance) : '—'}
                        </td>
                        <td style={tdStyle}>{r.achievementPct !== null ? r.achievementPct.toFixed(0) + '%' : '—'}</td>
                      </tr>
                      {cin2OpenMonth === r.ym && (
                        <tr>
                          <td colSpan={hasAnyAdvance ? 10 : 9} style={{ padding: 0, background: '#F8FAFC', borderBottom: '2px solid #E5E7EB' }}>
                            <div style={{ padding: 16 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div>
                                  <h4 style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#0E7490', marginBottom: 6 }}>Other Income</h4>
                                  {cin2OtherMovements.filter(row => row.type === 'income' && (row.period_month || '').slice(0,7) === r.ym).length === 0 ? (
                                    <p style={{ fontSize: 12, color: '#9CA3AF' }}>(none)</p>
                                  ) : cin2OtherMovements.filter(row => row.type === 'income' && (row.period_month || '').slice(0,7) === r.ym).map(row => (
                                    <div key={row.id} style={{ fontSize: 12, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontWeight: 600 }}>{cin2CategoryName(row.category_id)}</span>
                                      <span>— {fmt(row.amount)}</span>
                                      {row.notes && <span style={{ fontSize: 10, color: '#9CA3AF' }}>({row.notes})</span>}
                                      <button onClick={() => cin2DeleteOtherMovement(row.id)} style={{ ...btnGhost('#DC2626'), padding: '1px 6px', fontSize: 10, marginLeft: 'auto' }}>Delete</button>
                                    </div>
                                  ))}
                                  <button onClick={() => cin2OpenAddMovement('income', r.ym)} style={{ ...btnGhost('#0E7490'), marginTop: 6 }}>+ Other Income</button>
                                </div>
                                <div>
                                  <h4 style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#B45309', marginBottom: 6 }}>Other Deduction</h4>
                                  {cin2OtherMovements.filter(row => row.type === 'deduction' && (row.period_month || '').slice(0,7) === r.ym).length === 0 ? (
                                    <p style={{ fontSize: 12, color: '#9CA3AF' }}>(none)</p>
                                  ) : cin2OtherMovements.filter(row => row.type === 'deduction' && (row.period_month || '').slice(0,7) === r.ym).map(row => (
                                    <div key={row.id} style={{ fontSize: 12, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontWeight: 600 }}>{cin2CategoryName(row.category_id)}</span>
                                      <span>— {fmt(row.amount)}</span>
                                      {row.notes && <span style={{ fontSize: 10, color: '#9CA3AF' }}>({row.notes})</span>}
                                      <button onClick={() => cin2DeleteOtherMovement(row.id)} style={{ ...btnGhost('#DC2626'), padding: '1px 6px', fontSize: 10, marginLeft: 'auto' }}>Delete</button>
                                    </div>
                                  ))}
                                  <button onClick={() => cin2OpenAddMovement('deduction', r.ym)} style={{ ...btnGhost('#B45309'), marginTop: 6 }}>+ Other Deduction</button>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px dashed #DDD9D0' }}>
                                <button onClick={cin2OpenCollection} style={{ ...btnGhost('#1A7A4A') }}>+ Collection</button>
                                {hasAnyAdvance && <button onClick={cin2OpenAdvance} style={{ ...btnGhost('#7C3AED') }}>+ Advance</button>}
                                {r.certified > 0 && (
                                  <button onClick={() => cin2OpenCertDetail(r)} style={{ ...btnGhost('#6B7280'), marginLeft: 'auto' }} title="Record confirmed Retention Held / Advance Recovered for this certificate (used for Outstanding tracking only — does not affect Expected/Actual Cash In above)">
                                    Retention / Advance Recovery…
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
                {monthRows.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#0A2540' }}>
                      <td style={{ ...tdStyleL, color: '#fff', cursor: 'default' }}>TOTAL</td>
                      <td style={{ ...tdStyle, color: '#fff' }}>{fmt(totalInvoice)}</td>
                      <td style={{ ...tdStyle, color: '#fff', fontWeight: 700 }}>{fmt(totalCertified)}</td>
                      <td style={{ ...tdStyle, color: '#fff', fontWeight: 700 }}>{fmt(totalCollected)}</td>
                      {hasAnyAdvance && <td style={{ ...tdStyle, color: '#fff' }}>{fmt(totalAdvanceReceived)}</td>}
                      <td style={{ ...tdStyle, color: '#fff' }}>{fmt(totalOtherIncome)}</td>
                      <td style={{ ...tdStyle, color: '#fff' }}>{fmt(totalOtherDeduction)}</td>
                      <td style={{ ...tdStyle, color: '#fff' }}>{fmt(totalExpectedCashIn)}</td>
                      <td style={{ ...tdStyle, color: '#fff', fontWeight: 700 }}>{fmt(totalActualCashIn)}</td>
                      <td style={{ ...tdStyle, color: totalCashInVariance < 0 ? '#FCA5A5' : '#fff' }}>{fmtSigned(totalCashInVariance)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            </>)}

      {cin2ActiveView === 'erp_out' && (() => {
        const thS  = { padding: '8px 10px', fontSize: 10, fontWeight: 700, color: '#fff', textAlign: 'right', whiteSpace: 'nowrap' }
        const thSL = { ...thS, textAlign: 'left' }
        const tdS  = { padding: '7px 10px', fontSize: 11, textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #E5E7EB' }
        const tdSL = { ...tdS, textAlign: 'left', fontFamily: 'inherit' }

        // ── Filtering — operates on cf_payment_entries (enriched) only. ──
        const f = cout2Filters
        const filtered = cout2Entries.filter(e => {
          const ym = (e.date || '').slice(0, 7)
          if (f.month && ym.slice(5, 7) !== f.month) return false
          if (f.year && ym.slice(0, 4) !== f.year) return false
          if (f.paymentType !== 'all' && (e.payment_type || 'subcontractor_supplier') !== f.paymentType) return false
          if (f.payee && !(e.payee || '').toLowerCase().includes(f.payee.toLowerCase())) return false
          if (f.poNumber && !(e._po_number || '').toLowerCase().includes(f.poNumber.toLowerCase())) return false
          if (f.costCode && !(e.sub_code || '').toLowerCase().includes(f.costCode.toLowerCase())) return false
          if (f.poStatus !== 'all' && (e._po_status || '') !== f.poStatus) return false
          if (f.search) {
            const s = f.search.toLowerCase()
            const hay = [e.payee, e._po_number, e.sub_code, e.notes, e._contract_no].filter(Boolean).join(' ').toLowerCase()
            if (!hay.includes(s)) return false
          }
          return true
        })

        // ── KPIs — all computed from filtered cf_payment_entries, never from PO totals. ──
        const totalCashOut = filtered.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
        const paymentCount = filtered.length
        const avgPayment = paymentCount > 0 ? totalCashOut / paymentCount : 0
        const now = new Date()
        const curYm = now.toISOString().slice(0, 7)
        const thisMonthTotal = filtered.filter(e => (e.date || '').slice(0, 7) === curYm)
          .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
        const prevYm = (() => { const d = new Date(now); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7) })()
        const prevMonthTotal = filtered.filter(e => (e.date || '').slice(0, 7) === prevYm)
          .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
        const ytdTotal = filtered.filter(e => (e.date || '').slice(0, 4) === String(now.getFullYear()))
          .reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)

        // ── Monthly aggregation — one row per month present in the (filtered) data. ──
        // Months with zero transactions are not fabricated here (no independent project
        // timeline source for Cash Out, unlike Cash In's Scheduling-driven month range) —
        // every month shown corresponds to actual Payment Entries, satisfying "do not
        // invent dates" while still covering the full range that exists in the data.
        const monthsSet = new Set(filtered.map(e => (e.date || '').slice(0, 7)).filter(Boolean))
        const monthsList = [...monthsSet].sort()
        const monthlyRows = monthsList.map((ym, idx) => {
          const monthEntries = filtered.filter(e => (e.date || '').slice(0, 7) === ym)
          const total = monthEntries.reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
          const prevTotal = idx > 0
            ? filtered.filter(e => (e.date || '').slice(0, 7) === monthsList[idx - 1]).reduce((s, e) => s + (parseFloat(e.amount_inc_vat) || 0), 0)
            : null
          const momChange = prevTotal !== null && prevTotal !== 0 ? ((total - prevTotal) / prevTotal) * 100 : null
          // Payment Type breakdown for this month — sum of types reconciles exactly to `total`
          // by construction (every entry belongs to exactly one payment_type bucket).
          const byType = {}
          monthEntries.forEach(e => {
            const t = e.payment_type || 'subcontractor_supplier'
            if (!byType[t]) byType[t] = { amount: 0, count: 0 }
            byType[t].amount += parseFloat(e.amount_inc_vat) || 0
            byType[t].count += 1
          })
          const typeRows = Object.entries(byType).map(([type, v]) => ({
            type, label: paymentTypeLabel(type), amount: v.amount, count: v.count,
            pct: total > 0 ? (v.amount / total) * 100 : 0,
          })).sort((a, b) => b.amount - a.amount)
          // No existing Expected Cash Out data source (unlike Cash In's Scheduling/
          // Certified SSOT) — shown as "Not Available" rather than inventing a forecast
          // engine that has no real system source, per explicit instruction.
          const expectedCashOut = null
          const variance = expectedCashOut !== null ? total - expectedCashOut : null
          return { ym, total, count: monthEntries.length, momChange, typeRows, entries: monthEntries, expectedCashOut, variance }
        })

        // ── Payment Type summary (whole filtered set) — reconciles exactly to totalCashOut. ──
        const byTypeAll = {}
        filtered.forEach(e => {
          const t = e.payment_type || 'subcontractor_supplier'
          if (!byTypeAll[t]) byTypeAll[t] = { amount: 0, count: 0 }
          byTypeAll[t].amount += parseFloat(e.amount_inc_vat) || 0
          byTypeAll[t].count += 1
        })
        const typeSummaryAll = Object.entries(byTypeAll).map(([type, v]) => ({
          type, label: paymentTypeLabel(type), amount: v.amount, count: v.count,
          pct: totalCashOut > 0 ? (v.amount / totalCashOut) * 100 : 0,
        })).sort((a, b) => b.amount - a.amount)

        const allPoStatuses = [...new Set(cout2Entries.map(e => e._po_status).filter(Boolean))].sort()
        const availableYears = [...new Set(cout2Entries.map(e => (e.date || '').slice(0, 4)).filter(Boolean))].sort()

        return (
          <div style={{ padding: '20px 24px' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 800, color: '#0A2540' }}>Cash Out — New (ERP View)</h2>
                <p style={{ fontSize: 11, color: '#6B7280' }}>Source: Payment Entries (cf_payment_entries) • PO/Subcontract data shown for reference only</p>
              </div>
            </div>

            {cout2Loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>Loading…</div>
            ) : (
              <>
                {/* ── KPI HEADER ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Total Cash Out', value: fmt(totalCashOut), color: '#0A2540' },
                    { label: 'This Month', value: fmt(thisMonthTotal), color: '#1A7A4A' },
                    { label: 'YTD Cash Out', value: fmt(ytdTotal), color: '#0369A1' },
                    { label: 'Previous Month', value: fmt(prevMonthTotal), color: '#6B7280' },
                    { label: 'Payment Count', value: paymentCount.toLocaleString(), color: '#7C3AED' },
                  ].map((k, i) => (
                    <div key={i} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '14px 16px' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9CA3AF', marginBottom: 6 }}>{k.label}</p>
                      <p style={{ fontSize: 18, fontWeight: 800, color: k.color, fontFamily: 'monospace' }}>{k.value}</p>
                    </div>
                  ))}
                </div>

                {/* ── FILTERS ── */}
                <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Year</label>
                    <select value={f.year} onChange={e => setCout2Filters(p => ({ ...p, year: e.target.value }))}
                      style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6 }}>
                      <option value="">All</option>
                      {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Month</label>
                    <select value={f.month} onChange={e => setCout2Filters(p => ({ ...p, month: e.target.value }))}
                      style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6 }}>
                      <option value="">All</option>
                      {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Payment Type</label>
                    <select value={f.paymentType} onChange={e => setCout2Filters(p => ({ ...p, paymentType: e.target.value }))}
                      style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6 }}>
                      <option value="all">All</option>
                      {getAllPaymentTypes().map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>PO Status</label>
                    <select value={f.poStatus} onChange={e => setCout2Filters(p => ({ ...p, poStatus: e.target.value }))}
                      style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6 }}>
                      <option value="all">All</option>
                      {allPoStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Payee</label>
                    <input value={f.payee} onChange={e => setCout2Filters(p => ({ ...p, payee: e.target.value }))}
                      placeholder="Search payee…" style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, width: 130 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>PO Number</label>
                    <input value={f.poNumber} onChange={e => setCout2Filters(p => ({ ...p, poNumber: e.target.value }))}
                      placeholder="PO #…" style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, width: 110 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Cost Code</label>
                    <input value={f.costCode} onChange={e => setCout2Filters(p => ({ ...p, costCode: e.target.value }))}
                      placeholder="Cost code…" style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, width: 110 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ display: 'block', fontSize: 10, color: '#9CA3AF', marginBottom: 3 }}>Search</label>
                    <input value={f.search} onChange={e => setCout2Filters(p => ({ ...p, search: e.target.value }))}
                      placeholder="Search payee, PO, cost code, notes…" style={{ padding: '6px 10px', fontSize: 11, border: '1px solid #D1D5DB', borderRadius: 6, width: '100%' }} />
                  </div>
                  {(f.month || f.year || f.paymentType !== 'all' || f.payee || f.poNumber || f.costCode || f.poStatus !== 'all' || f.search) && (
                    <button onClick={() => setCout2Filters({ month: '', year: '', paymentType: 'all', payee: '', poNumber: '', costCode: '', poStatus: 'all', search: '' })}
                      style={{ ...btnGhost('#6B7280'), padding: '6px 10px' }}>Clear filters</button>
                  )}
                </div>

                {/* ── PAYMENT TYPE SUMMARY (whole filtered set) ── */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${typeSummaryAll.length || 1}, 1fr)`, gap: 12, marginBottom: 20 }}>
                  {typeSummaryAll.map(t => (
                    <div key={t.type} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9CA3AF', marginBottom: 4 }}>{t.label}</p>
                      <p style={{ fontSize: 15, fontWeight: 800, color: '#0A2540', fontFamily: 'monospace' }}>{fmt(t.amount)}</p>
                      <p style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>{t.pct.toFixed(1)}% • {t.count} txn{t.count !== 1 ? 's' : ''}</p>
                    </div>
                  ))}
                </div>

                {/* ── MONTHLY CASH OUT (main ERP table) ── */}
                <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ background: '#0A2540' }}>
                        <th style={thSL}>Month</th>
                        <th style={thS}>Total Cash Out</th>
                        <th style={thS}>Payment Count</th>
                        <th style={thS}>MoM Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyRows.length === 0 ? (
                        <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>No Payment Entries match the current filters</td></tr>
                      ) : monthlyRows.map((r, ri) => (
                        <Fragment key={r.ym}>
                          <tr className={ri % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'} style={{ cursor: 'pointer' }}
                            onClick={() => { setCout2OpenMonth(cout2OpenMonth === r.ym ? null : r.ym); setCout2OpenType(null) }}>
                            <td style={{ ...tdSL, fontWeight: 700 }}>{cout2OpenMonth === r.ym ? '▾' : '▸'} {r.ym}</td>
                            <td style={{ ...tdS, fontWeight: 700 }}>{fmt(r.total)}</td>
                            <td style={tdS}>{r.count}</td>
                            <td style={{ ...tdS, color: r.momChange === null ? '#9CA3AF' : r.momChange < 0 ? '#1A7A4A' : r.momChange > 0 ? '#D97706' : '#9CA3AF' }}>
                              {r.momChange === null ? '—' : `${r.momChange >= 0 ? '+' : ''}${r.momChange.toFixed(1)}%`}
                            </td>
                          </tr>

                          {cout2OpenMonth === r.ym && (
                            <tr>
                              <td colSpan={4} style={{ padding: 0, background: '#F8FAFC', borderBottom: '2px solid #E5E7EB' }}>
                                <div style={{ padding: 14 }}>
                                  <h4 style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#0369A1', marginBottom: 8 }}>
                                    Payment Type Breakdown — {r.ym}
                                  </h4>
                                  <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 4 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ ...thSL, background: '#0369A1' }}>Payment Type</th>
                                        <th style={{ ...thS, background: '#0369A1' }}>Amount</th>
                                        <th style={{ ...thS, background: '#0369A1' }}>Count</th>
                                        <th style={{ ...thS, background: '#0369A1' }}>% of Month</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {r.typeRows.map(t => {
                                        const key = `${r.ym}|${t.type}`
                                        const isOpen = cout2OpenType === key
                                        return (
                                          <Fragment key={t.type}>
                                            <tr style={{ cursor: 'pointer', background: isOpen ? '#EFF6FF' : 'transparent' }}
                                              onClick={() => setCout2OpenType(isOpen ? null : key)}>
                                              <td style={{ ...tdSL, paddingLeft: 20 }}>{isOpen ? '▾' : '▸'} {t.label}</td>
                                              <td style={tdS}>{fmt(t.amount)}</td>
                                              <td style={tdS}>{t.count}</td>
                                              <td style={tdS}>{t.pct.toFixed(1)}%</td>
                                            </tr>
                                            {isOpen && (
                                              <tr>
                                                <td colSpan={4} style={{ padding: '10px 10px 14px 30px', background: '#fff' }}>
                                                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                    <thead>
                                                      <tr>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>Date</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>Payee</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>PO Number</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>Contract No.</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>Cost Code</th>
                                                        <th style={{ ...thS, background: '#1A7A4A', fontSize: 9 }}>Amount Inc. VAT</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>PO VAT %</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>PO Status</th>
                                                        <th style={{ ...thSL, background: '#1A7A4A', fontSize: 9 }}>Notes</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {r.entries.filter(e => (e.payment_type || 'subcontractor_supplier') === t.type).map(e => (
                                                        <tr key={e.id}>
                                                          <td style={{ ...tdSL, fontSize: 10 }}>{e.date}</td>
                                                          <td style={{ ...tdSL, fontSize: 10 }}>{e.payee || '—'}</td>
                                                          <td style={{ ...tdSL, fontSize: 10 }}>{e._po_number || '—'}</td>
                                                          <td style={{ ...tdSL, fontSize: 10 }}>{e._contract_no || 'Not Available'}</td>
                                                          <td style={{ ...tdSL, fontSize: 10 }}>{e.sub_code || '—'}</td>
                                                          <td style={{ ...tdS, fontSize: 10, fontWeight: 700 }}>{fmt(e.amount_inc_vat)}</td>
                                                          <td style={{ ...tdSL, fontSize: 10, color: '#9CA3AF' }} title="PO-level VAT rate — not transaction-specific">
                                                            {e._po_vat_pct != null ? `${e._po_vat_pct}% (PO)` : 'Not Available'}
                                                          </td>
                                                          <td style={{ ...tdSL, fontSize: 10, color: '#9CA3AF' }} title="PO status — not the payment transaction's own status">
                                                            {e._po_status ? `${e._po_status} (PO)` : 'Not Available'}
                                                          </td>
                                                          <td style={{ ...tdSL, fontSize: 10, color: '#6B7280' }}>{e.notes || '—'}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </td>
                                              </tr>
                                            )}
                                          </Fragment>
                                        )
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ background: '#F5F1EA' }}>
                                        <td style={{ ...tdSL, fontWeight: 700 }}>Month Total</td>
                                        <td style={{ ...tdS, fontWeight: 700 }}>{fmt(r.total)}</td>
                                        <td style={{ ...tdS, fontWeight: 700 }}>{r.count}</td>
                                        <td style={tdS}>100%</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                    {monthlyRows.length > 0 && (
                      <tfoot>
                        <tr style={{ background: '#0A2540' }}>
                          <td style={{ ...tdSL, color: '#fff', fontWeight: 800 }}>TOTAL</td>
                          <td style={{ ...tdS, color: '#fff', fontWeight: 800 }}>{fmt(totalCashOut)}</td>
                          <td style={{ ...tdS, color: '#fff', fontWeight: 800 }}>{paymentCount}</td>
                          <td style={tdS}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <p style={{ fontSize: 10, color: '#9CA3AF', marginTop: 8 }}>
                  * Amount Exc. VAT, VAT Amount and Payment Method are not available at the individual transaction level in the current data model — not shown to avoid fabricating figures.
                  PO VAT % and PO Status reflect the linked Purchase Order, not the payment transaction itself.
                </p>
              </>
            )}
          </div>
        )
      })()}

            {cin2ActiveView === 'schedule' && (
              <CashScheduleView monthRows={monthRows} fmt={fmt} fmtSigned={fmtSigned} hasAnyAdvance={hasAnyAdvance} otherMovements={cin2OtherMovements} categoryMaster={cin2CategoryMaster} cashOutEntries={cout2Entries} paymentTypeLabel={paymentTypeLabel} />
            )}

            {cin2ShowCollectionModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Collection</h2>
                    <button onClick={() => setCin2ShowCollectionModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Date *</label>
                      <input type="date" value={cin2CollectionForm.date} onChange={e => setCin2CollectionForm(p => ({ ...p, date: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1 font-medium text-green-700">Amount (SAR) *</label>
                      <input type="number" value={cin2CollectionForm.collected_amount} onChange={e => setCin2CollectionForm(p => ({ ...p, collected_amount: e.target.value }))}
                        className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm font-bold bg-green-50" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Reference</label>
                      <input value={cin2CollectionForm.notes} onChange={e => setCin2CollectionForm(p => ({ ...p, notes: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowCollectionModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveCollection} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Add</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowAddMovementModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">{cin2ShowAddMovementModal.type === 'income' ? 'Other Income' : 'Other Deduction'}</h2>
                    <button onClick={() => setCin2ShowAddMovementModal(null)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Category *</label>
                      <select value={cin2ShowAddMovementModal.category_id}
                        onChange={e => setCin2ShowAddMovementModal(p => ({ ...p, category_id: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">Select…</option>
                        {cin2CategoryMaster.filter(c => c.type === cin2ShowAddMovementModal.type && c.is_active).map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                        <option value="__new__">+ Create new category…</option>
                      </select>
                    </div>
                    {cin2ShowAddMovementModal.category_id === '__new__' && (
                      <div>
                        <label className="block text-xs text-[#6B655C] mb-1">New Category Name *</label>
                        <input value={cin2ShowAddMovementModal.new_category_name} placeholder="e.g. Loan, Bank Charges…"
                          onChange={e => setCin2ShowAddMovementModal(p => ({ ...p, new_category_name: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1 font-medium text-cyan-700">Amount (SAR) *</label>
                      <input type="number" value={cin2ShowAddMovementModal.amount}
                        onChange={e => setCin2ShowAddMovementModal(p => ({ ...p, amount: e.target.value }))}
                        className="w-full border border-cyan-300 rounded-lg px-3 py-2 text-sm font-bold bg-cyan-50" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Month *</label>
                      <input type="month" value={cin2ShowAddMovementModal.month?.slice(0,7)}
                        onChange={e => setCin2ShowAddMovementModal(p => ({ ...p, month: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                      <input value={cin2ShowAddMovementModal.notes} onChange={e => setCin2ShowAddMovementModal(p => ({ ...p, notes: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowAddMovementModal(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveOtherMovement} className="flex-1 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium">Add</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowManageCategories && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Manage Other Income / Deduction Categories</h2>
                    <button onClick={() => setCin2ShowManageCategories(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                    {['income','deduction'].map(t => (
                      <div key={t}>
                        <h4 className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: t === 'income' ? '#0E7490' : '#B45309' }}>
                          {t === 'income' ? 'Income Categories' : 'Deduction Categories'}
                        </h4>
                        {cin2CategoryMaster.filter(c => c.type === t).map(c => (
                          <div key={c.id} className="flex items-center justify-between text-sm py-1">
                            <span style={{ opacity: c.is_active ? 1 : 0.4 }}>{c.name}</span>
                            <button onClick={() => cin2ToggleCategoryActive(c.id, c.is_active)} className="text-xs underline text-gray-500">
                              {c.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <input placeholder={`New ${t} category…`} id={`new-cat-${t}`}
                            className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm" />
                          <button onClick={() => { const el = document.getElementById(`new-cat-${t}`); cin2AddCategory(el.value, t); el.value = '' }}
                            className="px-3 py-1 bg-gray-700 text-white rounded-lg text-xs">Add</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowManageCategories(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Close</button>
                  </div>
                </div>
              </div>
            )}


            {cin2ShowAdvanceModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Advance Received</h2>
                    <button onClick={() => setCin2ShowAdvanceModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Date *</label>
                      <input type="date" value={cin2AdvanceForm.date} onChange={e => setCin2AdvanceForm(p => ({ ...p, date: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1 font-medium text-green-700">Amount (SAR) *</label>
                      <input type="number" value={cin2AdvanceForm.collected_amount} onChange={e => setCin2AdvanceForm(p => ({ ...p, collected_amount: e.target.value }))}
                        className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm font-bold bg-green-50" />
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Notes</label>
                      <input value={cin2AdvanceForm.notes} onChange={e => setCin2AdvanceForm(p => ({ ...p, notes: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowAdvanceModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveAdvance} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Add</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowAllocateModal && cin2AllocateTarget && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Allocate Collection</h2>
                    <button onClick={() => setCin2ShowAllocateModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <p className="text-xs text-[#6B655C]">Collection amount: <span className="font-bold">{fmt(cin2AllocateTarget.collected_amount||0)} SAR</span></p>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Certification (month) *</label>
                      <select value={cin2AllocateForm.certification_id} onChange={e => setCin2AllocateForm(p => ({ ...p, certification_id: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                        <option value="">— select —</option>
                        {monthRows.filter(r => r.certified > 0).map(r => (
                          <option key={r.ym} value={r.ym}>{r.ym} — Net Certified: {fmt(r.netCertified)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1 font-medium text-green-700">Amount to Allocate (SAR) *</label>
                      <input type="number" value={cin2AllocateForm.allocated_amount} onChange={e => setCin2AllocateForm(p => ({ ...p, allocated_amount: e.target.value }))}
                        className="w-full border border-green-300 rounded-lg px-3 py-2 text-sm font-bold bg-green-50" />
                    </div>
                    <p className="text-[11px] text-[#9A948C]">Limits are enforced by the database and shown here if exceeded.</p>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowAllocateModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveAllocation} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Allocate</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowCertDetailModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md mx-4">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Retention / Advance Recovery — {cin2CertDetailMonth}</h2>
                    <button onClick={() => setCin2ShowCertDetailModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <p className="text-[11px] text-[#9A948C]">Certified and Client Invoice are owned by Scheduling and cannot be edited here. The values below are the actual Finance-recorded amounts for this month's certification.</p>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Retention Held (SAR)</label>
                      <input type="number" value={cin2CertDetailForm.retention_held_amount} onChange={e => setCin2CertDetailForm(p => ({ ...p, retention_held_amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <p className="text-[10px] text-[#9A948C] mt-1">Pre-filled from the project's configured Retention rule (see ⚙ Cash In Rules) — edit if this certification has a contractual exception.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Advance Recovered (SAR)</label>
                      <input type="number" value={cin2CertDetailForm.advance_recovered_amount} onChange={e => setCin2CertDetailForm(p => ({ ...p, advance_recovered_amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <p className="text-[10px] text-[#9A948C] mt-1">Enforced against the project's remaining Advance Balance by the database on save.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Other Deduction (SAR)</label>
                      <input type="number" value={cin2CertDetailForm.other_deduction_amount} onChange={e => setCin2CertDetailForm(p => ({ ...p, other_deduction_amount: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      <p className="text-[10px] text-[#9A948C] mt-1">Manual amount — e.g. LD, penalties, backcharges, contra charges.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Other Deduction Description</label>
                      <input value={cin2CertDetailForm.other_deduction_description} onChange={e => setCin2CertDetailForm(p => ({ ...p, other_deduction_description: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. Liquidated Damages Jan-26" />
                    </div>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowCertDetailModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveCertDetail} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Save</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowRulesModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-6 border-b border-[#DDD9D0]">
                    <h2 className="text-lg font-bold">Cash In Rules</h2>
                    <button onClick={() => setCin2ShowRulesModal(false)} className="text-[#9A948C] hover:text-[#6B655C] text-xl">×</button>
                  </div>
                  <div className="p-6 space-y-5">
                    <p className="text-[11px] text-[#9A948C]">These rules only generate a suggested starting value in the certification form. Finance always reviews and confirms the actual value — nothing here is written directly as a financial transaction.</p>

                    <div className="border-t border-[#DDD9D0] pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <input type="checkbox" checked={cin2RulesForm.has_advance} onChange={e => setCin2RulesForm(p => ({ ...p, has_advance: e.target.checked }))} />
                        <label className="text-sm font-bold text-[#0A2540]">This project has an Advance Payment</label>
                      </div>
                      {cin2RulesForm.has_advance && (
                        <div className="space-y-3 pl-6">
                          <div>
                            <label className="block text-xs text-[#6B655C] mb-1">Recovery Method</label>
                            <select value={cin2RulesForm.advance_recovery_method} onChange={e => setCin2RulesForm(p => ({ ...p, advance_recovery_method: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                              <option value="percentage_certified">Percentage of Certified</option>
                              <option value="fixed_amount">Fixed Amount</option>
                              <option value="manual">Manual (no suggestion)</option>
                            </select>
                          </div>
                          {cin2RulesForm.advance_recovery_method !== 'manual' && (
                            <div>
                              <label className="block text-xs text-[#6B655C] mb-1">{cin2RulesForm.advance_recovery_method === 'percentage_certified' ? 'Value (%)' : 'Value (SAR)'}</label>
                              <input type="number" value={cin2RulesForm.advance_recovery_value} onChange={e => setCin2RulesForm(p => ({ ...p, advance_recovery_value: e.target.value }))}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-[#6B655C] mb-1">Start Condition</label>
                              <select value={cin2RulesForm.advance_recovery_start_type} onChange={e => setCin2RulesForm(p => ({ ...p, advance_recovery_start_type: e.target.value }))}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                <option value="first_certificate">First Certificate</option>
                                <option value="certificate_number">Certificate Number</option>
                                <option value="date">Date</option>
                                <option value="manual">Manual</option>
                              </select>
                            </div>
                            {(cin2RulesForm.advance_recovery_start_type === 'certificate_number' || cin2RulesForm.advance_recovery_start_type === 'date') && (
                              <div>
                                <label className="block text-xs text-[#6B655C] mb-1">Start Value</label>
                                <input type={cin2RulesForm.advance_recovery_start_type === 'date' ? 'date' : 'number'} value={cin2RulesForm.advance_recovery_start_value} onChange={e => setCin2RulesForm(p => ({ ...p, advance_recovery_start_value: e.target.value }))}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs text-[#6B655C] mb-1">Stop Condition</label>
                            <select value={cin2RulesForm.advance_recovery_stop_type} onChange={e => setCin2RulesForm(p => ({ ...p, advance_recovery_stop_type: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                              <option value="until_fully_recovered">Until Fully Recovered</option>
                              <option value="max_amount">Max Amount</option>
                              <option value="certificate_number">Certificate Number</option>
                              <option value="date">Date</option>
                            </select>
                          </div>
                          <p className="text-[10px] text-[#9A948C]">Suggestions are always capped at the remaining unrecovered Advance, enforced by the database.</p>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-[#DDD9D0] pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <input type="checkbox" checked={cin2RulesForm.has_retention} onChange={e => setCin2RulesForm(p => ({ ...p, has_retention: e.target.checked }))} />
                        <label className="text-sm font-bold text-[#0A2540]">This project has Retention</label>
                      </div>
                      {cin2RulesForm.has_retention && (
                        <div className="space-y-3 pl-6">
                          <div>
                            <label className="block text-xs text-[#6B655C] mb-1">Method</label>
                            <select value={cin2RulesForm.retention_method} onChange={e => setCin2RulesForm(p => ({ ...p, retention_method: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                              <option value="percentage">Percentage</option>
                              <option value="fixed_amount">Fixed Amount</option>
                              <option value="manual">Manual (no suggestion)</option>
                            </select>
                          </div>
                          {cin2RulesForm.retention_method !== 'manual' && (
                            <div>
                              <label className="block text-xs text-[#6B655C] mb-1">{cin2RulesForm.retention_method === 'percentage' ? 'Value (%)' : 'Value (SAR)'}</label>
                              <input type="number" value={cin2RulesForm.retention_value} onChange={e => setCin2RulesForm(p => ({ ...p, retention_value: e.target.value }))}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs text-[#6B655C] mb-1">Start Condition</label>
                            <select value={cin2RulesForm.retention_start_type} onChange={e => setCin2RulesForm(p => ({ ...p, retention_start_type: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                              <option value="first_certificate">First Certificate</option>
                              <option value="certificate_number">Certificate Number</option>
                              <option value="date">Date</option>
                              <option value="manual">Manual</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-[#6B655C] mb-1">Cap</label>
                              <select value={cin2RulesForm.retention_cap_type} onChange={e => setCin2RulesForm(p => ({ ...p, retention_cap_type: e.target.value }))}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                <option value="no_cap">No Cap</option>
                                <option value="max_amount">Max Amount</option>
                                <option value="max_pct_contract">Max % of Contract Value</option>
                              </select>
                            </div>
                            {cin2RulesForm.retention_cap_type !== 'no_cap' && (
                              <div>
                                <label className="block text-xs text-[#6B655C] mb-1">{cin2RulesForm.retention_cap_type === 'max_pct_contract' ? 'Cap (%)' : 'Cap (SAR)'}</label>
                                <input type="number" value={cin2RulesForm.retention_cap_value} onChange={e => setCin2RulesForm(p => ({ ...p, retention_cap_value: e.target.value }))}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-[#DDD9D0] pt-4">
                      <h3 className="text-sm font-bold text-[#0A2540] mb-3">Expected Collection Timing (Forecast Only)</h3>
                      <p className="text-[10px] text-[#9A948C] mb-3">This only controls the forecast shown in "Expected Collection" — it never creates or affects an actual Collection transaction.</p>
                      <div>
                        <label className="block text-xs text-[#6B655C] mb-1">Method</label>
                        <select value={cin2RulesForm.expected_collection_method} onChange={e => setCin2RulesForm(p => ({ ...p, expected_collection_method: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                          <option value="same_month">Same Month</option>
                          <option value="fixed_lag">Fixed Lag (X months after certification)</option>
                          <option value="split">Split / Staged Collection</option>
                        </select>
                      </div>

                      {cin2RulesForm.expected_collection_method === 'fixed_lag' && (
                        <div className="mt-3">
                          <label className="block text-xs text-[#6B655C] mb-1">Lag (months after certification)</label>
                          <input type="number" min="0" value={cin2RulesForm.expected_collection_lag_months}
                            onChange={e => setCin2RulesForm(p => ({ ...p, expected_collection_lag_months: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                          <p className="text-[10px] text-[#9A948C] mt-1">e.g. Certified in August + 2 months = Expected Collection in October.</p>
                        </div>
                      )}

                      {cin2RulesForm.expected_collection_method === 'split' && (
                        <div className="mt-3 space-y-2">
                          <div className="flex justify-between text-[10px] text-[#6B655C] px-1">
                            <span>Offset (months)</span><span>% of Net Certified</span>
                          </div>
                          {cin2ECSplitRows.map((row, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <input type="number" min="0" value={row.offset_months}
                                onChange={e => setCin2ECSplitRows(rows => rows.map((r,i) => i===idx ? { ...r, offset_months: e.target.value } : r))}
                                className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                              <input type="number" min="0" max="100" value={row.percentage}
                                onChange={e => setCin2ECSplitRows(rows => rows.map((r,i) => i===idx ? { ...r, percentage: e.target.value } : r))}
                                className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
                              <span className="text-xs text-[#6B655C]">%</span>
                              {cin2ECSplitRows.length > 1 && (
                                <button onClick={() => setCin2ECSplitRows(rows => rows.filter((_,i) => i!==idx))}
                                  className="text-red-500 text-xs px-2">✕</button>
                              )}
                            </div>
                          ))}
                          <button onClick={() => setCin2ECSplitRows(rows => [...rows, { offset_months: rows.length, percentage: 0 }])}
                            className="text-xs text-[#0369A1]">+ Add offset row</button>
                          <p className="text-[10px]" style={{ color: Math.abs(cin2ECSplitRows.reduce((s,r)=>s+(parseFloat(r.percentage)||0),0) - 100) > 0.01 ? '#DC2626' : '#1A7A4A' }}>
                            Total: {cin2ECSplitRows.reduce((s,r)=>s+(parseFloat(r.percentage)||0),0)}% {Math.abs(cin2ECSplitRows.reduce((s,r)=>s+(parseFloat(r.percentage)||0),0) - 100) > 0.01 ? '(must equal 100%)' : '✓'}
                          </p>
                        </div>
                      )}
                    </div>

                    {(cin2Rules || cin2Entries.some(e => e.transaction_type === 'cert_finance_detail')) && (
                      <div className="border-t border-[#DDD9D0] pt-4">
                        <button onClick={() => { setCin2ResetConfirmText(''); setCin2ShowResetConfirm(true) }}
                          className="text-xs text-red-600 border border-red-300 rounded-lg px-3 py-2 hover:bg-red-50">
                          Reset Rules & Recalculate
                        </button>
                        <p className="text-[10px] text-[#9A948C] mt-1">Deletes this project's rules and all saved Retention/Advance Recovery/Deduction details. Collections and Advance Received are not affected.</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 p-6 border-t border-[#DDD9D0]">
                    <button onClick={() => setCin2ShowRulesModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                    <button onClick={cin2SaveRules} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">Save Rules</button>
                  </div>
                </div>
              </div>
            )}

            {cin2ShowResetConfirm && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 border-2 border-red-300">
                  <div className="p-6">
                    <h2 className="text-lg font-bold text-red-700 mb-3">⚠ Reset Cash In Rules</h2>
                    <p className="text-sm text-[#3C3A36] mb-4">
                      This will delete the Cash In Rules and all saved Retention, Advance Recovery
                      and Other Deduction details for this project.
                      <br /><br />
                      <b>Collections and Advance Received transactions will NOT be deleted.</b>
                      <br /><br />
                      This action cannot be undone. Type <b>RESET</b> below to confirm.
                    </p>
                    <input value={cin2ResetConfirmText} onChange={e => setCin2ResetConfirmText(e.target.value)}
                      placeholder="Type RESET"
                      className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm mb-4" />
                    <div className="flex gap-3">
                      <button onClick={() => setCin2ShowResetConfirm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
                      <button onClick={cin2ResetRules} disabled={cin2ResetConfirmText.trim().toUpperCase() !== 'RESET' || cin2Resetting}
                        className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                        {cin2Resetting ? 'Resetting…' : 'Confirm Reset'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      </div>
    </div>
  )
}

// ── Cash Schedule View — READ-ONLY reconciliation presentation of the exact same ──
// monthRows data used by ERP View above. No separate fetch, no separate calculation
// source, no editing, no upload. Mirrors the logic of the existing monthly Excel
// cash schedule (Opening Balance → Collection → Deductions → Net Cash In), but every
// number is derived from the same monthRows the ERP View already computed.
function CashScheduleView({ monthRows, fmt, fmtSigned, hasAnyAdvance, otherMovements = [], categoryMaster = [], cashOutEntries = [], paymentTypeLabel }) {
  const thS = { padding: '7px 9px', fontSize: 10, fontWeight: 700, color: '#fff', textAlign: 'right', whiteSpace: 'nowrap' }
  const thSL = { ...thS, textAlign: 'left', minWidth: 160 }
  const tdS = { padding: '6px 9px', fontSize: 11, textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #E5E7EB' }
  const tdSL = { ...tdS, textAlign: 'left', fontFamily: 'inherit' }

  if (monthRows.length === 0) {
    return <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No data yet.</div>
  }

  // ── Other Income / Other Deduction — aggregate per category, per month. ──
  // Every active category gets its own row (even if zero for a given month, per the
  // matrix convention used throughout this table), positioned between Collection and
  // NET CASH IN. Net Cash In below automatically folds these rows in, so a new category
  // added via "+ Other Income/Deduction" requires no further wiring anywhere else.
  const monthList = monthRows.map(r => r.ym)
  const amountByCategoryAndMonth = {}  // { categoryId: { ym: amount } }
  otherMovements.forEach(m => {
    const ym = (m.period_month || '').slice(0, 7)
    if (!amountByCategoryAndMonth[m.category_id]) amountByCategoryAndMonth[m.category_id] = {}
    amountByCategoryAndMonth[m.category_id][ym] = (amountByCategoryAndMonth[m.category_id][ym] || 0) + (parseFloat(m.amount) || 0)
  })
  // Only show categories that are active OR have at least one recorded movement (so a
  // deactivated category with historical data doesn't silently vanish from the matrix).
  const incomeCategories = categoryMaster.filter(c => c.type === 'income' && (c.is_active || amountByCategoryAndMonth[c.id]))
  const deductionCategories = categoryMaster.filter(c => c.type === 'deduction' && (c.is_active || amountByCategoryAndMonth[c.id]))

  const otherIncomeByMonth = {}
  const otherDeductionByMonth = {}
  monthList.forEach(ym => {
    otherIncomeByMonth[ym] = incomeCategories.reduce((s, c) => s + (amountByCategoryAndMonth[c.id]?.[ym] || 0), 0)
    otherDeductionByMonth[ym] = deductionCategories.reduce((s, c) => s + (amountByCategoryAndMonth[c.id]?.[ym] || 0), 0)
  })

  // Business rule (per approved correction): Collection entered by Finance already
  // represents the NET cash actually received in the bank — it is NOT a gross figure
  // that Retention/Advance Recovery should be subtracted from again. Cash In per month now
  // automatically includes Collection + Advance + Other Income − Other Deduction.
  const rows = monthRows.map(r => {
    const collectionSubtotal = r.advanceReceived + r.collected
    const otherIncome = otherIncomeByMonth[r.ym] || 0
    const otherDeduction = otherDeductionByMonth[r.ym] || 0
    const cashInTotal = collectionSubtotal + otherIncome - otherDeduction
    return { ...r, collectionSubtotal, otherIncome, otherDeduction, cashInTotal }
  })

  // ── CASH OUT — sourced entirely from cf_payment_entries (via cashOutEntries prop),
  // using the SAME payment_type buckets already established in the Cash Out ERP tab.
  // No forecasting, no new categories — purely an actuals reconciliation by month. ──
  const cashOutByMonth = {}       // { ym: total }
  const cashOutByTypeAndMonth = {}  // { type: { ym: amount } }
  cashOutEntries.forEach(e => {
    const ym = (e.date || '').slice(0, 7)
    if (!ym) return
    const type = e.payment_type || 'subcontractor_supplier'
    const amt = parseFloat(e.amount_inc_vat) || 0
    cashOutByMonth[ym] = (cashOutByMonth[ym] || 0) + amt
    if (!cashOutByTypeAndMonth[type]) cashOutByTypeAndMonth[type] = {}
    cashOutByTypeAndMonth[type][ym] = (cashOutByTypeAndMonth[type][ym] || 0) + amt
  })
  const cashOutTypes = Object.keys(cashOutByTypeAndMonth).sort((a,b) => {
    // Keep a stable, sensible order: Subcontractor/Supplier, Payroll, then anything else.
    const order = { subcontractor_supplier: 0, payroll: 1 }
    return (order[a] ?? 99) - (order[b] ?? 99)
  })
  const cashOutRows = monthList.map(ym => ({ ym, total: cashOutByMonth[ym] || 0 }))

  // ── BALANCE — rolling: Opening + Cash In − Cash Out = Closing; next month's Opening
  // = previous month's Closing. This replaces the old Cash-In-only rolling position. ──
  let runningPosition = 0
  const openingByMonth = []
  const closingByMonth = []
  rows.forEach((r, i) => {
    openingByMonth.push(runningPosition)
    const cashOut = cashOutRows[i]?.total || 0
    runningPosition += r.cashInTotal - cashOut
    closingByMonth.push(runningPosition)
  })

  const sum = (arr) => arr.reduce((s,v) => s+v, 0)
  const totalAdvanceReceived = sum(rows.map(r => r.advanceReceived))
  const totalProgress = sum(rows.map(r => r.collected))
  const totalCollectionSubtotal = sum(rows.map(r => r.collectionSubtotal))
  const totalCashIn = sum(rows.map(r => r.cashInTotal))
  const totalCashOut = sum(cashOutRows.map(r => r.total))
  const totalNetCashFlow = totalCashIn - totalCashOut
  // TOTAL column for balance-type rows uses the final closing position, NOT a sum
  // (summing balances across months is mathematically meaningless).
  const finalClosingPosition = closingByMonth[closingByMonth.length - 1] || 0

  const sectionRow = (label, color) => (
    <tr style={{ background: color }}>
      <td colSpan={rows.length + 2} style={{ padding: '5px 9px', fontSize: 10, fontWeight: 800, color: '#0A2540', letterSpacing: '.04em' }}>{label}</td>
    </tr>
  )

  const flowRow = (label, values, total, opts = {}) => (
    <tr key={opts.key}>
      <td style={{ ...tdSL, paddingLeft: opts.indent ? 24 : 9, fontWeight: opts.bold ? 700 : 400, color: opts.color }}>{label}</td>
      {values.map((v,i) => <td key={i} style={{ ...tdS, fontWeight: opts.bold ? 700 : 400, color: opts.color }}>{v ? fmt(v) : '—'}</td>)}
      <td style={{ ...tdS, fontWeight: 700, background: '#F5F1EA' }}>{fmt(total)}</td>
    </tr>
  )

  return (
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 800, color: '#0A2540', marginBottom: 10 }}>Monthly Cash Flow Schedule</h3>
      <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: '#0A2540' }}>
              <th style={thSL}>Item</th>
              {rows.map(r => <th key={r.ym} style={thS}>{r.ym}</th>)}
              <th style={{ ...thS, background: '#0F1017' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {/* OPENING BALANCE — a balance row: shows each month's rolling opening position,
                TOTAL column shows the final closing position, never a sum of openings. */}
            <tr style={{ background: '#F0F9FF' }}>
              <td style={{ ...tdSL, fontWeight: 700, color: '#0369A1' }}>OPENING BALANCE</td>
              {openingByMonth.map((v,i) => <td key={i} style={{ ...tdS, color: '#0369A1' }}>{fmt(v)}</td>)}
              <td style={{ ...tdS, fontWeight: 700, color: '#0369A1', background: '#E0F2FE' }} title="Closing position, not a sum of openings">{fmt(finalClosingPosition)}</td>
            </tr>

            {sectionRow('A. COLLECTION', '#ECFDF5')}
            {flowRow('1.1  Advance Received', rows.map(r => r.advanceReceived), totalAdvanceReceived, { indent: true })}
            {flowRow('1.2  Progress Payment', rows.map(r => r.collected), totalProgress, { indent: true })}
            {flowRow('Collection Sub-total', rows.map(r => r.collectionSubtotal), totalCollectionSubtotal, { indent: true, bold: true, color: '#1A7A4A' })}

            {/* ── B. OTHER INCOME / DEDUCTION — one row per user-defined category, folded
                into the matrix between Collection and NET CASH IN. Each category's own row
                shows its own monthly amounts; the sub-totals and NET CASH IN below pick
                these up automatically via otherIncomeByMonth/otherDeductionByMonth — adding
                a new category via the ERP View requires no further changes here. ── */}
            {(incomeCategories.length > 0 || deductionCategories.length > 0) && sectionRow('B. OTHER INCOME / DEDUCTION', '#ECFEFF')}
            {incomeCategories.map(c => flowRow(
              c.name,
              monthList.map(ym => amountByCategoryAndMonth[c.id]?.[ym] || 0),
              sum(monthList.map(ym => amountByCategoryAndMonth[c.id]?.[ym] || 0)),
              { indent: true, color: '#0E7490', key: c.id }
            ))}
            {incomeCategories.length > 0 && flowRow('Other Income Sub-total', rows.map(r => r.otherIncome), sum(rows.map(r => r.otherIncome)), { indent: true, bold: true, color: '#0E7490' })}
            {deductionCategories.map(c => flowRow(
              c.name,
              monthList.map(ym => -(amountByCategoryAndMonth[c.id]?.[ym] || 0)),
              -sum(monthList.map(ym => amountByCategoryAndMonth[c.id]?.[ym] || 0)),
              { indent: true, color: '#B45309', key: c.id }
            ))}
            {deductionCategories.length > 0 && flowRow('Other Deduction Sub-total', rows.map(r => -r.otherDeduction), -sum(rows.map(r => r.otherDeduction)), { indent: true, bold: true, color: '#B45309' })}

            <tr style={{ background: '#1A7A4A' }}>
              <td style={{ ...tdSL, color: '#fff', fontWeight: 800 }}>CASH IN TOTAL</td>
              {rows.map(r => <td key={r.ym} style={{ ...tdS, color: '#fff', fontWeight: 800 }}>{fmt(r.cashInTotal)}</td>)}
              <td style={{ ...tdS, color: '#fff', fontWeight: 800, background: '#0F3D26' }}>{fmt(totalCashIn)}</td>
            </tr>

            {/* ── CASH OUT — sourced from cf_payment_entries (existing Cash Out ERP data),
                using the same payment_type buckets already established there. Purely
                actuals; no forecasting, no new categories. ── */}
            {sectionRow('CASH OUT', '#FEF2F2')}
            {cashOutTypes.map(type => flowRow(
              paymentTypeLabel ? paymentTypeLabel(type) : type,
              monthList.map(ym => -(cashOutByTypeAndMonth[type]?.[ym] || 0)),
              -sum(monthList.map(ym => cashOutByTypeAndMonth[type]?.[ym] || 0)),
              { indent: true, color: '#B91C1C', key: type }
            ))}
            <tr style={{ background: '#B91C1C' }}>
              <td style={{ ...tdSL, color: '#fff', fontWeight: 800 }}>CASH OUT TOTAL</td>
              {cashOutRows.map(r => <td key={r.ym} style={{ ...tdS, color: '#fff', fontWeight: 800 }}>{r.total ? '-' + fmt(r.total) : '—'}</td>)}
              <td style={{ ...tdS, color: '#fff', fontWeight: 800, background: '#7F1D1D' }}>{totalCashOut ? '-' + fmt(totalCashOut) : '—'}</td>
            </tr>

            {/* ── NET CASH FLOW = Cash In Total − Cash Out Total (this month's movement only,
                not a balance). ── */}
            <tr style={{ background: '#0A2540' }}>
              <td style={{ ...tdSL, color: '#fff', fontWeight: 800 }}>NET CASH FLOW</td>
              {rows.map((r,i) => {
                const net = r.cashInTotal - (cashOutRows[i]?.total || 0)
                return <td key={r.ym} style={{ ...tdS, color: net < 0 ? '#FCA5A5' : '#fff', fontWeight: 800 }}>{fmtSigned(net)}</td>
              })}
              <td style={{ ...tdS, color: totalNetCashFlow < 0 ? '#FCA5A5' : '#fff', fontWeight: 800, background: '#000' }}>{fmtSigned(totalNetCashFlow)}</td>
            </tr>

            {/* ── CLOSING BALANCE — rolling: Opening + Cash In − Cash Out = Closing;
                next month's Opening = previous month's Closing. TOTAL column shows the
                final closing position, not a sum (summing balances is meaningless). ── */}
            <tr style={{ background: '#F0F9FF' }}>
              <td style={{ ...tdSL, fontWeight: 700, color: '#0369A1' }}>CLOSING BALANCE</td>
              {closingByMonth.map((v,i) => <td key={i} style={{ ...tdS, color: '#0369A1', fontWeight: 700 }}>{fmt(v)}</td>)}
              <td style={{ ...tdS, fontWeight: 700, color: '#0369A1', background: '#E0F2FE' }} title="Final closing position, not a sum">{fmt(finalClosingPosition)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 10, color: '#9CA3AF', marginTop: 8 }}>* All amounts in SAR. Deduction and Cash Out rows are shown as negative values. Opening/Closing Balance rows show the rolling cash position (Opening + Cash In − Cash Out = Closing) — not a sum of monthly values.</p>
    </div>
  )
}

